/**
 * PSP/CRE Tool v3.0 - Tab 2: Operations Configuration
 * COMPLETE TAB 2 IMPLEMENTATION (Tasks 2.1-2.24)
 * 
 * Features:
 * - Standard Mode: Simple operation configuration (corps summary)
 * - Detailed Mode: Per-brigade/battalion configuration
 * - Expert Features: Position editing + State override (controlled by global mode)
 * - Multinational Support: Per-unit national standards
 * - Medical Integration: Links to Role 2 facilities
 * - Full Tab1 Integration: Extracts all unit data
 * - FORCE QUALITY: Extracts from Tab1, displays read-only, passes to calculator
 * - ENEMY ASSESSMENT: Corps-level enemy quality and force superiority
 * - TECH ENVIRONMENT: Per-brigade technology override
 * - FRONTAGE FIX: Proper filtering of non-frontage units (v4.1)
 * - SUPPORT UNIT POSITIONING: Automatic rear area placement (v4.3)
 * - OPERATIONAL STATE: COMBAT_ENGAGED/SUPPORT_ACTIVE/TRUE_RESERVE (v5.0)
 * - THREAT ASSESSMENT: Full threat configuration with impact modifiers (v5.1)
 * - LOC CONFIGURATION: Road status, security, bridges for evacuation planning (v5.2)
 * - ADVANCED FEATURES: Infrastructure, HNS, Night Ops, Climate/DNBI, MRoE (v5.3)
 * - PHASE 2 HIERARCHY: Corps  Division  Brigade phase cascade system (v6.0)
 * 
 * Author: Dr. Tatar Radu MD
 * Date: February 2026
 * Version: 7.7.0 - ENH-012: Theater region selector in climate section (March 7, 2026)
 *   Import THEATER_REGIONS from climateZones. Add theater_region to climateConfig state init
 *   (loads from saved, defaults to 'CENTRAL_EUROPE'). New Theater Region dropdown in the
 *   climate Collapse grid (second row, full-width). Included in climate_config save block.
 *   Backward compat: old scenarios without theater_region default to 'CENTRAL_EUROPE'.
 *
 * Version: 7.14.0 (April 4, 2026) - BUG-FMAP-001: Frontage map division display fixes:
 *   (1) parent_division_name now set on brigade configs (was never stored, only parent_division_id);
 *   (2) division label in map now uses parent_division_name correctly (was falling back to
 *       brigade_name.split(' Brigade')[0] — produced garbage e.g. "1st");
 *   (3) brigade labels in corps (multi-div) now show "D1/B2" format for uniqueness —
 *       previously all showed "Bde 1 / Bde 2 / Bde 3" repeated across every division.
 *
 * Version: 7.13.0 (March 20, 2026) - ENH-023: Per-phase override at Division (vs Corps) and
 *   Brigade (vs Division). New module-level getEffectivePhases(unit, parentPhases) merges
 *   unit.phase_overrides[index] into parent phases (posture/mission/description only —
 *   duration locked). All cascade functions now call getEffectivePhases so overrides survive
 *   re-cascades. phase_overrides: {} added to all division and brigade init paths.
 *   cascadeCorpsPhasesToDivisions: applies div.phase_overrides before setting div.phases;
 *   applies brigade.phase_overrides before setting brigade.phases (both in setDivisionConfigs
 *   updater and setTimeout brigade pass). cascadeDivisionPhasesWithData and
 *   cascadeDivisionPhasesToBrigades: wrap output in getEffectivePhases(brigade, newPhases).
 *   toggleDivisionInheritance / toggleBrigadeInheritance: call getEffectivePhases when
 *   switching TO inherit mode. handleSaveDivisionConfig: preserves phase_overrides from
 *   config via existing ...config spread — no additional change required.
 *
 * Version: 7.12.4 (March 20, 2026) - Cosmetic: nsColors POLISH #c62828 → #ffa000 (amber).
 *   #c62828 was too visually similar to ROMANIAN #d32f2f on the frontage map.
 *   #f57c00 (original suggestion) already occupied by GERMAN — collision avoided.
 *   #ffa000 (deep amber) is distinct from all 7 other nation colors in the map.
 *
 * Version: 7.12.3 (March 19, 2026) - BUG-164 final fix: scenario.force.division singular path.
 *   Tab1 writes divisions: forceBuilderResult?.divisions||[] — empty in DIVISION echelon.
 *   Tab1 never wrote forceBuilderResult.division (singular) to the flat scenario.force path.
 *   Tab1 v4.16.0 now adds division: forceBuilderResult?.division||null to forceData.
 *   Tab2 divisions useMemo adds scenario?.force?.division as final fallback.
 *   divisionConfigs now initialises with a real entry in DIVISION echelon.
 * Version: 7.12.2 (March 19, 2026) - BUG-164 actual root cause: wrong data path.
 *   Tab1 writes force structure to BOTH scenario.force.forceBuilderResult (nested)
 *   AND scenario.force.divisions / scenario.force.brigades (flattened top-level).
 *   Tab2 brigades/divisions useMemos only read the nested forceBuilderResult path.
 *   forceBuilderResult is null/empty on many render cycles (DIVISION echelon:
 *   formatBuildResult returns .division singular — nested path never has .divisions).
 *   The flat scenario.force.divisions is always populated by Tab1 forceData object.
 *   Fix: both useMemos check forceBuilderResult first, then fall back to flat paths.
 *   divisionConfigs initialises correctly — cascade, BrigadePhaseTab, BUG-070 resolve.
 * Version: 7.12.0 (March 19, 2026) - BUG-163 root cause + structural DIVISION echelon fix:
 *   STRUCTURAL FIX: divisions useMemo returned [] in DIVISION echelon because
 *   ForceService.formatBuildResult returns .division (singular) for DIVISION echelon
 *   but .divisions (plural array) for CORPS echelon. Tab2 only read .divisions →
 *   divisionConfigs always initialised to [] → ALL cascade, phase lookup, and
 *   BrigadePhaseTab failures in DIVISION echelon traced back to this single cause.
 *   Fix: useMemo checks .divisions first, falls back to [.division] if singular.
 *   This alone corrects BUG-164 (cascade written to empty array), BUG-070 (useEffect
 *   never fired — divisions was always []), and BrigadePhaseTab "0 phases".
 *   BUG-163 ROOT CAUSE FIX: nsColors lookup used config.nationalStandard (camelCase)
 *   but all three brigade init paths only wrote national_standard (snake_case).
 *   Fix: all three paths now write BOTH nationalStandard and national_standard
 *   explicitly, regardless of what Brigade.toJSON() preserves.
 *   Previous BUG-163 fix (adding FRENCH/GERMAN/POLISH to nsColors map) was correct
 *   but incomplete — the map was never reached because the key was undefined.
 *   cascadeAllDivisionPhasesToBrigades: was only called from cascadeCorpsPhasesToDivisions
 *     via setTimeout. BUG-164 fix replaced that call with direct setBrigadeConfigs — leaving
 *     this function with no call sites. Removed entirely.
 *   mergedPhases: assigned inside setDivisionConfigs updater but never read outside it.
 *     Same variable as removed in v7.10.8 but the uploaded file was pre-fix. Removed again.
 * Version: 7.10.9 (March 18, 2026) - BUG-163 + BUG-164 + hardcoded color cleanup:
 *   BUG-163: Frontage map nsColors only had ROMANIAN/NATO/US/MULTINATIONAL.
 *     French/British/German/Polish brigades rendered grey with no legend entry.
 *     Added GERMAN, BRITISH, POLISH, FRENCH to nsColors map. Legend auto-generates
 *     from nsColors so new nations appear automatically.
 *   BUG-164: Corps→Division→Brigade phase cascade broken — stale closure.
 *     cascadeCorpsPhasesToDivisions used setTimeout(cascadeAllDivisionPhasesToBrigades)
 *     which closed over stale divisionConfigs (phases=[]). Brigades received empty arrays.
 *     Same root cause as BUG-151. Fix: capture inheriting division IDs inside the
 *     setDivisionConfigs updater, then directly setBrigadeConfigs with in-hand phases.
 *     Works for both CORPS and DIVISION echelon.
 *   Hardcoded colors: #f44336 Enemy Assessment title → error.main via MUI sx.
 *     #4caf50 Tab title → success.main. #888 region label → text.disabled.
 *     Phase mission colors (ATTACK/DEFEND/etc) left as-is — SVG inline style
 *     requires hex values; no MUI theme token equivalent for SVG fill.
 * Version: 7.10.8 (March 18, 2026) - ESLint: remove unused mergedPhases variable.
 * Version: 7.10.7 (March 18, 2026) - BUG-149: Pass echelonLabel to CorpsConfigDialog.
 *   CorpsConfigDialog now accepts echelonLabel prop (default "Corps"). Tab2 passes
 *   echelonLabel so the dialog title reflects the actual planning echelon.
 * Version: 7.10.6 (March 18, 2026) - BUG-150 + BUG-151:
 *   BUG-150: Brigade end_day not cascaded when operation duration changes.
 *     Root cause: setTotalDuration fires on the duration input but no useEffect
 *     propagated the new value to brigadeConfigs.operation.end_day. Brigades whose
 *     end_day was set at init time silently retained the old value → Days 11-15 of a
 *     15-day operation had no posture/mission → silent casualty undercount in calculator.
 *     Fix: useEffect on totalDuration iterates brigadeConfigs and clamps each
 *     operation.end_day to Math.min(config.operation.end_day, newDuration). Brigades
 *     with end_day already ≤ newDuration are untouched (no unnecessary re-renders).
 *   BUG-151: Brigade Phases tab shows "Inherited Phases — 0 phases" despite Division
 *     having 5 phases saved and visible in Division Configuration panel.
 *     Root cause: handleSaveDivisionConfig called setDivisionConfigs (async state) then
 *     setTimeout(cascadeDivisionPhasesToBrigades). cascadeDivisionPhasesToBrigades closes
 *     over divisionConfigs at creation time (stale closure) — the divisionConfigs it
 *     reads still lacks the newly saved phases. Brigade lookup returns division.phases=[]
 *     → BrigadePhaseTab receives empty array → "0 phases" message.
 *     Fix: handleSaveDivisionConfig now passes the merged division config directly to
 *     cascadeDivisionPhasesToBrigades (bypasses closure) and removes the setTimeout.
 *     A new helper cascadeDivisionPhasesWithData(divisionId, phasesArray) performs the
 *     brigade update using the caller-supplied phases — no divisionConfigs closure needed.
 * Version: 7.10.4 (March 17, 2026) - BUG-132: All hardcoded "Corps Configuration" /
 *   "Corps-Level" label strings in renderCorpsConfig() and renderStandardModeConfig()
 *   replaced with echelonLabel derived from echelon prop. When planning at DIVISION
 *   echelon the card now reads "Division Configuration", "Division-Level Planning",
 *   "Configure Division HQ & Phases", etc. Also cleaned dead empty
 *   `if (forceBuilderResult) {}` block after getEchelonCounts useMemo.
 *
 * Version: 7.10.3 (March 16, 2026) - BUG-115: LOC coordination_delay key mismatch fixed (coordination_delay → coordination_delay_minutes) + getSampleTravelTime NaN guard added
 *
 * Version: 7.10.2 (March 16, 2026) - CLEANUP-001 continued: removed 7 phantom keys from
 *   UNIT_TYPE_DOCTRINE and 5 from UNIT_TYPE_HAS_FRONTAGE that have no corresponding
 *   UNIT_TYPES entry in unitTypes.js — dead entries, no consumer, no throw.
 *   Removed from UNIT_TYPE_DOCTRINE: ARMORED, SPECIAL_FORCES, RECONNAISSANCE, SUPPORT,
 *     MILITARY_POLICE, MAINTENANCE, HEADQUARTERS.
 *   Removed from UNIT_TYPE_HAS_FRONTAGE: ARMORED, SPECIAL_FORCES, RECONNAISSANCE,
 *     MAINTENANCE, HEADQUARTERS.
 *   NOTE: SUPPORT and MILITARY_POLICE were not in UNIT_TYPE_HAS_FRONTAGE (already absent).
 *   UNIT_TYPE_DOCTRINE now contains exactly the 19 UNIT_TYPES keys + 0 phantoms.
 *   No logic change — unitTypeHasFrontage() and getUnitTypeDoctrine() behaviour unchanged.
 *
 * Version: 7.10.1 (March 15, 2026) - CLEANUP-001: removed dead code confirmed unused after Tab 6 Phase 4.
 *   Removed: renderEchelonPositionDefaults() function (eslint-disable-next-line + ~155 lines).
 *   Removed: hqOverrideConfig useState block (lines ~4104-4123).
 *   Removed: hq_override_config key from buildUpdates() operationParams.
 *   Removed: CLEANUP NOTE comment + commented {renderEchelonPositionDefaults()} call site.
 *   No consumer confirmed in Tab 6, Tab 3, calculator, or any service module.
 *   mtfDefaultOffsets.js reads per-unit hq_position_feba_km — not this global override.
 *
 * Version: 7.10.0 (March 15, 2026) - ENH-022b: Operation start date + D+N calendar linkage.
 *   New optional TextField type="date" (operation_start_date) in Corps Parameters card.
 *   formatDayLabel(day, startDate) utility: returns "D+N" when no date set, "D+N (DD Mon)"
 *   when date is set. Timeline column headers updated. Saved to operation_parameters as
 *   operation_start_date (null when empty). Zero calculation impact. Backward compatible.
 *   ENH-017: confirmed CLOSED (reorder implemented v7.9.14 — closing formally this session).
 *
 * Version: 7.9.17 (March 16, 2026) - UT-GAP-002: Added 7 missing UNIT_TYPES keys to
 *   UNIT_TYPE_DOCTRINE and UNIT_TYPE_HAS_FRONTAGE.
 *   Missing: AIR_ASSAULT, MOUNTAIN, ARMOR, STRYKER, ENGINEER, CAVALRY, HQ.
 *   All 7 were falling through to DEFAULT_UNIT_DOCTRINE (has_frontage:true, position:5km,
 *   COMBAT category) — wrong for ENGINEER (combat support), wrong position for all.
 *   getUnitTypeDoctrine() is called at 14 locations in Tab2 — frontage map rendering,
 *   position validation, state auto-assignment, sync detection.
 *   Values derived from brigadeTypeOffsets.js offsets + existing Tab2 UNIT_TYPE_DOCTRINE
 *   pattern for analogous types.
 *   Also updated UNIT_TYPE_HAS_FRONTAGE for all 7 new types.
 *
 * Version: 7.9.16 (March 13, 2026) - BUG-094: LOC sample evacuation times showed ~NaN min:
 *   Two fixes: (1) locConfig load path now spreads DEFAULT_LOC_CONFIG first — prior explicit-key
 *   form stripped any keys added after save, causing getLOCModifiers() to return NaN speed_modifier.
 *   (2) getSampleTravelTime() guards speed_modifier and coordination_delay against undefined/NaN.
 *
 * Version: 7.9.15 (March 13, 2026) - BUG-077: Phase mismatch warnings at wrong level:
 *   Corps Configuration card warning block was collecting mismatches from division and
 *   brigade phases and surfacing them at corps level — where the user cannot act on them.
 *   Brigade mismatches already appear correctly in the brigade dialog Mission tab (lines
 *   6510-6574 with Auto-Fix button). Division mismatches belong in DivisionConfigDialog.
 *   Fix: removed division and brigade mismatch collection from renderCorpsConfig() warning
 *   block — corps card now shows only corps phase mismatches. The warning text updated to
 *   reflect scope ("Corps phases only"). Zero logic change to calculators.
 *
 * Version: 7.9.14 (March 10, 2026) - ENH-017: Tab2 card reorder:
 *   Extracted ThreatAssessmentPanel, EnemyAssessmentPanel, AirSituationPanel,
 *   LOCConfigurationPanel, AdvancedFeaturesPanel from renderCorpsConfig() into
 *   new renderOperationalModifiers(). Main return block reordered:
 *   Mode → Standard → Corps → Division → Brigades → Modifiers → Timeline → Map.
 *   Zero logic change. Display-only reorder.
 *
 * Version: 7.9.13 (March 10, 2026) - Division personnel correct source:
 *   v7.9.12 used hq_personnel+support_personnel which don't exist on the
 *   division object (Tab1 UI state only). division.strength IS the HQ
 *   personnel total (Tab1 convention: corps.strength=Corps HQ, division.strength=
 *   Division HQ, per the CRITICAL BUG fix documented in Tab1 header §289-296).
 *   Both init paths now use division.strength || 1000.
 *
 * Version: 7.9.12 (March 10, 2026) - Division personnel from Tab1:
 *   Both divisionConfigs initializer paths (useState + useEffect BUG-070)
 *   hardcoded personnel:1000. Fixed to read division.hq_personnel +
 *   division.support_personnel from Tab1 data, with division.personnel
 *   and 1000 as fallbacks. Also reads tactical_in_parent.hq_position_feba_km
 *   for position_feba_km default instead of hardcoded 30.
 *
 * Version: 7.9.11 (March 10, 2026) - BUG-070:
 *   divisionConfigs useState lazy initializer ran once at mount before
 *   forceBuilderResult.divisions was populated — divisions.length===0 fired
 *   and divisionConfigs stayed [] forever, making all division buttons fail.
 *   Added useEffect([divisions, brigades]) that re-populates divisionConfigs
 *   when divisions arrive post-mount and configs are still empty.
 *   Mirror of the same pattern used for brigadeConfigs (CRIT-002 fix).
 *
 * Version: 7.9.10 (March 10, 2026) - BUG-069 correct fix:
 *   v7.9.8 fix was wrong: changed button to division.division_id but Tab1
 *   division objects use .id (not .division_id). divisionConfigs initializes
 *   with division_id: division.id, so the correct key is division.id.
 *   Reverted button to handleEditDivisionConfig(division.id) — now correctly
 *   matches divisionConfigs[n].division_id on find().
 *
 * Version: 7.9.9 (March 10, 2026) - ESLint clean:
 *   calculateDoctrinePositions suppressed with eslint-disable-next-line.
 *   Same family as doctrinePositions and renderEchelonPositionDefaults —
 *   all three consumed only by dead showHqOverride block. Remove together
 *   after Tab 6 Phase 1 if still unused.
 *
 * Version: 7.9.8 (March 10, 2026) - BUG-069 actual fix:
 *   DivisionConfigDialog.jsx exists and is complete. Root cause was
 *   division.id vs division.division_id mismatch at button call site.
 *   divisionConfigs.find(d => d.division_id === editingDivision) returned
 *   undefined -> initialDivision null guard -> dialog rendered nothing.
 *   Fixed: handleEditDivisionConfig(division.division_id).
 *
 * Version: 7.9.7 (March 10, 2026) - Remove division button gate:
 *   v7.9.6 gate was wrong philosophy — divisions and brigades can define custom
 *   phases independently of corps. Corps phases are an inheritance source, not a
 *   prerequisite. Restored button unconditionally. Replaced static Alert with
 *   dynamic inheritance chain status: warning when corps phases empty (informs
 *   user of cascade option without blocking), info when corps phases exist.
 *
 * Version: 7.9.6 (March 10, 2026) - Division button gate:
 *   BUG-069 root cause confirmed: corpsPhases=[] on fresh scenario. divisionConfigs
 *   default to uses_corps_phases:true, so DivisionConfigDialog opened but showed
 *   nothing (inheriting empty corps phases). Fixed by gating the button behind
 *   corpsPhases.length > 0 — when empty, shows a warning Alert directing user to
 *   configure Corps HQ & Phases first. Console.warn diagnostic retained for ID
 *   mismatch detection when the dialog does open.
 *
 * Version: 7.9.5 (March 10, 2026) - ESLint clean:
 *   doctrinePositions assignment commented out (was used only by dead showHqOverride block).
 *   renderEchelonPositionDefaults suppressed with eslint-disable-next-line (call site
 *   commented out; function preserved for potential Tab 6 use with explanatory note).
 *
 * Version: 7.9.4 (March 10, 2026) - Expert Mode + Division button fixes:
 *   BUG-068: isExpertMode = mode === 'EXPERT' || mode === 'DETAILED'. Mode 'EXPERT'
 *     was remapped to 'DETAILED' in the v7 migration; the old check was permanently
 *     false. Position TextField and Operational State Select now correctly unlock
 *     in Detailed mode.
 *   BUG-069: Division "Configure HQ & Phases" button now logs a console.warn if
 *     divisionConfigs.find() returns undefined (silent failure diagnostic).
 *   UX: Removed stale "Phase 2 will add division-level phase planning" alert text.
 *     Updated to accurate description of what the dialog configures.
 *
 * Version: 7.9.3 (March 10, 2026) - Comment out orphaned card:
 *   renderEchelonPositionDefaults() call commented out. Card had no downstream consumer
 *   (mtfDefaultOffsets.js reads per-unit hq_position_feba_km, not hq_override_config).
 *   hqOverrideConfig state and save key preserved for potential future use.
 *   Cleanup note added at call site — revisit after Tab 6 Phase 1.
 *
 * Version: 7.9.2 (March 10, 2026) - BUG-067 + dead code removal:
 *   BUG-067: config.logistics/medical/environment all guarded with ?. + safe defaults
 *     in renderBrigadeDialog tabs 3/4/5. Fixes crash on brigades loaded from Tab1
 *     that never passed through Tab2 initialization (missing sub-objects).
 *   ARCH: Removed dead showHqOverride block from AdvancedFeaturesPanel (~180 lines).
 *     Removed hqOverrideConfig/onHqOverrideConfigChange/showHqOverride props from
 *     AdvancedFeaturesPanel signature and call site. Live hqOverrideConfig state
 *     remains at Tab2 level, feeding renderEchelonPositionDefaults unchanged.
 *   UX: Renamed "Echelon Position Defaults" card to "Echelon HQ Positions - MTF Anchor
 *     Points". Updated Alert to explain HQ/MTF vs per-unit FEBA combat position.
 *
 * Version: 7.9.1 (March 10, 2026) - ENH-016 conflict warning:
 *   AirSituationPanel: new enemyAdThreat prop. Conflict detected when adOverride !== null
 *   AND adOverride !== threatConfig.enemy_ad. Shows warning Alert + header Chip when active.
 *   Planning Effect row updated: shows "(overrides Threat Assessment: X)" inline.
 *   Call site: enemyAdThreat={threatConfig.enemy_ad} passed.
 *
 * Version: 7.9.0 (March 10, 2026) - ENH-016: Friendly Air Situation
 *   NEW IMPORT: AIR_SITUATION_LEVELS, DEFAULT_AIR_SITUATION_CONFIG, validateAirSituationConfig
 *     from airSituation.js (new constants file v1.0.0).
 *   NEW STATE: airSituationConfig — loads from operation_parameters.air_situation_config
 *     (default PARITY = 1.0x, no effect).
 *   NEW COMPONENT: AirSituationPanel — 4-option Select (Air Superiority / Parity /
 *     Contested / Enemy Air Superiority). Shows casualty modifier + MEDEVAC AD override.
 *     Positioned between EnemyAssessmentPanel and LOCConfigurationPanel.
 *   SAVE: air_situation_config: { level } added to buildUpdates operationParams.
 *   VALIDATION: validateAirSituationConfig called in handleMarkComplete.
 *
 * Version: 7.6.1 - ESLint no-loop-func fix in computeDivisionFrontageMap (February 25, 2026)
 *
 * ==============================================================================
 * v7.8.1 (March 10, 2026) - ENH-BAP-MOVE: Body armor relocated to Tab1 ForceQualityPanel:
 *   - REMOVED: BODY_ARMOR_LEVELS, DEFAULT_BODY_ARMOR_LEVEL import
 *   - REMOVED: bodyArmorLevel state
 *   - REMOVED: body_armor_level from operationParams
 *   - REMOVED: bodyArmorLevel/onBodyArmorLevelChange props from EnemyAssessmentPanel
 *   - REMOVED: "Friendly Force Body Armor Standard" Select from EnemyAssessmentPanel UI
 *   - REMOVED: armor line from EnemyAssessmentPanel summary Paper
 *   Body armor is now set in Tab1 Friendly Force Quality card (forceQuality.body_armor_level).
 *   Calculator reads from forceQuality?.body_armor_level (passed as forceQuality to brigade loop).
 *
 * v7.8.0 (March 10, 2026) - ENH-BAP: Body armor protection level (Category 20 Research Audit):
 *   - NEW IMPORT: BODY_ARMOR_LEVELS, DEFAULT_BODY_ARMOR_LEVEL from bodyArmorProtection.js
 *   - NEW STATE: bodyArmorLevel — reads from operation_parameters.body_armor_level (default LEVEL_III)
 *   - operationParams: body_armor_level: bodyArmorLevel added
 *   - EnemyAssessmentPanel: new bodyArmorLevel + onBodyArmorLevelChange props
 *   - UI: "Friendly Force Body Armor Standard" Select in EnemyAssessmentPanel
 *     (5 levels NONE→IV, shows % KIA reduction per option)
 *   - Summary Paper: armor effect line added (short_label + % reduction)
 *
 * v7.7.1 (March 8, 2026) - BUG-066: flushState markDirty:
 * - BUG-066 FIXED: flushState() now calls markDirty() — toolbar nav bypassed
 *   Save & Continue, silently saving without flagging Tab 5 results as stale.
 *
 * v7.7.0 (March 8, 2026) - calculationService integration:
 * - IMPORT: markDirty from calculationService.js.
 * - Save & Continue: markDirty() called after onUpdate() — signals Tab 5 that
 *   operations configuration has changed and any existing result is stale.
 *
 * v7.6.1 (February 25, 2026) - ESLint: no-loop-func fix
 * - computeDivisionFrontageMap: `offset` (mutable let) captured into
 *   `const divOffset` before forEach so callback closes over stable value.
 *   Functional behaviour unchanged.
 *
 * ==============================================================================
 * v7.6.0 (February 25, 2026) - FRONTAGE: Division-Aware Boundaries + ESLint Fixes
 * - ROOT CAUSE: autoRedistributeFrontage and all initial-assignment paths used a flat
 *   global index over all brigades. Brigade 3 of Division 1 was treated as adjacent to
 *   Brigade 1 of Division 2 — sectors crossed division boundaries freely.
 * - NEW HELPER: computeDivisionFrontageMap() groups combat-eligible brigades by
 *   parent_division_id, assigns each division a contiguous proportional sector, then
 *   distributes sub-sectors within each division. Adjacency (adjacent_left/right) is null
 *   at division boundaries — no cross-division neighbour links ever generated.
 * - WIRE: Helper used in all three frontage-assignment locations:
 *   1. autoRedistributeFrontage useCallback (RESET + FILL_GAPS modes)
 *   2. Initial brigadeConfigs useState
 *   3. Auto-populate useEffect (empty brigadeConfigs path)
 * - SVG MAP: Division boundary dashed orange lines + division name labels added.
 *   Legend updated to show "Division Boundary" chip.
 * - ESLint Fix 1: _setEvacuationParadigm unused setter → plain const derivation.
 * - ESLint Fix 2: extractedNationalStandard now included in useMemo return object
 *   (national_standard field normalised to snake_case for calculator).
 *
 * ==============================================================================
 * v7.5.2 (February 16, 2026) - CRITICAL BUGFIX: Frontage Map Display Bugs
 * - BUG 1: Wrong Brigade Colors on Frontage Map
 *   - PROBLEM: Division 1 Brigade 3 (NATO) showing GREEN (US color) instead of BLUE
 *   - ROOT CAUSE: Line 6620 used config.national_standard (snake_case) but Tab1 saves nationalStandard (camelCase)
 *   - FIX: Changed to config.nationalStandard (matches Tab1 field naming)
 * 
 * - BUG 2: Support Units Appearing on Frontage Map
 *   - PROBLEM: Artillery brigade showing ON frontage when should be off (stale data)
 *   - ROOT CAUSE: Line 3899 used `position: brigade.position || {...}` preserving OLD frontage allocation
 *   - IMPACT: If user changed unit_type in Tab1, Tab2 kept old position data with frontage_width_km > 0
 *   - EXAMPLE: Brigade was MECHANIZED (combat) -> changed to ARTILLERY (support) -> still had frontage
 *   - FIX: Always recalculate frontage based on current frontageStatus.holdsFrontage
 *   - LOGIC: Support units FORCE frontage_width_km = 0, combat units preserve/calculate frontage
 * 
 * - BUG 3: Combat Units Missing from Frontage Map
 *   - PROBLEM: Heavy Armored brigade appearing in "Not on frontage" when should be on frontage
 *   - CAUSE: Same as Bug 2 - stale position data with frontage_width_km = 0 from when it was support
 *   - FIX: Same as Bug 2 - recalculate based on current unit_type
 * 
 * - CHANGED LINES:
 *   - Line 3898-3918: Position initialization with frontageStatus-based recalculation
 *   - Line 6620: Color assignment uses nationalStandard (not national_standard)
 * ==============================================================================
 * v7.5.1 (February 16, 2026) - BUGFIX: Monte Carlo Toggle Not Visible
 * - PROBLEM: Monte Carlo checkbox hidden inside showHqOverride conditional (set to false)
 * - SYMPTOM: User could not see "🎲 Uncertainty Analysis (Monte Carlo)" in Advanced Features
 * - FIX 1: Moved Monte Carlo section outside showHqOverride conditional (line ~3182)
 * - FIX 2: Updated feature counter to exclude HQ Override (now 6 features, not 7)
 * - REASON: HQ Override moved outside Advanced Features panel in previous version
 * - RESULT: Monte Carlo now always visible, counter shows "0/6 enabled" correctly
 * ==============================================================================
 * v7.5.0 (February 16, 2026) - PHASE 1.3: Monte Carlo Casualty Toggle
 * - NEW STATE: monteCarloConfig state variable (line ~3673)
 * - NEW UI: Monte Carlo checkbox in AdvancedFeaturesPanel (after HQ Override)
 * - NEW SAVE: monte_carlo_config saved to operation_parameters (buildUpdates line ~5115)
 * - DEFAULT: OFF (users explicitly enable for uncertainty analysis)
 * - ITERATIONS: 1000 (fixed, ~5s calculation time)
 * - EDUCATIONAL: Tooltip explains uncertainty quantification
 * - LOCATION: Tab 2 "Advanced Features" panel (line ~2845)
 * - INTEGRATION: Works with casualtyCalculator v5.4.0 Monte Carlo wrapper
 * ==============================================================================
 * v7.4.6 (February 15, 2026) - VARIABLE FIXES: Revert extractedNationalStandard, Remove generateId
 * - FIXED: extractedNationalStandard no-undef error at lines 4465, 4467
 * - CAUSE: v7.4.4 incorrectly prefixed with underscore thinking it was unused
 * - REALITY: Variable IS used on lines 4465, 4467 for national_standard and strength
 * - SOLUTION: Reverted to extractedNationalStandard (removed underscore prefix)
 * - REMOVED: generateId() function (line 981) - truly unused, leftover from deleted handlers
 * - VERIFICATION: Zero ESLint errors, 2 warnings (both prefixed with underscore, acceptable)
 * ==============================================================================
 * v7.4.5 (February 15, 2026) - SYNTAX FIX: Remove Orphaned Brace
 * - FIXED: SyntaxError at line 4865 - orphaned closing brace from v7.4.4 cleanup
 * - CAUSE: sed removal of handleAddPhase left stray `};` behind
 * - IMPACT: Build failure, could not compile
 * - SOLUTION: Removed orphaned brace, added comment explaining removal
 * - VERIFICATION: Clean compilation, zero syntax errors
 * ==============================================================================
 * v7.4.4 (February 15, 2026) - ESLINT CLEANUP: Remove Unused Code
 * - REMOVED: 6 unused Table imports (Table, TableBody, TableCell, TableContainer, TableHead, TableRow)
 * - REMOVED: 3 unused Corps phase functions (updateCorpsPhase, addCorpsPhase, deleteCorpsPhase)
 * - REMOVED: 3 unused Corps config handlers (handleCorpsPersonnelChange, handleCorpsOperationalStateChange, handleCorpsPositionChange)
 * - REMOVED: 3 unused Brigade phase handlers (handleAddPhase, handleUpdatePhaseField, handleDeletePhase)
 * - REMOVED: renderCorpsConfiguration stub (deprecated, returns null)
 * - REMOVED: Dead forEach loop at line 4570 (assigned config but never used)
 * - PREFIXED: setEvacuationParadigm with underscore (read-only state)
 * - NOTE: extractedNationalStandard prefix REVERTED in v7.4.6 (variable IS used)
 * - TOTAL REMOVED: ~150 lines of orphaned code from Phase 2 UI development
 * - REASON: These functions were created for inline editing but became obsolete when
 *   CorpsConfigDialog and DivisionConfigDialog were built to handle phase management
 * - RESULT: Zero ESLint warnings, cleaner codebase, no functional changes
 * ==============================================================================
 * v7.4.3 (February 15, 2026) - COSMETIC FIX: Corps Count Display
 * - PROBLEM: Corps count showing as 0 in Force Structure Summary (line ~6814)
 * - ROOT CAUSE: getEchelonCounts only returned corps: 1 when echelon === 'CORPS'
 * - IMPACT: Cosmetic only - calculations were correct, display was wrong
 * - FIX: Check if corps object exists (hasCorps) instead of checking echelon string
 * - RESULT: Now shows "1 Corps" whenever corps exists in force structure
 * - LOCATION: getEchelonCounts useMemo (line ~3298), dependency added: corps
 * ==============================================================================
 * v7.4.2 (February 14, 2026) - BUG-B FIX:
 * - Added enemy_force_quality to operation_parameters (line ~5228) - required by calculator
 * - PROBLEM: Calculator expected operation_parameters.enemy_force_quality but got undefined
 * - ROOT CAUSE: Tab2 saved enemy_quality inside corps_config, not at operation_parameters root
 * - IMPACT: Force ratio calculations, enemy effectiveness modifiers - all using fallback values
 * - FIX: Added enemy_force_quality at ROOT level (keeps corps_config.enemy_quality for internal use)
 * - CASCADING EFFECTS: Affects attacker/defender ratios, enemy combat effectiveness, casualty rates
 * - VERIFICATION: localStorage now shows operation_parameters.enemy_force_quality with enemy settings
 * - RELATED: BUG-B documented, companion to Tab1 v4.6.4 (force_quality fix)
 * ==============================================================================
 * v7.4.1 (February 14, 2026) - BUG-057 FIX:
 * - Added evacuation_paradigm state (line ~3439) - default: 'CONTESTED'
 * - Added evacuation_paradigm to operation_parameters (line ~5220)
 * - Fixes: ~280 NaN warnings in survivalModule.js
 * - Impact: survivalModule.js now receives valid paradigm config (t1_survival_modifier)
 * - Values: PERMISSIVE, CONTESTED, DEGRADED, PROLONGED
 * ==============================================================================
 * v7.4.0 (February 13, 2026) - CRITICAL DATA LOSS FIX:
 * - Line 4480: Added ...brigade spread to preserve ALL Tab1 data
 * - Fixes: sub_units deletion (support battalions lost), name deletion, breakdown loss
 * - Impact: Calculator no longer shows [BUG-065] warnings, proper support categorization
 * - Line 3693: Already correct (useState initialization had spread)
 * ==============================================================================
 * v7.3.0 (February 11, 2026) - CLEANUP:
 * - STEP 1.1: Removed 57 debug console.log statements (0 kept)
 * - STEP 1.2: Removed dead imports AddIcon, DeleteIcon
 * - STEP 1.2: Migrated 7 force quality constants to combatEffectivenessModifiers.js imports
 *             Fixed field: .multiplier → .casualty_multiplier (5 locations)
 *             Fixed field: .friendly_mult → .friendly_casualty_multiplier
 *             Fixed field: .enemy_mult → .enemy_casualty_multiplier
 * - STEP 1.5: buildUpdates() now calls extractMedicalFacilities + calculateEvacuationDistances
 *             medical_configuration.mtf_hierarchy populated on every save
 * v7.2.2 (February 11, 2026) - BUG FIX: Two-part fix for brigade type change not updating frontage
 *   PART 1 - Stale closure in handleAutoSync setTimeout:
 *     - ROOT CAUSE: setTimeout captured OLD autoRedistributeFrontage (stale brigadeConfigs)
 *     - FIX: autoRedistributeFrontageRef (useRef) always points to latest memoized function
 *   PART 2 - unit_type watchdog useEffect:
 *     - ROOT CAUSE: Sync detection useEffect can miss unit_type changes in race conditions
 *       (hash comparison uses wrong snapshot, effect sees stale brigades)
 *     - FIX: Independent watchdog compares unit_type directly between brigades and brigadeConfigs,
 *       silently patches mismatches with correct frontage_status, position, and operational_state
 *       then redistributes frontage via ref — no sync dialog needed
 *   - Fixes: ARTILLERY→INFANTRY change in Tab1, Tab2 still shows no frontage after change
 * v7.2.1 (February 10, 2026) - BUG FIX: unit_type added to generateForceHash and modified detection
 *   - generateForceHash now includes unit_type → brigade type changes trigger sync dialog
 *   - modified detection now includes unit_type mismatch → sync correctly processes type changes
 *   - Fixes: MECHANIZED/ARMORED brigades showing no frontage after changing from ARTILLERY in Tab1
 * v7.2.0 (February 10, 2026) - UI RESTRUCTURE: logical section order, HQ positions promoted, phase status inline
 * v7.1.0 (February 10, 2026) - TOOLBAR NAVIGATION ARCHITECTURE
 * ==============================================================================
 * - ARCH: Added forwardRef + useImperativeHandle for toolbar navigation compatibility
 *   - App.jsx can now call tab2Ref.current.flushState() before toolbar tab switch
 *   - Tab2 is Pattern B: buffers all state locally, only writes on Save & Continue
 *   - flushState silently saves current state to scenario without validation gate
 *   - Prevents data loss when user navigates away before clicking Save & Continue
 * - REFACTOR: Extracted buildUpdates() useCallback from handleMarkComplete
 *   - buildUpdates() constructs operationParams + updatedForce from current state
 *   - handleMarkComplete: validates -> buildUpdates() -> onUpdate -> onComplete
 *   - flushState: buildUpdates() -> onUpdateRef.current (no validation, no navigation)
 *   - Single data construction path - flushState always matches handleMarkComplete
 * - ENCODING FIX: Removed all non-ASCII characters from code lines (emoji display corruption)
 *   - 125 code lines cleaned (emojis replaced with ASCII equivalents)
 *
 * ==============================================================================
 * Version: 7.0 - v3.0 ARCHITECTURAL REFACTOR: Single Source of Truth
 * 
 * ==============================================================================
 * ARCHITECTURAL REFACTOR in v7.0 (February 7, 2026 - v3.0 INTEGRATION)
 * ==============================================================================
 * 
 * PROBLEM SOLVED: Dual data storage causing inconsistencies
 * - BEFORE v7.0:
 *   - Tab 1 stores brigades in force.forceBuilderResult.brigades
 *   - Tab 2 CREATES COPIES and saves to operation_parameters.brigade_configs
 *   - Result: TWO sources of truth, positions inconsistent, ~420 duplicated data points
 * 
 * - AFTER v7.0:
 *   - Tab 2 MODIFIES brigades IN PLACE (spreads existing + adds operational fields)
 *   - Tab 2 saves back to force.forceBuilderResult.brigades (SINGLE source of truth)
 *   - Result: ALL tabs see same data, positions consistent, no duplication
 * 
 * CHANGES IMPLEMENTED:
 * 1. brigadeConfigs initialization (lines 3567-3766):
 *    - OLD: Created NEW objects with selected fields only
 *    - NEW: Spreads existing brigade + adds Tab2 operational fields
 *    - PRESERVES: All Tab 1 data including sub_units, composition_source, etc.
 * 
 * 2. Save operation (handleMarkComplete):
 *    - OLD: Saved to operation_parameters.brigade_configs
 *    - NEW: Saves back to force.forceBuilderResult.brigades
 *    - PASSES: Updated force structure + operation_parameters to onUpdate
 * 
 * 3. Backward compatibility:
 *    - Checks both locations during load (old brigade_configs, new brigades)
 *    - Prefers force.forceBuilderResult.brigades if exists
 *    - Automatic migration for any old scenarios (though none exist yet)
 * 
 * IMPACT:
 * -  Tab 3 now sees Tab 2's modified positions (evacuation chains correct)
 * -  Calculator gets complete brigade data (no missing fields)
 * -  50% less localStorage data (~420  ~210 data points)
 * -  Single save location = impossible to have inconsistent data
 * -  New Tab 1 fields automatically available to all tabs
 * 
 * BENEFITS:
 * - Data consistency: All tabs see same brigade data
 * - Simplified logic: Single save location, no manual field copying
 * - Reduced bugs: Impossible to have inconsistent positions
 * - Better performance: 50% less data in localStorage
 * - Robust architecture: Production-ready data flow
 * 
 * PRESERVES from v6.6:
 * - sub_units preservation (critical for Calculator v3.0)
 * - composition_source tracking
 * - All operational state features
 * - All phase hierarchy features
 * 
 * ADDED in v6.5 (January 31, 2026 - SECTION 12 FIX):
 * - AUTO-SYNC: Posture changes automatically update mission_type
 *   - Added getMissionFromPosture() helper function
 *   - Modified updateCorpsPhase() to auto-sync when operational_posture changes
 *   - Example: User changes posture to "DELIBERATE_ATTACK"  mission auto-updates to "ATTACK"
 *   - Console logs: " AUTO-SYNC: Posture 'DELIBERATE_ATTACK'  Mission 'ATTACK'"
 *   - Prevents invalid combinations like "Attack posture with Defend mission"
 *   - Cascades properly: Corps auto-sync  Divisions  Brigades
 * 
 * - GLOBAL VALIDATION WARNING: Shows all phase mismatches across operation
 *   - Checks Corps phases for posture/mission mismatches
 *   - Checks Division custom phases for mismatches
 *   - Checks Brigade custom phases for mismatches
 *   - Displays warning alert at top of Corps Configuration
 *   - Shows up to 5 mismatches with "...and X more" if needed
 *   - Example: "Division 2 Phase 1: HASTY_ATTACK (posture) expects ATTACK mission, but has DEFEND"
 * 
 * - RATIONALE: User feedback showed confusion when posture/mission don't match
 *   - Military doctrine: Posture MUST align with Mission
 *   - DELIBERATE_ATTACK  ATTACK mission
 *   - HASTY_DEFENSE  DEFEND mission
 *   - Auto-sync prevents user errors and ensures doctrinal compliance
 * 
 * - IMPACT: Prevents "nonsensical combinations" that violate military doctrine
 *   - Educational: Students learn proper PostureMission relationships
 *   - Operational: Ensures accurate casualty calculations
 *   - User Experience: One field change updates both (less work)
 * 
 * - COMPONENTS AFFECTED:
 *   - updateCorpsPhase() now includes auto-sync logic
 *   - renderCorpsConfiguration() now shows validation warning
 *   - CorpsConfigDialog.jsx (needs update for full auto-sync)
 *   - DivisionConfigDialog.jsx (needs update for full auto-sync)
 *   - BrigadePhaseTab.jsx (needs update for full auto-sync)
 * 
 * NOTE: Full auto-sync requires updating component files (CorpsConfigDialog, 
 * DivisionConfigDialog, BrigadePhaseTab) to implement same auto-sync logic 
 * when user changes posture in those dialogs.
 * 
 * ADDED in v6.4 (January 31, 2026):
 * - BUG FIX 1 COMPLETE: Added "Auto-Fix" button to posture/mission validation alert
 *   - Button appears in validation alert when mismatch detected
 *   - One-click fix: Updates mission_type to match posture automatically
 *   - Completes IMPLEMENTATION_ROADMAP Section 2 BUG FIX 1 in full
 *   - Example: HASTY_ATTACK posture + DEFEND mission  Click Auto-Fix  mission becomes ATTACK
 *   - User can still manually change mission or return to Tab 1 to change posture
 * - RATIONALE: Eliminates "partial completion" - auto-sync now fully functional
 * - IMPACT: Section 2 BUG FIX 1 now 100% complete (validation + auto-fix)
 * 
 * ADDED in v6.3 (January 31, 2026):
 * - BUG FIX 1: POSTURE/MISSION VALIDATION
 *   - Added validation for posture  mission_type compatibility
 *   - Validation alert shows when HASTY_ATTACK (posture) + DEFEND (mission) mismatch occurs
 *   - Uses existing postureToMissionType mapping for validation
 *   - Shows expected mission_type based on posture from Tab 1
 * - BUG FIX 2: TAB 1 vs TAB 2 MISSION CONFUSION
 *   - Renamed "Mission Type"  "Operational Mission" (clarity)
 *   - Added read-only display of "Assigned Task" from Tab 1 (tactical_role)
 *   - Added read-only display of "Operational Posture" from Tab 1 (posture)
 *   - Mission tab now clearly shows:
 *     - Assigned Task (from higher echelon, Tab 1) - Main Effort/Supporting/etc
 *     - Operational Posture (from Tab 1) - Deliberate Attack/Hasty Defense/etc
 *     - Operational Mission (Tab 2 config) - Attack/Defend/etc
 *   - Prevents confusion between Assigned Task vs Operational Mission
 * - RATIONALE: Users were confused by two different "missions"
 *   - Tab 1 Assigned Task = What higher echelon ordered (Main Effort role)
 *   - Tab 2 Operational Mission = Type of operation executing (Attack/Defend)
 *   - Tab 1 Posture must match Tab 2 Mission (HASTY_ATTACK  must be ATTACK mission)
 * 
 * ADDED in v6.2 (January 30, 2026):
 * - UI-001: Added Corps Configuration button in Corps Config section
 * - UI-002: Added CorpsConfigDialog component with full phase management
 * - UI-003: Added Division Configuration button in Division Overview accordion
 * - UI-004: Added DivisionConfigDialog component with phase inheritance controls
 * - UI-005: Replaced old phase management UI with BrigadePhaseTab component
 * - UI-006: Connected all handler functions (handleEditCorpsConfig, handleEditDivisionConfig, etc.)
 * - UI-007: Wired up toggleBrigadeInheritance to BrigadePhaseTab component
 * - COMPLETED: Phase 2 full integration (backend + UI)
 * - STATUS: All 7 steps from PHASE2_BUGFIX_SUMMARY.md implemented
 * - RATIONALE: Users can now configure Corps  Division  Brigade phase hierarchy with visual interface
 * 
 * FIXES in v6.1 (January 30, 2026):
 * - FIX-ESL-001: Removed duplicate 'phases' key at line 3345 (main brigade init)
 * - FIX-ESL-002: Removed duplicate 'phases' key at line 3893 (CRIT-002 useEffect)
 * - FIX-ESL-003: Added cascadeAllDivisionPhasesToBrigades to useCallback deps
 * - ADDED: 9 handler functions for Corps/Division configuration:
 *   - handleCorpsPersonnelChange, handleCorpsOperationalStateChange, handleCorpsPositionChange
 *   - handleEditCorpsConfig, handleCloseCorpsDialog, handleSaveCorpsConfig
 *   - handleEditDivisionConfig, handleCloseDivisionDialog, handleSaveDivisionConfig
 * - DOCUMENTED: All Phase management functions (updateCorpsPhase, addCorpsPhase, deleteCorpsPhase,
 *   toggleDivisionInheritance, toggleBrigadeInheritance) are READY FOR UI but not called yet
 * - RATIONALE: Phase 2 split into 2 sessions - backend complete, UI components pending
 * - STATUS: All ESLint warnings resolved, all setters have handlers, save/load working
 * 
 * PHASE 2 INTEGRATION in v6.0 (January 30, 2026):
 * - ADDED: Corps-level personnel, operational state, position, and phases
 * - ADDED: Division-level configuration with phase inheritance from Corps
 * - ADDED: Brigade-level phase inheritance from Division
 * - ADDED: Phase cascade functions (calculatePhaseDays, cascadeCorpsPhasesToDivisions, etc.)
 * - ADDED: divisionConfigs state with automatic initialization from Tab1 divisions
 * - ADDED: corpsPhases, corpsPersonnel, corpsOperationalState, corpsPositionFeba state
 * - ADDED: uses_division_phases and phases fields to brigadeConfigs
 * - ADDED: Toggle functions for inheritance control (toggleDivisionInheritance, toggleBrigadeInheritance)
 * - UPDATED: Save operation now includes corps_config.phases and division_configs
 * - UPDATED: brigadeConfigs initialization includes phase inheritance fields
 * - STATUS: Backend integration complete, UI components to be added in next session
 * - RATIONALE: Enables hierarchical planning with automatic cascade from Corps  Division  Brigade
 *   - Corps sets operational phases for entire operation
 *   - Divisions can inherit Corps phases or define custom phases
 *   - Brigades can inherit Division phases or define custom phases
 *   - Any change at higher echelon automatically cascades to inheriting units
 * - TESTING: Console shows " PHASE2: Initializing division configs..."
 * 
 * PHASE 1 CLEANUP in v5.15 (January 30, 2026):
 * - REMOVED: Orphaned Division Operational Posture dropdown (was not connected to brigades)
 * - REMOVED: divisionConfigs state and associated functions (updateDivisionConfig, handleApplyDivisionPhases)
 * - REMOVED: division_configs from save operation
 * - UPDATED: Division Overview section now shows read-only info only
 * - RATIONALE: Clean separation - Tab 1 (WHO) vs Tab 2 (WHAT)
 *   - Division posture dropdown did not cascade to brigades
 *   - Created confusion (3 different sources for posture/mission)
 *   - Calculator only reads from brigade_configs (per-brigade settings)
 * - IMPACT: None on calculator (was not reading division_configs)
 * - FORWARD: Phase 2 will add proper division-level phases with auto-cascade
 * 
 * FIXES in v5.14 (January 30, 2026):
 * - BUG FIX: Added SUPPORT and SUSTAINMENT brigade types to UNIT_TYPE_DOCTRINE
 *   - SUPPORT: US terminology - Multifunctional sustainment (supply, transport, maintenance)
 *   - SUSTAINMENT: Alias for SUPPORT
 *   - Both positioned FAR REAR (55km default, 30-80km range)
 *   - Both have has_frontage: false (CSS brigades don't hold frontage)
 *   - Fixes NATO SUPPORT brigades incorrectly holding frontage
 *   - Distinguishes SUPPORT (US) from LOGISTICS (NATO) in doctrine
 * 
 * FIXES in v5.13 (January 28, 2026):
 * - UI FIX: Brigade ID display replaced with human-readable position
 *   - BEFORE: "Brigade ID: brig_1769574332494_iuej0ym7d" (meaningless UUID)
 *   - AFTER: "Position: Brigade 1 of Division 1 (3 brigades in division)"
 *   - Critical for multinational/multi-division scenarios (e.g., 9 brigades across 3 divisions)
 *   - Shows: brigade number, parent division, total brigades in that division
 * 
 * FIXES in v5.12 (January 28, 2026):
 * - BUG FIX: Phases can create invalid overlaps/gaps causing calculator errors
 *   - Changed UI: Users now enter DURATION only, not start/end days
 *   - Auto-sequential calculation: Each phase automatically starts after previous ends
 *   - Display shows: Duration input + calculated days (e.g., "Duration: 3 (1-3)")
 *   - Update duration: All subsequent phases recalculate automatically
 *   - Delete phase: Remaining phases recalculate to fill gaps
 *   - Added validation alert: Shows if total phase days  operation duration
 *     - Warning: Days not covered (need more phases)
 *     - Error: Phases exceed duration (reduce durations)
 *     - Success: All days covered 
 *   - Makes it IMPOSSIBLE to create overlapping or invalid phases
 *   - Example: Duration 10 days  Phase1: 3d (1-3), Phase2: 4d (4-7), Phase3: 3d (8-10)
 * 
 * FIXES in v5.11 (January 28, 2026):
 * - BUG FIX: Corps Configuration showing "0 Divisions" when Echelon = DIVISION
 *   - Added getEchelonCounts() function that properly calculates display based on echelon
 *   - BATTALION: Shows 1 Battalion
 *   - BRIGADE: Shows 1 Brigade, X Battalions
 *   - DIVISION: Shows 1 Division, X Brigades, Y Battalions (was 0 Divisions)
 *   - CORPS: Shows 1 Corps, X Divisions, Y Brigades, Z Battalions
 *   - Conditional display: Only shows chips for echelons that exist at current level
 *   - Added proper pluralization (Brigade vs Brigades)
 * 
 * FIXES in v5.10 (January 19, 2026):
 * - CRIT-007 FIX: Changed onUpdate() to only pass operation_parameters
 *   - BEFORE: onUpdate({ ...scenario, operation_parameters }) - spreads stale scenario
 *   - AFTER: onUpdate({ operation_parameters }) - App merges with current state
 *   - Root cause: When Tab 3 saves, its scenario prop is stale (doesn't have Tab 2's data)
 *     so spreading it overwrites Tab 2's operation_parameters with undefined
 *   - Symptom: Validation shows "No brigade configurations - Tab 2 data not saved"
 *     even after clicking "Save & Continue" in Tab 2
 * 
 * FIXES in v5.9 (January 18, 2025):
 * - CRIT-002 FIX: Added useEffect to auto-populate brigadeConfigs when:
 *   - brigadeConfigs is empty (Tab2 initialized before Tab1 loaded)
 *   - brigades array has items (Tab1 data now available)
 *   - No saved config exists (don't overwrite user's work)
 *   - Root cause: useState initializer only runs once, so if Tab2 mounts
 *     before Tab1 data arrives, brigadeConfigs stays empty forever
 *   - Symptom: Calculator error "brigade_configs must be an array"
 * 
 * FIXES in v5.8 (January 12, 2025):
 * - FIX-T2-009: Added `configured: true` to operation_parameters when saving
 *   - Addresses validation warning "Operation parameters marked as incomplete"
 * - FIX-T2-010: Added top-level `threat_level` to operation_parameters
 *   - Validation now finds it without searching nested paths
 * 
 * FIXES in v5.7 (January 12, 2025):
 * - FIX-T2-008: Default configMode based on global mode prop
 *   - Expert mode: defaults to DETAILED view
 *   - Standard mode: defaults to STANDARD (summary) view
 * 
 * FIXES in v5.6 (January 12, 2025):
 * - FIX-T2-006: Fixed battle_modifier  battle_casualty_multiplier (line 4491, 4493)
 *   - Was showing "undefinedx" for battle modifier in brigade rows
 * - FIX-T2-007: Fixed dnbi_rate_per_1000_day  dnbi?.combined_rate (lines 840, 874, 4442, 5049)
 *   - Was showing "undefined/1k DNBI" in legend and state displays
 *   - Now correctly reads from operationalStates.js dnbi.combined_rate property
 * 
 * FIXES in v5.5 (January 9, 2025):
 * - UI-009: Simplified mode selector from 3 options to 2
 *   - Removed redundant EXPERT button (BRIGADE and EXPERT did the same thing)
 *   - Now: "Summary View" (corps-level) vs "Detailed View" (per-brigade)
 *   - Global Expert mode (from App.jsx) still controls position editing
 *   - Backward compatible: 'BRIGADE'/'EXPERT' treated as 'DETAILED'
 * 
 * FIXES in v5.4 (January 7, 2025):
 * - FIX-T2-001: Removed redundant external expanded states (panels manage own state)
 * - FIX-T2-002: Added toggle control for autoCalculate
 * - FIX-T2-003: Added selector for overallMission  
 * - FIX-T2-004: Added toggle for usePhases with phases management
 * - FIX-T2-005: Added division config update handlers
 * - All 8 unused state setters now properly utilized
 * 
 * NEW in v5.3 (Tasks 2.12-2.24):
 * - Infrastructure Status: Power, Communications, Water, Local Medical
 * - Host Nation Support (HNS): Support levels, hospitals, ambulances
 * - Night Operations: Night ops levels, illumination, NVG availability
 * - Climate & Endemic Disease: Climate zones, disease risk, water/food safety, DNBI rates
 * - MRoE (EPW/Civilian): Enemy prisoner and civilian casualty planning
 * - Advanced Features Panel: Consolidated toggle panel for all optional features
 * - All configurations saved with modifiers for calculator integration
 * 
 * NEW in v5.2 (Task 2.8):
 * - Added LOC Configuration section for ground evacuation planning
 * - MSR/ASR road status selectors (PAVED/IMPROVED/UNPAVED/DAMAGED/DENIED)
 * - LOC security level selector (SECURE/GUARDED/CONTESTED/INTERDICTED/DENIED)
 * - Primary bridge status selector with MLC ratings
 * - Weather impact on routes (CLEAR/RAIN/SNOW/ICE/FOG/DUST)
 * - Evacuation time modifier calculation
 * - Sample travel time display (15km, 30km, 60km)
 * - Golden Hour risk warning when ground evac exceeds 60 minutes
 * - Route risk assessment (ambush/IED probability)
 * - Requirements display (escort, armored vehicles, engineering)
 * - Preset configurations (Default, High Threat)
 * - Integrates with casualty calculator via loc_config.modifiers
 * 
 * NEW in v5.1 (Task 2.3):
 * - Added comprehensive Threat Assessment section
 * - Threat type selector (CONVENTIONAL/ASYMMETRIC/HYBRID/PEACEKEEPING/HIGH_INTENSITY)
 * - Enemy AD level selector (affects MEDEVAC availability)
 * - IED threat level selector (affects ground evacuation)
 * - Insurgent activity selector (affects rear area security)
 * - CBRN risk selector (affects treatment capacity)
 * - Artillery threat selector (Ukraine lessons learned)
 * - Drone threat selector (FPV/reconnaissance)
 * - Impact preview showing combined modifiers
 * - Threat warnings with recommendations
 * - Integrates with casualty calculator
 * 
 * NEW in v5.0 (Task 2.1):
 * - Added OPERATIONAL_STATES constants with battle/DNBI modifiers
 * - Added operational_state field to brigade_configs
 * - Auto-assignment of operational state based on position and unit type
 * - UI selector for operational state per brigade
 * - Expert Mode: Position editable for support brigades
 * - Impact display showing casualty multipliers and DNBI rates
 * - Doctrine range warnings for position outside recommended values
 * - Position auto-updates operational state (can be overridden)
 * 
 * FIXES in v4.4:
 * - handleAutoSync now updates unit_type when brigade type changes in Tab1
 * - handleAutoSync now recalculates frontage_status when unit_type changes
 * - handleAutoSync now updates evacuation_assets from Tab1
 * - handleAutoSync now updates operation posture and mission_type
 * - handleAutoSync now redistributes frontage after sync
 * - Fixed: MEDICAL brigade changed in Tab1 now properly moves to rear in Tab2
 * 
 * FIXES in v4.3:
 * - Support units (MEDICAL, LOGISTICS, ARTILLERY, SIGNAL) automatically placed in rear
 * - MARCH/MARCHING posture units excluded from frontage
 * - Auto-redistribute frontage on initial load
 * - Proper position_feba_km based on unit type (-40km for MEDICAL, -60km for LOGISTICS, etc.)
 * - Added frontage_status field to track frontage eligibility and reason
 * - Added MOVEMENT mission type for MARCH/TRANSIT postures
 * - Fixed all postureToMissionType mappings to include MARCH variants
 * 
 * FIXES in v4.2:
 * - BUG-002: Sync detection now properly detects Tab1 changes (was empty [])
 * - BUG-003: Save & Continue button now activates after sync (consequence of BUG-002)
 * - Posture fallback: Changed 'DEFENSE' to 'DELIBERATE_DEFENSE' (3 locations)
 * - Strength fallback: Uses national standard (ROU:4000, NATO:4500, US:5000)
 * 
 * FIXES in v4.1:
 * - Added UNIT_TYPE_HAS_FRONTAGE map for proper unit type filtering
 * - LOGISTICS, ARTILLERY, AVIATION, SIGNAL, MEDICAL excluded from frontage map
 * - RESERVE, SUPPORTING, MARCHING, WITHDRAWAL postures excluded
 * - autoRedistributeFrontage now only assigns frontage to eligible units
 * - Enhanced rear unit display with reason chips
 */

import React, { useState, useEffect, useRef, useMemo, useCallback, forwardRef, useImperativeHandle } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Alert,
  AlertTitle,
  Grid,
  Paper,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Chip,
  Checkbox,
  FormControlLabel,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Collapse,
  Switch,
  Stack,
  Slider,
  Popover,
  ToggleButton,
  ToggleButtonGroup,
  Snackbar,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  FormHelperText
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Warning as WarningIcon,
  Edit as EditIcon,
  People as PeopleIcon,
  Flight as FlightIcon,
  LocalHospital as LocalHospitalIcon,
  Shield as ShieldIcon,
  Security as SecurityIcon,
  Radar as RadarIcon,
  ContentCopy as CopyIcon,
  GpsFixed as GpsFixedIcon,
  Speed as SpeedIcon,
  Lock as LockIcon,
  AccountTree as AccountTreeIcon
} from '@mui/icons-material';
import {
  OPERATION_TYPES,
  TERRAIN_TYPES,
  OPERATIONAL_POSTURES,
  ENGAGEMENT_LEVELS,
  WEATHER_CONDITIONS,
  THREAT_LEVELS,
  MEDICAL_PROTECTION_DEFAULT,
  MEDICAL_PROTECTION_THRESHOLDS
} from '../constants/operationsConfig';
import { NATIONAL_STANDARDS } from '../constants/nationalStandards';
import { enemyPowerIndex, levelOfIndex, RESISTANCE_ANCHORS } from '../constants/phaseCasualtyTable';
import HelpText from '../components/HelpText';
import Tab2CasualtyPreview from '../components/Tab2CasualtyPreview';
import { EnemyIndexContext, OperationWeatherContext } from '../components/PhaseCasualtyControls';
import { resolveRoadState, appliedThreatSummary } from '../constants/threatEffects';
import { AIR_SITUATION_CEILING } from '../constants/threatEffects';
import { CLIMATE_OPTION_STATUS } from '../constants/climateZones';
import { DEFAULT_START_TIME_Z, seasonOfDay, endInstant, formatZulu } from '../constants/exerciseDates';
import EnemyForcesPanel from '../components/EnemyForcesPanel';
import { buildSectors } from '../calculationEngine/modules/enemyForces';
import { defaultTotalFrontageKm } from '../constants/frontageDensity';

import {
  TRAINING_LEVELS,
  EQUIPMENT_LEVELS,
  MORALE_LEVELS,
  FORCE_SUPERIORITY,
  EXPERIENCE_MODIFIERS as EXPERIENCE_LEVELS,
  TECHNOLOGY_ENVIRONMENT,
  MEDICAL_CAPABILITY
} from '../constants/combatEffectivenessModifiers';

import {
  extractMedicalFacilities,
  calculateEvacuationDistances
} from '../services/MedicalService';
import { getBrigadeHQPosition } from '../constants/brigadeTypeOffsets';
import {
  THREAT_TYPES,
  ENEMY_AD_LEVELS,
  IED_THREAT_LEVELS,
  INSURGENT_ACTIVITY,
  CBRN_RISK,
  ARTILLERY_THREAT,
  DRONE_THREAT,
  DRONE_DOW_MODIFIER_RANGE,
  getThreatModifiers,
  validateThreatConfiguration,
  DEFAULT_THREAT_CONFIG,
  UKRAINE_2024_BASELINE
} from '../constants/threatAssessment';
import {
  ROAD_STATUS,
  LOC_SECURITY,
  BRIDGE_STATUS,
  WEATHER_ROUTE_IMPACTS,
  getLOCModifiers,
  validateLOCConfiguration,
  DEFAULT_LOC_CONFIG,
  HIGH_THREAT_LOC_CONFIG
} from '../constants/locConfig';
import {
  POWER_STATUS,
  COMMS_STATUS,
  WATER_STATUS,
  LOCAL_MEDICAL_STATUS,
  getInfrastructureModifiers,
  validateInfrastructureConfig,
  DEFAULT_INFRASTRUCTURE_CONFIG,
  AUSTERE_INFRASTRUCTURE_CONFIG
} from '../constants/infrastructureStatus';
import {
  HNS_SUPPORT_LEVELS,
  getHNSModifiers,
  validateHNSConfig,
  DEFAULT_HNS_CONFIG,
  NATO_EXERCISE_HNS_CONFIG
} from '../constants/hnsConfig';
import {
  AIR_SITUATION_LEVELS,
  DEFAULT_AIR_SITUATION_CONFIG,
  validateAirSituationConfig
} from '../constants/airSituation';
import {
  NIGHT_OPS_LEVELS,
  ILLUMINATION_CONDITIONS,
  NVG_AVAILABILITY,
  getNightOpsModifiers,
  validateNightOpsConfig,
  DEFAULT_NIGHT_OPS_CONFIG,
  CONVENTIONAL_NIGHT_OPS_CONFIG
} from '../constants/nightOperations';
import {
  CLIMATE_ZONES,
  ENDEMIC_DISEASE_LEVELS,
  WATER_FOOD_SAFETY,
  THEATER_REGIONS,
  calculateDNBIRate,
  getClimateModifiers,
  validateClimateConfig,
  DEFAULT_CLIMATE_CONFIG,
  EASTERN_EUROPE_CONFIG
} from '../constants/climateZones';
import {
  EPW_THREAT_LEVELS,
  CIVILIAN_CASUALTY_LEVELS,
  getMRoEModifiers,
  validateMRoEConfig,
  DEFAULT_MROE_CONFIG,
  OFFENSIVE_MROE_CONFIG,
  URBAN_MROE_CONFIG,
  PEACEKEEPING_MROE_CONFIG,
  MROE_STATES,
  PATIENT_CATEGORIES,
  MROE_DEFAULT_MATRICES,
  MROE_Y_STAR_CONDITIONS
} from '../constants/mroeConfig';

// NEW: Import from dedicated operationalStates.js file
import {
  OPERATIONAL_STATES,
  getOperationalStateOptions,
  getBattleCasualtyMultiplier,
  getDNBIRate,
  getDefaultStateForUnitType,
  validatePositionForState
} from '../constants/operationalStates';

// Import from components folder
import { DEFAULT_EVACUATION_PARADIGM, resolveEvacuationParadigm } from '../constants/evacuationParadigms';
import { PARADIGM_HOLD_HOURS, HOLD_HOURS_LIMIT } from '../constants/woundHazardParameters';
import CorpsConfigDialog from '../components/CorpsConfigDialog';
import { evacuationWindow } from '../calculationEngine/modules/evacuationWindowModel';
import { eligibilityLoad } from '../calculationEngine/modules/civilianLoad';
import DivisionConfigDialog from '../components/DivisionConfigDialog';
import BrigadePhaseTab from '../components/BrigadePhaseTab';
import { markDirty } from '../services/calculationService.js';
import { lsRead } from '../utils/lsStore.js';
import { useTranslation } from 'react-i18next';

// V4-014: CoA B detection — localStorage keys (mirrors Tab5/Tab6)
const COA_KEY_A = 'psp_cre_coa_1';
const COA_KEY_B = 'psp_cre_coa_2';
// ============================================================================
// OPERATIONAL STATES - Imported from '../constants/operationalStates'
// See operationalStates.js for full configuration
// ============================================================================


// ============================================================================
// UNIT TYPE DOCTRINE POSITIONS (km from FEBA - negative = behind FEBA)
// Used for auto-assignment and validation
// ============================================================================

const UNIT_TYPE_DOCTRINE = {
  // Combat/Maneuver Units - ON FEBA
  HEAVY: { 
    has_frontage: true, 
    default_position: 5,
    position_range: { min: 0, max: 10 },
    default_state: 'COMBAT_ENGAGED',
    category: 'COMBAT'
  },
  INFANTRY: { 
    has_frontage: true, 
    default_position: 3,
    position_range: { min: 0, max: 8 },
    default_state: 'COMBAT_ENGAGED',
    category: 'COMBAT'
  },
  MECHANIZED: { 
    has_frontage: true, 
    default_position: 5,
    position_range: { min: 0, max: 10 },
    default_state: 'COMBAT_ENGAGED',
    category: 'COMBAT'
  },
  AIRBORNE: { 
    has_frontage: true, 
    default_position: 3,
    position_range: { min: 0, max: 8 },
    default_state: 'COMBAT_ENGAGED',
    category: 'COMBAT'
  },

  // v7.9.17 FIX (UT-GAP-002): Previously missing combat/maneuver types
  AIR_ASSAULT: {
    has_frontage: true,
    default_position: 2,
    position_range: { min: 0, max: 8 },
    default_state: 'COMBAT_ENGAGED',
    category: 'COMBAT',
    notes: 'Helicopter-mobile infantry — same forward posture as AIRBORNE'
  },
  MOUNTAIN: {
    has_frontage: true,
    default_position: 2,
    position_range: { min: 0, max: 8 },
    default_state: 'COMBAT_ENGAGED',
    category: 'COMBAT',
    notes: 'Mountain infantry — forward posture, dispersed in terrain'
  },
  ARMOR: {
    has_frontage: true,
    default_position: 5,
    position_range: { min: 0, max: 12 },
    default_state: 'COMBAT_ENGAGED',
    category: 'COMBAT',
    notes: 'US Armored BCT — alias for HEAVY, same positioning'
  },
  STRYKER: {
    has_frontage: true,
    default_position: 4,
    position_range: { min: 0, max: 10 },
    default_state: 'COMBAT_ENGAGED',
    category: 'COMBAT',
    notes: 'US medium-weight BCT — between HEAVY and INFANTRY in standoff'
  },
  RECONNAISSANCE: {
    has_frontage: true,
    default_position: 0,
    position_range: { min: -5, max: 5 },
    default_state: 'COMBAT_ENGAGED',
    category: 'COMBAT',
    notes: 'Reconnaissance screening — HQ at FEBA, elements forward'
  },
  ARTILLERY: { 
    has_frontage: false, 
    default_position: 20,
    position_range: { min: 15, max: 30 },
    default_state: 'SUPPORT_ACTIVE',
    category: 'COMBAT_SUPPORT',
    warning_if_closer: 15  // Counter-battery range warning
  },
  AIR_DEFENSE: { 
    has_frontage: false, 
    default_position: 25,
    position_range: { min: 10, max: 40 },
    default_state: 'SUPPORT_ACTIVE',
    category: 'COMBAT_SUPPORT'
  },
  
  // Combat Service Support - FAR REAR
  LOGISTICS: { 
    has_frontage: false, 
    default_position: 60,
    position_range: { min: 30, max: 80 },
    default_state: 'SUPPORT_ACTIVE',
    category: 'CSS',
    notes: 'NATO terminology - Supply-focused brigade'
  },
  SIGNAL: { 
    has_frontage: false, 
    default_position: 35,
    position_range: { min: 20, max: 50 },
    default_state: 'SUPPORT_ACTIVE',
    category: 'CSS'
  },
  MEDICAL: { 
    has_frontage: false, 
    default_position: 40,
    position_range: { min: 15, max: 60 },
    default_state: 'SUPPORT_ACTIVE',
    category: 'CSS',
    notes: 'Medical Brigade provides Role 2/3 to OTHER units'
  },
  AVIATION: { 
    has_frontage: false, 
    default_position: 80,
    position_range: { min: 40, max: 100 },
    default_state: 'SUPPORT_ACTIVE',
    category: 'CSS',
    notes: 'Airfield requirements'
  },
  // v7.9.17 FIX (UT-GAP-002): ENGINEERS (brigade) and HQ — previously missing; 2026-10-24: one key, the older ENGINEERS row (frontage true) removed
  ENGINEERS: {
    has_frontage: false,
    default_position: 8,
    position_range: { min: 3, max: 20 },
    default_state: 'COMBAT_ENGAGED',
    category: 'COMBAT_SUPPORT',
    notes: 'Combat engineer — breaching/obstacle ops, task-attached near frontline'
  },
  HQ: {
    has_frontage: false,
    default_position: 30,
    position_range: { min: 15, max: 50 },
    default_state: 'SUPPORT_ACTIVE',
    category: 'CSS',
    notes: 'Corps/Division HQ element — precision-strike target, well behind FEBA'
  }
};

// Default doctrine for unknown unit types
const DEFAULT_UNIT_DOCTRINE = {
  has_frontage: true,
  default_position: 5,
  position_range: { min: 0, max: 15 },
  default_state: 'COMBAT_ENGAGED',
  category: 'COMBAT'
};

// ============================================================================
// UNIT TYPE FRONTAGE CONFIGURATION (kept for backward compatibility)
// ============================================================================

const UNIT_TYPE_HAS_FRONTAGE = {
  // Original entries — matching UNIT_TYPES keys only
  HEAVY: true,
  INFANTRY: true,
  MECHANIZED: true,
  AIRBORNE: true,
  LOGISTICS: false,
  ARTILLERY: false,
  AVIATION: false,
  SIGNAL: false,
  MEDICAL: false,
  AIR_DEFENSE: false,
  // v7.9.17 FIX (UT-GAP-002): Added 7 missing UNIT_TYPES keys
  AIR_ASSAULT: true,
  MOUNTAIN: true,
  ARMOR: true,
  STRYKER: true,
  RECONNAISSANCE: true,
  ENGINEERS: false,  // Combat support — near front but no holding frontage
  HQ: false          // Command post — no frontage
};

/**
 * Check if a unit type holds frontage
 */
const unitTypeHasFrontage = (unitType) => {
  if (unitType in UNIT_TYPE_HAS_FRONTAGE) {
    return UNIT_TYPE_HAS_FRONTAGE[unitType];
  }
  return UNIT_TYPE_DOCTRINE[unitType]?.has_frontage ?? true;
};

/**
 * Get doctrine for a unit type
 */
const getUnitTypeDoctrine = (unitType) => {
  return UNIT_TYPE_DOCTRINE[unitType] || DEFAULT_UNIT_DOCTRINE;
};

/**
 * Auto-assign operational state based on position (km from FEBA)
 * Note: Position values are POSITIVE for behind FEBA (e.g., 20km means 20km behind FEBA)
 */
const getOperationalStateFromPosition = (positionKm, unitType) => {
  const absPosition = Math.abs(positionKm);
  
  // First check unit type default - support units should stay in support/reserve
  const typeDefault = getDefaultStateForUnitType(unitType);
  
  // If unit type suggests non-combat (SUPPORT_ACTIVE or TRUE_RESERVE), use that as minimum
  if (typeDefault === 'TRUE_RESERVE') {
    return 'TRUE_RESERVE';
  }
  
  if (typeDefault === 'SUPPORT_ACTIVE') {
    // Support units can be in SUPPORT_ACTIVE or TRUE_RESERVE based on position
    return absPosition <= 60 ? 'SUPPORT_ACTIVE' : 'TRUE_RESERVE';
  }
  
  // Combat units - determine by position
  if (absPosition <= 15) {
    return 'COMBAT_ENGAGED';
  }
  if (absPosition <= 60) {
    return 'SUPPORT_ACTIVE';
  }
  return 'TRUE_RESERVE';
};

/**
 * Validate position against doctrine and return warnings
 */
const validatePositionAgainstDoctrine = (positionKm, unitType) => {
  const doctrine = getUnitTypeDoctrine(unitType);
  const warnings = [];
  
  const absPosition = Math.abs(positionKm);
  
  if (absPosition < doctrine.position_range.min) {
    warnings.push({
      type: 'TOO_FORWARD',
      message: `${unitType} at ${absPosition}km is forward of doctrine minimum (${doctrine.position_range.min}km)`,
      severity: 'warning'
    });
  }
  
  if (absPosition > doctrine.position_range.max) {
    warnings.push({
      type: 'TOO_REAR',
      message: `${unitType} at ${absPosition}km is beyond doctrine maximum (${doctrine.position_range.max}km)`,
      severity: 'info'
    });
  }
  
  // Specific warning for artillery in counter-battery range
  if (doctrine.warning_if_closer && absPosition < doctrine.warning_if_closer) {
    warnings.push({
      type: 'COUNTER_BATTERY',
      message: `${unitType} at ${absPosition}km is within enemy counter-battery range (<${doctrine.warning_if_closer}km)`,
      severity: 'error'
    });
  }
  
  return warnings;
};

// ============================================================================
// ============================================================================

// Default force quality values
const DEFAULT_FORCE_QUALITY = {
  training_level: 'REGULAR',
  equipment_level: 'ADEQUATE',
  morale_level: 'NORMAL',
  experience_level: 'OVER_180_DAYS',
  technology_env: 'DRONE_CONTESTED',
  medical_capability: 'GOOD'
};

// 2026-10-13 (Tab 2 plan, step 3): the force superiority label (overwhelming ... severely inferior) never reached the casualty
// number (the calculator reads a numeric strength ratio). The enemy to friendly strength ratio below replaces it; the old
// dropdown is kept behind this flag, hidden.
const SHOW_FORCE_SUPERIORITY_LABEL = false;

// 2026-10-13 (Tab 2 plan 7.2): the LOC weather selector is hidden: the road weather is the operation weather (one weather).
const SHOW_LOC_WEATHER_SELECTOR = false;

// Default enemy quality
const DEFAULT_ENEMY_QUALITY = {
  training_level: 'REGULAR',
  equipment_level: 'ADEQUATE',
  morale_level: 'NORMAL'
};

// ============================================================================
// RESERVE BRIGADE CONSTANTS
// ============================================================================

const RESERVE_POSITIONS = {
  CLOSE_RESERVE: -15,
  DISTANT_RESERVE: -30
};

const RESERVE_CASUALTY_MODIFIERS = {
  CLOSE_RESERVE: 0.3,
  DISTANT_RESERVE: 0.1
};

// ============================================================================
// SUPPORT UNIT POSITIONS (distance from FEBA in km, POSITIVE = behind FEBA)
// Updated to use positive values for clearer understanding
// ============================================================================

const SUPPORT_UNIT_POSITIONS = {
  LOGISTICS: 60,
  ARTILLERY: 20,
  AVIATION: 80,
  SIGNAL: 35,
  MEDICAL: 40,
  AIR_DEFENSE: 25,
  MAINTENANCE: 50,
  HQ: 30,
  DEFAULT_COMBAT: 5
};

// ============================================================================
// NON-FRONTAGE POSTURES (units in these postures don't hold frontage)
// ============================================================================

const NON_FRONTAGE_POSTURES = [
  'MARCH',
  'MARCHING',
  'RESERVE',
  'WITHDRAWAL',
  'RETIRE',
  'TRANSIT',
  'RECONSTITUTION',
  'REST',
  'TRAINING'
];

// ============================================================================
// NON-FRONTAGE MISSIONS (units with these missions don't hold frontage)
// ============================================================================

const NON_FRONTAGE_MISSIONS = [
  'RESERVE',
  'SUPPORTING',
  'FOLLOW_AND_SUPPORT',
  'REAR_GUARD',
  'REAR_AREA_SECURITY',
  'ADMINISTRATIVE'
];

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================


/**
 * Determine if a brigade should hold frontage based on unit type AND posture
 */
const getBrigadeFrontageStatus = (unitType, posture, missionType) => {
  if (!unitTypeHasFrontage(unitType)) {
    const doctrine = getUnitTypeDoctrine(unitType);
    // Use SUPPORT_UNIT_POSITIONS for specific unit type positioning
    const supportPosition = SUPPORT_UNIT_POSITIONS[unitType] || doctrine.default_position;
    return {
      holdsFrontage: false,
      reason: `Support unit (${unitType})`,
      position_km: supportPosition,
      casualty_modifier: 0.15
    };
  }
  
  if (NON_FRONTAGE_POSTURES.includes(posture)) {
    return {
      holdsFrontage: false,
      reason: `${posture} posture`,
      position_km: SUPPORT_UNIT_POSITIONS.HQ || 30,
      casualty_modifier: posture === 'RESERVE' ? RESERVE_CASUALTY_MODIFIERS.DISTANT_RESERVE : 0.20
    };
  }
  
  if (NON_FRONTAGE_MISSIONS.includes(missionType)) {
    return {
      holdsFrontage: false,
      reason: `${missionType} mission`,
      position_km: SUPPORT_UNIT_POSITIONS.DEFAULT_COMBAT || 25,
      casualty_modifier: 0.25
    };
  }
  
  return {
    holdsFrontage: true,
    reason: 'Combat unit on frontage',
    position_km: SUPPORT_UNIT_POSITIONS.DEFAULT_COMBAT || 5,
    casualty_modifier: 1.0
  };
};

// NOTE: calculateCasualtyModifier and calculateEnvironmentalModifier were removed
// as they duplicate functionality from getThreatModifiers() and other imported functions

const getNationalStandardModifier = (nationalStandard) => {
  const modifiers = {
    'ROMANIAN': 0.95,
    'NATO': 1.0,
    'US': 1.05,
    'MULTINATIONAL': 1.0
  };
  return modifiers[nationalStandard] || 1.0;
};

const getHelicopterAvailability = (helicopterCount) => {
  if (helicopterCount === 0) return 'NONE';
  if (helicopterCount <= 2) return 'MINIMAL';
  if (helicopterCount <= 5) return 'MODERATE';
  return 'EXTENSIVE';
};

/**
 * Calculate combined force quality multiplier
 */
const calculateForceQualityMultiplier = (forceQuality) => {
  if (!forceQuality) return 1.0;
  
  const training = TRAINING_LEVELS[forceQuality.training_level]?.casualty_multiplier || 1.0;
  const equipment = EQUIPMENT_LEVELS[forceQuality.equipment_level]?.casualty_multiplier || 1.0;
  const morale = MORALE_LEVELS[forceQuality.morale_level]?.casualty_multiplier || 1.0;
  const experience = EXPERIENCE_LEVELS[forceQuality.experience_level]?.casualty_multiplier || 1.0;
  const tech = TECHNOLOGY_ENVIRONMENT[forceQuality.technology_env]?.casualty_multiplier || 1.0;
  
  const combined = training * equipment * morale * experience * tech;
  return Math.max(0.2, Math.min(5.0, combined));
};

/**
 * Resolve force quality from Tab1 brigade data
 */
const resolveForceQuality = (brigade, forceBuilderResult) => {
  if (!brigade || !forceBuilderResult) {
    return { resolved: DEFAULT_FORCE_QUALITY, inherited_from: 'DEFAULT' };
  }
  
  if (brigade.force_quality) {
    return { resolved: brigade.force_quality, inherited_from: 'BRIGADE' };
  }
  
  if (brigade.force_quality_override) {
    const parentDivision = forceBuilderResult.divisions?.find(
      d => d.id === brigade.parent_division_id
    );
    const divisionQuality = parentDivision?.force_quality_override || 
                            forceBuilderResult.corps?.force_quality ||
                            DEFAULT_FORCE_QUALITY;
    
    return {
      resolved: { ...divisionQuality, ...brigade.force_quality_override },
      inherited_from: 'BRIGADE_OVERRIDE'
    };
  }
  
  const parentDivision = forceBuilderResult.divisions?.find(
    d => d.id === brigade.parent_division_id
  );
  
  if (parentDivision?.force_quality_override) {
    const corpsQuality = forceBuilderResult.corps?.force_quality || DEFAULT_FORCE_QUALITY;
    return {
      resolved: { ...corpsQuality, ...parentDivision.force_quality_override },
      inherited_from: 'DIVISION'
    };
  }
  
  if (parentDivision?.force_quality) {
    return { resolved: parentDivision.force_quality, inherited_from: 'DIVISION' };
  }
  
  if (forceBuilderResult.corps?.force_quality) {
    return { resolved: forceBuilderResult.corps.force_quality, inherited_from: 'CORPS' };
  }
  
  return { resolved: DEFAULT_FORCE_QUALITY, inherited_from: 'DEFAULT' };
};

/**
 * Get force quality label for display
 */
const getForceQualityLabel = (type, value) => {
  const lookup = {
    training_level: TRAINING_LEVELS,
    equipment_level: EQUIPMENT_LEVELS,
    morale_level: MORALE_LEVELS,
    experience_level: EXPERIENCE_LEVELS,
    technology_env: TECHNOLOGY_ENVIRONMENT,
    medical_capability: MEDICAL_CAPABILITY
  };
  return lookup[type]?.[value]?.label || value;
};

/**
 * v7.6.0: Division-aware frontage position calculator.
 *
 * Groups combat-eligible brigades by parent_division_id, assigns each division
 * a contiguous sector proportional to its brigade count, then distributes
 * sub-sectors within each division sector. Adjacency (adjacent_left/right) is
 * null at division boundaries — brigades from different divisions are NEVER
 * marked as adjacent. This prevents the Division 1 / Division 2 mixing shown
 * on the Frontage Allocation Map.
 *
 * @param {Array}  brigadeList        - Flat array of brigade objects (from force.brigades)
 * @param {number} totalFrontageWidth - Total frontage width in km
 * @returns {Object} Map: brigadeId -> { start, end, indexInDiv, divCount, leftNeighborId, rightNeighborId }
 */
// 2026-10-14 (Dr. Tatar): the pre-operation assembly happens BEFORE D-day, so it is not limited by the operation duration
// (the old rule, BUG-T11, was a mistake: the assembly days are not operation days). The calculator ceiling is 90 days.
// true restores the former limit (assembly <= operation days).
const ASSEMBLY_LIMITED_BY_OPERATION = false;
const ASSEMBLY_MAX_DAYS = 90;

const computeDivisionFrontageMap = (brigadeList, totalFrontageWidth) => {
  // Group combat-eligible brigades by parent_division_id
  const divisionOrder = [];
  const divisionGroups = {};

  for (const brigade of brigadeList) {
    if (!unitTypeHasFrontage(brigade.unit_type || brigade.brigade_type || 'MECHANIZED')) continue;
    const divId = brigade.parent_division_id || 'UNKNOWN';
    if (!divisionGroups[divId]) {
      divisionGroups[divId] = [];
      divisionOrder.push(divId);
    }
    divisionGroups[divId].push(brigade);
  }

  const totalCombatBrigades = Object.values(divisionGroups)
    .reduce((sum, arr) => sum + arr.length, 0);

  if (totalCombatBrigades === 0) return {};

  // Sort divisions by division_number for stable left-to-right ordering
  divisionOrder.sort((a, b) => {
    const aNum = divisionGroups[a][0]?.parent_division_number
              ?? divisionGroups[a][0]?.division_number ?? 99;
    const bNum = divisionGroups[b][0]?.parent_division_number
              ?? divisionGroups[b][0]?.division_number ?? 99;
    return aNum - bNum;
  });

  const widthPerBrigade = totalFrontageWidth / totalCombatBrigades;
  const positionMap = {};
  let offset = 0;

  for (const divId of divisionOrder) {
    const divBrigades = divisionGroups[divId];

    // Preserve existing relative left-to-right order within division
    const sorted = [...divBrigades].sort(
      (a, b) => (a.position?.frontage_start_km ?? 999)
              - (b.position?.frontage_start_km ?? 999)
    );

    // v7.6.1 no-loop-func fix: snapshot mutable `offset` into a const so the
    // forEach callback closes over a stable value, not the outer loop variable.
    const divOffset = offset;

    sorted.forEach((brigade, idx) => {
      const id = brigade.id || brigade.brigade_id;
      positionMap[id] = {
        start: Math.round((divOffset + idx * widthPerBrigade) * 10) / 10,
        end:   Math.round((divOffset + (idx + 1) * widthPerBrigade) * 10) / 10,
        indexInDiv: idx,
        divCount: sorted.length,
        // Adjacent only within same division — null at division boundaries
        leftNeighborId:  idx > 0 ? (sorted[idx - 1].id || sorted[idx - 1].brigade_id) : null,
        rightNeighborId: idx < sorted.length - 1 ? (sorted[idx + 1].id || sorted[idx + 1].brigade_id) : null,
        divisionId: divId
      };
    });

    offset += divBrigades.length * widthPerBrigade;
  }

  return positionMap;
};

/**
 * TabPanel component for brigade configuration dialog
 */
function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index} style={{ paddingTop: 16 }}>
      {value === index && children}
    </div>
  );
}

// ============================================================================
// OPERATIONAL STATE DISPLAY COMPONENT (Task 2.1 - NEW)
// ============================================================================

const OperationalStateBadge = ({ state, compact = true }) => {
  const stateConfig = OPERATIONAL_STATES[state];
  if (!stateConfig) return null;
  
  if (compact) {
    return (
      <Tooltip
        title={
          <Box sx={{ p: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>{stateConfig.label}</Typography>
            <Typography variant="caption" display="block">{stateConfig.description}</Typography>
            <Divider sx={{ my: 0.5 }} />
            <Typography variant="caption" display="block">
              Battle Casualties: {stateConfig.battle_casualty_multiplier}x base rate
            </Typography>
            <Typography variant="caption" display="block">
              DNBI: {stateConfig.dnbi?.combined_rate} per 1,000/day
            </Typography>
          </Box>
        }
      >
        <Chip
          icon={<span>{stateConfig.icon}</span>}
          label={stateConfig.shortLabel}
          size="small"
          color={stateConfig.color}
          variant="outlined"
          sx={{ cursor: 'help' }}
        />
      </Tooltip>
    );
  }
  
  return (
    <Paper variant="outlined" sx={{ p: 1.5, bgcolor: `${stateConfig.color}.50` }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="h6">{stateConfig.icon}</Typography>
        <Typography variant="subtitle2">{stateConfig.label}</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {stateConfig.description}
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
        <Chip
          label={`Battle: ${stateConfig.battle_casualty_multiplier}x`}
          size="small"
          color={stateConfig.battle_casualty_multiplier === 1.0 ? 'error' : 
                 stateConfig.battle_casualty_multiplier < 0.5 ? 'success' : 'warning'}
        />
        <Chip
          label={`DNBI: ${stateConfig.dnbi?.combined_rate}/1k/day`}
          size="small"
          variant="outlined"
        />
      </Box>
    </Paper>
  );
};

// ============================================================================
// OPERATIONAL STATE SELECTOR COMPONENT (Task 2.1 - NEW)
// ============================================================================

const OperationalStateSelector = ({
  brigadeConfig,
  onStateChange,
  onPositionChange,
  mode,
  expanded = false
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(expanded);
  
  const currentState = brigadeConfig.operational_state || 'COMBAT_ENGAGED';
  const currentPosition = brigadeConfig.position?.position_feba_km || 5;
  const unitType = brigadeConfig.unit_type || 'MECHANIZED';
  const doctrine = getUnitTypeDoctrine(unitType);
  const isExpertMode = mode === 'EXPERT' || mode === 'DETAILED';
  const hasFrontage = unitTypeHasFrontage(unitType);
  
  // Check if state was manually overridden
  const autoAssignedState = getOperationalStateFromPosition(currentPosition, unitType);
  const isOverridden = brigadeConfig.operational_state_override === true;
  
  // Get position warnings
  const positionWarnings = validatePositionAgainstDoctrine(currentPosition, unitType);
  
  const handleStateChange = (newState) => {
    onStateChange(brigadeConfig.brigade_id, newState, true); // true = manual override
  };
  
  const handlePositionChange = (newPosition) => {
    onPositionChange(brigadeConfig.brigade_id, newPosition);
  };
  
  const handleResetToAuto = () => {
    onStateChange(brigadeConfig.brigade_id, autoAssignedState, false); // false = auto-assigned
  };
  
  return (
    <Card sx={{ mb: 2, border: '1px solid', borderColor: `${OPERATIONAL_STATES[currentState]?.color || 'grey'}.main` }}>
      <CardContent sx={{ pb: 1 }}>
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            cursor: 'pointer'
          }}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <GpsFixedIcon color="action" />
            <Typography variant="subtitle2">{t('tab2.operational_state_pos')}</Typography>
            <OperationalStateBadge state={currentState} compact={true} />
            {isOverridden && (
              <Chip label="Override" size="small" color="info" variant="outlined" />
            )}
          </Box>
          <IconButton size="small">
            <ExpandMoreIcon sx={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }} />
          </IconButton>
        </Box>
        
        <Collapse in={isExpanded}>
          <Box sx={{ mt: 2 }}>
            {/* Position Display/Editor */}
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                    Position from FEBA
                  </Typography>
                  {isExpertMode || !hasFrontage ? (
                    <TextField
                      type="number"
                      size="small"
                      fullWidth
                      value={currentPosition}
                      onChange={(e) => handlePositionChange(Number(e.target.value))}
                      InputProps={{
                        endAdornment: <Typography variant="caption">km</Typography>
                      }}
                      helperText={`Doctrine range: ${doctrine.position_range.min}-${doctrine.position_range.max} km`}
                    />
                  ) : (
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        {hasFrontage ? 'ON FEBA' : `${currentPosition} km`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        (Edit in Expert Mode)
                      </Typography>
                    </Box>
                  )}
                </Box>
                
                {/* Position Warnings */}
                {positionWarnings.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    {positionWarnings.map((warning, idx) => (
                      <Alert 
                        key={idx} 
                        severity={warning.severity} 
                        sx={{ mb: 1, py: 0 }}
                        icon={<WarningIcon fontSize="small" />}
                      >
                        <Typography variant="caption">{warning.message}</Typography>
                      </Alert>
                    ))}
                  </Box>
                )}
              </Grid>
              
              <Grid size={{ xs: 12, md: 6 }}>
                {/* Operational State Selector */}
                <FormControl fullWidth size="small">
                  <InputLabel>{t('tab2.operational_state_label')}</InputLabel>
                  <Select
                    value={currentState}
                    onChange={(e) => handleStateChange(e.target.value)}
                    label={t('tab2.operational_state_label')}
                    disabled={!isExpertMode && hasFrontage}
                  >
                    {getOperationalStateOptions().map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <span>{option.icon}</span>
                          <Box>
                            <Typography variant="body2">{option.label}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Battle: {option.multiplier}x | DNBI: {option.dnbi_rate?.toFixed(1) || 'N/A'}/1k/day
                            </Typography>
                          </Box>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                {/* Auto-assignment info */}
                <Box sx={{ mt: 1 }}>
                  {isOverridden && autoAssignedState !== currentState && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Auto-assigned: {OPERATIONAL_STATES[autoAssignedState]?.label}
                      </Typography>
                      <Button size="small" onClick={handleResetToAuto}>
                        {t('tab2.reset_to_auto')}
                      </Button>
                    </Box>
                  )}
                  {!isExpertMode && hasFrontage && (
                    <Typography variant="caption" color="text.secondary">
                      Combat units on FEBA are always COMBAT ENGAGED. Use Expert Mode to override.
                    </Typography>
                  )}
                </Box>
              </Grid>
            </Grid>
            
            {/* Impact Display */}
            <Box sx={{ mt: 2, p: 1.5, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 'bold' }} gutterBottom display="block">
                Impact on Calculations
              </Typography>
              <Grid container spacing={1}>
                <Grid size={{ xs: 6 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <SpeedIcon fontSize="small" color="error" />
                    <Typography variant="caption">
                      Battle Casualty Modifier: <strong>{getBattleCasualtyMultiplier(currentState)}x</strong>
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <LocalHospitalIcon fontSize="small" color="warning" />
                    <Typography variant="caption">
                      DNBI Rate: <strong>{getDNBIRate(currentState).toFixed(1)}</strong> per 1,000/day
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

// ============================================================================
// FORCE QUALITY DISPLAY COMPONENTS
// ============================================================================

/**
 * ForceQualityBadge - Compact display for brigade cards (READ-ONLY)
 */
const ForceQualityBadge = ({ forceQuality, inheritedFrom, compact = true }) => {
  const { t } = useTranslation();
  if (!forceQuality) return null;
  
  const multiplier = calculateForceQualityMultiplier(forceQuality);
  const color = multiplier < 1 ? 'success' : multiplier > 1.3 ? 'warning' : 'default';
  
  if (compact) {
    return (
      <Tooltip
        title={
          <Box sx={{ p: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>Force Quality (from Tab1)</Typography>
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" display="block">
                Training: {getForceQualityLabel('training_level', forceQuality.training_level)}
              </Typography>
              <Typography variant="caption" display="block">
                Equipment: {getForceQualityLabel('equipment_level', forceQuality.equipment_level)}
              </Typography>
              <Typography variant="caption" display="block">
                Morale: {getForceQualityLabel('morale_level', forceQuality.morale_level)}
              </Typography>
              <Typography variant="caption" display="block">
                Experience: {getForceQualityLabel('experience_level', forceQuality.experience_level)}
              </Typography>
            </Box>
            <Divider sx={{ my: 1 }} />
            <Typography variant="caption" color="text.secondary">
              Inherited from: {inheritedFrom}
            </Typography>
          </Box>
        }
      >
        <Chip
          icon={<ShieldIcon sx={{ fontSize: 14 }} />}
          label={`FQ: ${multiplier.toFixed(2)}x`}
          size="small"
          color={color}
          variant="outlined"
          sx={{ cursor: 'help' }}
        />
      </Tooltip>
    );
  }
  
  return (
    <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.50' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <ShieldIcon color="action" sx={{ fontSize: 18 }} />
        <Typography variant="subtitle2">{t('tab2.force_quality')}</Typography>
        <Chip 
          label={`${multiplier.toFixed(2)}x`} 
          size="small" 
          color={color}
        />
      </Box>
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
        <Chip
          label={` ${forceQuality.training_level}`}
          size="small"
          variant="outlined"
        />
        <Chip
          label={` ${forceQuality.equipment_level}`}
          size="small"
          variant="outlined"
        />
        <Chip
          label={` ${forceQuality.morale_level}`}
          size="small"
          variant="outlined"
        />
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        <CopyIcon sx={{ fontSize: 12, mr: 0.5 }} />
        Inherited from {inheritedFrom} - Edit in Tab 1
      </Typography>
    </Paper>
  );
};
  
// ============================================================================
// THREAT ASSESSMENT PANEL (Task 2.3)
// ============================================================================

/**
 * DroneScheduleEditor - planner inputs for the drone threat (Dr. Tatar, 2026-10-10): the DOW multiplier of the
 * chosen level (empty = the level's own value) and changes by day.
 */
const DroneScheduleEditor = ({ threatConfig, updateField }) => {
  const { t } = useTranslation();
  const schedule = Array.isArray(threatConfig.drone_schedule) ? threatConfig.drone_schedule : [];
  const levelDefault = DRONE_THREAT[threatConfig.drone_threat]?.dow_modifier;
  const outOfRange = (m) => m !== null && m !== undefined && m !== '' &&
    (Number(m) < DRONE_DOW_MODIFIER_RANGE.min || Number(m) > DRONE_DOW_MODIFIER_RANGE.max);
  const setRow = (i, patch) => updateField('drone_schedule', schedule.map((r, k) => (k === i ? { ...r, ...patch } : r)));
  const addRow = () => updateField('drone_schedule', [
    ...schedule,
    { from_day: schedule.length ? Math.max(...schedule.map((r) => r.from_day || 1)) + 1 : 2, drone_threat: threatConfig.drone_threat }
  ]);
  const removeRow = (i) => updateField('drone_schedule', schedule.filter((_, k) => k !== i));
  const parseMod = (v) => (v === '' ? null : Number(v));

  return (
    <Box sx={{ mt: 1.5 }}>
      <TextField
        size="small"
        type="number"
        label={t('tab2.drone_dow_label')}
        value={threatConfig.drone_dow_modifier ?? ''}
        placeholder={String(levelDefault)}
        onChange={(e) => updateField('drone_dow_modifier', parseMod(e.target.value))}
        error={outOfRange(threatConfig.drone_dow_modifier)}
        helperText={t('tab2.drone_dow_help', { value: levelDefault, min: DRONE_DOW_MODIFIER_RANGE.min, max: DRONE_DOW_MODIFIER_RANGE.max })}
        inputProps={{ min: DRONE_DOW_MODIFIER_RANGE.min, max: DRONE_DOW_MODIFIER_RANGE.max, step: 0.05 }}
        sx={{ width: 260 }}
      />
      <Typography variant="subtitle2" sx={{ mt: 2 }}>{t('tab2.drone_schedule_title')}</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>{t('tab2.drone_schedule_help')}</Typography>
      {schedule.map((row, i) => (
        <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            type="number"
            label={t('tab2.drone_schedule_day')}
            value={row.from_day ?? ''}
            onChange={(e) => setRow(i, { from_day: e.target.value === '' ? '' : Math.max(1, Math.round(Number(e.target.value))) })}
            inputProps={{ min: 1, step: 1 }}
            sx={{ width: 110 }}
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>{t('tab2.drone_schedule_level')}</InputLabel>
            <Select
              value={row.drone_threat ?? threatConfig.drone_threat}
              label={t('tab2.drone_schedule_level')}
              onChange={(e) => setRow(i, { drone_threat: e.target.value, dow_modifier: null })}
            >
              {Object.entries(DRONE_THREAT).map(([key, val]) => (
                <MenuItem key={key} value={key}>{val.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            type="number"
            label={t('tab2.drone_schedule_multiplier')}
            value={row.dow_modifier ?? ''}
            placeholder={String(DRONE_THREAT[row.drone_threat ?? threatConfig.drone_threat]?.dow_modifier)}
            onChange={(e) => setRow(i, { dow_modifier: parseMod(e.target.value) })}
            error={outOfRange(row.dow_modifier)}
            inputProps={{ min: DRONE_DOW_MODIFIER_RANGE.min, max: DRONE_DOW_MODIFIER_RANGE.max, step: 0.05 }}
            sx={{ width: 150 }}
          />
          <Button size="small" onClick={() => removeRow(i)} sx={{ mt: 0.5 }}>{t('tab2.drone_schedule_remove')}</Button>
        </Box>
      ))}
      <Button size="small" variant="outlined" onClick={addRow}>{t('tab2.drone_schedule_add')}</Button>
    </Box>
  );
};

/**
 * ThreatAssessmentPanel - Configure threat environment with impact preview
 */
const ThreatAssessmentPanel = ({
  threatConfig,
  onThreatConfigChange,
  onApplyPreset,
  expanded = false
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(expanded);

  // Calculate combined modifiers
  const modifiers = getThreatModifiers(threatConfig);
  const validation = validateThreatConfiguration(threatConfig);
  
  const updateField = (field, value) => {
    onThreatConfigChange({
      ...threatConfig,
      [field]: value
    });
  };
  
  // Severity color helper
  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'error': return 'error.main';
      case 'warning': return 'warning.main';
      case 'info': return 'info.main';
      default: return 'text.secondary';
    }
  };
  
  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            cursor: 'pointer'
          }}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <RadarIcon color="action" />
            <Typography variant="h6">{t('tab2.threat_assessment')}</Typography>
            <Chip 
              label={THREAT_TYPES[threatConfig.threat_type]?.shortLabel || 'CONV'}
              size="small"
              color={THREAT_TYPES[threatConfig.threat_type]?.color || 'default'}
            />
            {(() => {
              const applied = appliedThreatSummary(threatConfig);
              const chip = (key, label, value, hot) => (
                <Chip key={key} label={`${label} x${value.toFixed(2)}`} size="small" variant="outlined" color={hot ? 'warning' : 'default'} />
              );
              return (
                <>
                  {applied.front !== 1 && chip('front', t('tab2.chip_front'), applied.front, applied.front > 1.2)}
                  {applied.rear !== 1 && chip('rear', t('tab2.chip_rear'), applied.rear, applied.rear > 1.4)}
                  {applied.dow !== 1 && chip('dow', t('tab2.chip_dow'), applied.dow, applied.dow > 1.2)}
                  {applied.dnbi !== 1 && chip('dnbi', t('tab2.chip_dnbi'), applied.dnbi, applied.dnbi > 1.3)}
                  {applied.mascal_probability > 0 && (
                    <Chip label={`${t('tab2.chip_mascal')} ${Math.round(applied.mascal_probability * 100)}%`} size="small" variant="outlined" />
                  )}
                  {applied.no_golden_hour && (
                    <Chip label={t('tab2.chip_no_golden_hour')} size="small" color="error" variant="outlined" />
                  )}
                </>
              );
            })()}
          </Box>
          <IconButton size="small">
            <ExpandMoreIcon sx={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }} />
          </IconButton>
        </Box>
        
        <Collapse in={isExpanded}>
          <Box sx={{ mt: 2 }}>
            <HelpText id="threat_card" />
            <HelpText id="preset_threat_default" />
            <HelpText id="preset_threat_ukraine" />
            {/* Preset Buttons */}
            <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button 
                size="small" 
                variant="outlined"
                onClick={() => onApplyPreset(DEFAULT_THREAT_CONFIG)}
              >
                Default (Conventional)
              </Button>
              <Button 
                size="small" 
                variant="outlined"
                color="error"
                onClick={() => onApplyPreset(UKRAINE_2024_BASELINE)}
              >
                Ukraine 2024 Baseline
              </Button>
            </Box>
            
            <Grid container spacing={2}>
              {/* Threat Type */}
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t('tab2.threat_type_label')}</InputLabel>
                  <Select
                    value={threatConfig.threat_type}
                    onChange={(e) => updateField('threat_type', e.target.value)}
                    label={t('tab2.threat_type_label')}
                  >
                    {Object.entries(THREAT_TYPES).map(([key, val]) => (
                      <MenuItem key={key} value={key}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <span>{val.icon}</span>
                          <span>{val.label}</span>
                          <Chip label={`${val.casualty_modifier}x`} size="small" sx={{ ml: 'auto' }} />
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <HelpText id="threat_type" />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  {THREAT_TYPES[threatConfig.threat_type]?.description}
                </Typography>
              </Grid>
              
              {/* Enemy AD Level */}
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t('tab2.enemy_air_defense_label')}</InputLabel>
                  <Select
                    value={threatConfig.enemy_ad}
                    onChange={(e) => updateField('enemy_ad', e.target.value)}
                    label={t('tab2.enemy_air_defense_label')}
                  >
                    {Object.entries(ENEMY_AD_LEVELS).map(([key, val]) => (
                      <MenuItem key={key} value={key}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <span>{val.icon}</span>
                          <span>{val.label}</span>
                          <Chip 
                            label={`${Math.round(val.medevac_availability * 100)}% MEDEVAC`} 
                            size="small" 
                            color={val.medevac_availability < 0.5 ? 'error' : val.medevac_availability < 0.8 ? 'warning' : 'success'}
                            sx={{ ml: 'auto' }} 
                          />
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <HelpText id="enemy_ad" />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  MEDEVAC: {ENEMY_AD_LEVELS[threatConfig.enemy_ad]?.day_night_restriction || 'No restriction'}
                </Typography>
              </Grid>
              
              {/* IED Threat */}
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t('tab2.ied_threat_label')}</InputLabel>
                  <Select
                    value={threatConfig.ied_threat}
                    onChange={(e) => updateField('ied_threat', e.target.value)}
                    label={t('tab2.ied_threat_label')}
                  >

                    {Object.entries(IED_THREAT_LEVELS).map(([key, val]) => (
                      <MenuItem key={key} value={key}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <span>{val.icon}</span>
                          <span>{val.label}</span>
                          <Chip 
                            label={`${Math.round(val.ground_evac_speed_modifier * 100)}% speed`} 
                            size="small" 
                            color={val.ground_evac_speed_modifier < 0.5 ? 'error' : val.ground_evac_speed_modifier < 0.8 ? 'warning' : 'default'}
                            sx={{ ml: 'auto' }} 
                          />
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <HelpText id="ied" />
                {IED_THREAT_LEVELS[threatConfig.ied_threat]?.evacuation_delay_minutes > 0 && (
                  <Typography variant="caption" color="warning.main" sx={{ mt: 0.5, display: 'block' }}>
                    +{IED_THREAT_LEVELS[threatConfig.ied_threat]?.evacuation_delay_minutes} min evacuation delay
                  </Typography>

                )}
              </Grid>
              
              {/* Artillery Threat */}
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t('tab2.artillery_threat_label')}</InputLabel>
                  <Select
                    value={threatConfig.artillery_threat}
                    onChange={(e) => updateField('artillery_threat', e.target.value)}
                    label={t('tab2.artillery_threat_label')}
                  >
                    {Object.entries(ARTILLERY_THREAT).map(([key, val]) => (
                      <MenuItem key={key} value={key}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <span>{val.icon}</span>
                          <span>{val.label}</span>
                          <Chip 
                            label={`${val.casualty_modifier}x casualties`} 
                            size="small" 
                            color={val.casualty_modifier > 1.5 ? 'error' : val.casualty_modifier > 1.2 ? 'warning' : 'default'}
                            sx={{ ml: 'auto' }} 
                          />
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <HelpText id="artillery" />
                {ARTILLERY_THREAT[threatConfig.artillery_threat]?.mass_casualty_probability > 0 && (
                  <Typography variant="caption" color="warning.main" sx={{ mt: 0.5, display: 'block' }}>
                    {Math.round(ARTILLERY_THREAT[threatConfig.artillery_threat]?.mass_casualty_probability * 100)}% daily MASCAL probability
                  </Typography>
                )}
              </Grid>
              
              {/* Drone Threat */}
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t('tab2.drone_threat_label')}</InputLabel>
                  <Select
                    value={threatConfig.drone_threat}
                    onChange={(e) => updateField('drone_threat', e.target.value)}
                    label={t('tab2.drone_threat_label')}
                  >
                    {Object.entries(DRONE_THREAT).map(([key, val]) => (
                      <MenuItem key={key} value={key}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <span>{val.icon}</span>
                          <span>{val.label}</span>
                          <Chip 
                            label={`${val.casualty_modifier}x`} 
                            size="small" 
                            color={val.casualty_modifier > 1.5 ? 'error' : val.casualty_modifier > 1.2 ? 'warning' : 'default'}
                            sx={{ ml: 'auto' }} 
                          />
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <HelpText id="drone" />
                {DRONE_THREAT[threatConfig.drone_threat]?.fpv_casualty_rate > 0 && (
                  <Typography variant="caption" color="warning.main" sx={{ mt: 0.5, display: 'block' }}>
                    Enemy drone capacity influence on FPV casualties: {DRONE_THREAT[threatConfig.drone_threat]?.fpv_casualty_rate}/1k/day
                  </Typography>
                )}
                <DroneScheduleEditor threatConfig={threatConfig} updateField={updateField} />
              </Grid>
              
              {/* Insurgent Activity */}
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t('tab2.insurgent_activity_label')}</InputLabel>
                  <Select
                    value={threatConfig.insurgent_activity}
                    onChange={(e) => updateField('insurgent_activity', e.target.value)}
                    label={t('tab2.insurgent_activity_label')}
                  >
                    {Object.entries(INSURGENT_ACTIVITY).map(([key, val]) => (
                      <MenuItem key={key} value={key}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <span>{val.icon}</span>
                          <span>{val.label}</span>
                          <Chip 
                            label={`LOC ${Math.round(val.loc_security_modifier * 100)}%`} 
                            size="small" 
                            color={val.loc_security_modifier < 0.7 ? 'error' : val.loc_security_modifier < 0.9 ? 'warning' : 'default'}
                            sx={{ ml: 'auto' }} 
                          />
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <HelpText id="insurgent" />
              </Grid>
              
              {/* CBRN Risk */}
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t('tab2.cbrn_risk_label')}</InputLabel>
                  <Select
                    value={threatConfig.cbrn_risk}
                    onChange={(e) => updateField('cbrn_risk', e.target.value)}
                    label={t('tab2.cbrn_risk_label')}
                  >
                    {Object.entries(CBRN_RISK).map(([key, val]) => (
                      <MenuItem key={key} value={key}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <span>{val.icon}</span>
                          <span>{val.label}</span>
                          {val.medical_facility_impact?.capacity_reduction > 0 && (
                            <Chip 
                              label={`-${Math.round(val.medical_facility_impact.capacity_reduction * 100)}% capacity`} 
                              size="small" 
                              color="warning"
                              sx={{ ml: 'auto' }} 
                            />
                          )}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <HelpText id="cbrn" />
                {CBRN_RISK[threatConfig.cbrn_risk]?.decontamination_required && (
                  <Typography variant="caption" color="warning.main" sx={{ mt: 0.5, display: 'block' }}>
                    Decontamination stations required at MTFs
                  </Typography>
                )}
              </Grid>
            </Grid>
            
            {/* Impact Summary */}
            <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle2" gutterBottom>
                {t('tab2.combined_impact')}
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 6, md: 3 }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h5" color={modifiers.casualty_modifier > 1.5 ? 'error.main' : modifiers.casualty_modifier > 1.2 ? 'warning.main' : 'success.main'}>
                      {modifiers.casualty_modifier.toFixed(2)}x
                    </Typography>
                    <Typography variant="caption" color="text.secondary">{t('tab2.cas_modifier')}</Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 6, md: 3 }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h5" color={modifiers.dow_modifier > 1.3 ? 'error.main' : modifiers.dow_modifier > 1.1 ? 'warning.main' : 'text.primary'}>
                      {modifiers.dow_modifier.toFixed(2)}x
                    </Typography>
                    <Typography variant="caption" color="text.secondary">{t('tab2.dow_modifier')}</Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 6, md: 3 }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h5" color={modifiers.medevac_availability < 0.5 ? 'error.main' : modifiers.medevac_availability < 0.8 ? 'warning.main' : 'success.main'}>
                      {Math.round(modifiers.medevac_availability * 100)}%
                    </Typography>
                    <Typography variant="caption" color="text.secondary">{t('tab2.medevac_available')}</Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 6, md: 3 }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h5" color={modifiers.golden_hour_achievable ? 'success.main' : 'error.main'}>
                      {modifiers.golden_hour_achievable ? '[OK]' : ''}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">{t('tab2.golden_hour')}</Typography>
                  </Box>
                </Grid>
              </Grid>

              {modifiers.evacuation_delay_minutes > 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    {t('tab2.evac_delay', { minutes: modifiers.evacuation_delay_minutes })}
                  </Typography>
                </Alert>
              )}
            </Paper>
            
            {/* Requirements */}
            {Object.values(modifiers.requirements).some(v => v) && (
              <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {t('tab2.required_capabilities')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {modifiers.requirements.armored_ambulances && (
                    <Chip label={t('tab2.armored_ambulances')} size="small" color="warning" />
                  )}
                  {modifiers.requirements.forward_surgical && (
                    <Chip label={t('tab2.forward_surgical')} size="small" color="error" />
                  )}
                  {modifiers.requirements.route_clearance && (
                    <Chip label={t('tab2.route_clearance')} size="small" color="warning" />
                  )}
                  {modifiers.requirements.convoy_escort && (
                    <Chip label={t('tab2.convoy_escort')} size="small" color="warning" />
                  )}
                  {modifiers.requirements.decontamination && (
                    <Chip label={t('tab2.decontamination')} size="small" color="warning" />
                  )}
                  {modifiers.requirements.counter_uas && (
                    <Chip label={t('tab2.counter_uas')} size="small" color="warning" />
                  )}
                  {modifiers.requirements.mtf_mobility && (
                    <Chip label={t('tab2.mtf_mobility')} size="small" color="warning" />
                  )}
                </Box>
              </Paper>
            )}
            
            {/* Warnings */}
            {modifiers.warnings.length > 0 && (
              <Box sx={{ mt: 2 }}>
                {modifiers.warnings.map((warning, idx) => (
                  <Alert 
                    key={idx} 
                    severity={warning.severity} 
                    sx={{ mb: 1 }}
                    icon={<WarningIcon />}
                  >
                    <AlertTitle sx={{ color: getSeverityColor(warning.severity) }}>
                      {warning.category}
                    </AlertTitle>
                    <Typography variant="body2">{warning.message}</Typography>
                    {warning.recommendations && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                          {t('tab2.recommendations')}
                        </Typography>
                        <ul style={{ margin: '4px 0 0 0', paddingLeft: 20 }}>
                          {warning.recommendations.map((rec, recIdx) => (
                            <li key={recIdx}>
                              <Typography variant="caption">{rec}</Typography>
                            </li>
                          ))}
                        </ul>
                      </Box>
                    )}
                  </Alert>
                ))}
              </Box>
            )}
            
            {/* Validation Warnings */}
            {validation.warnings.length > 0 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <AlertTitle>{t('tab2.configuration_notes')}</AlertTitle>
                {validation.warnings.map((w, idx) => (
                  <Typography key={idx} variant="body2">{w}</Typography>
                ))}
              </Alert>
            )}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

// ============================================================================
// LOC CONFIGURATION PANEL (Task 2.8)
// Lines of Communication - Road status, security, bridges
// ============================================================================

/**
 * LOCConfigurationPanel - Configure Lines of Communication for evacuation planning
 */
const LOCConfigurationPanel = ({
  locConfig,
  onLocConfigChange,
  onApplyPreset,
  threatConfig = {},
  operationWeather = 'CLEAR',
  expanded = false
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(expanded);

  // Calculate combined modifiers
  const modifiers = getLOCModifiers(locConfig);
  const validation = validateLOCConfiguration(locConfig);
  // 2026-10-13 (Tab 2 plan 7.2): the road state the calculation uses (legs from Role 2 upward)
  const roadState = resolveRoadState({ locConfig, threatAssessment: threatConfig, weather: operationWeather });
  
  const updateField = (field, value) => {
    onLocConfigChange({
      ...locConfig,
      [field]: value
    });
  };
  
  // Calculate sample travel times for display
  const getSampleTravelTime = (distance_km) => {
    // BUG-115 FIX: Guard against modifiers being undefined/incomplete on first render.
    // If speed_modifier is not a positive finite number, return a safe placeholder
    // rather than propagating NaN into the chip labels.
    if (!modifiers || !isFinite(modifiers.speed_modifier) || modifiers.speed_modifier <= 0) {
      return Math.round((distance_km / 60) * 60); // baseline 60 km/h, no modifiers
    }
    const baseSpeed = 60; // km/h baseline
    // BUG-094 FIX: Guard speed_modifier and coordination_delay against undefined/NaN.
    // getLOCModifiers may return undefined for speed_modifier if locConfig was loaded
    // from a saved scenario that was missing keys (pre-fix). Fallback: 1.0 (no modifier).
    // BUG-115 FIX: getLOCModifiers returns coordination_delay_minutes (not coordination_delay).
    // coordDelay was always falling back to 0, making sample times slightly wrong
    // (missing escort/clearance delays). Key corrected to coordination_delay_minutes.
    const speedMod = (modifiers.speed_modifier > 0 && isFinite(modifiers.speed_modifier))
      ? modifiers.speed_modifier
      : 1;
    const coordDelay = isFinite(modifiers.coordination_delay_minutes) ? modifiers.coordination_delay_minutes : 0;
    const adjustedSpeed = baseSpeed * speedMod;
    const baseTime = (distance_km / adjustedSpeed) * 60; // minutes
    const totalTime = baseTime + coordDelay;
    return Math.round(totalTime);
  };
  
  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            cursor: 'pointer'
          }}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SpeedIcon color="action" />
            <Typography variant="h6">{t('tab2.loc_config')}</Typography>
            <Chip 
              label={`MSR: ${ROAD_STATUS[locConfig.msr_status]?.shortLabel || 'PAVED'}`}
              size="small"
              color={ROAD_STATUS[locConfig.msr_status]?.color || 'success'}
            />
            <Chip 
              label={`${Math.round(modifiers.speed_modifier * 100)}% speed`}
              size="small"
              color={modifiers.speed_modifier < 0.5 ? 'error' : modifiers.speed_modifier < 0.8 ? 'warning' : 'success'}
              variant="outlined"
            />
            {modifiers.escort_required && (
              <Chip label=" Escort Req'd" size="small" color="warning" variant="outlined" />
            )}
          </Box>
          <IconButton size="small">
            <ExpandMoreIcon sx={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }} />
          </IconButton>
        </Box>
        
        <Collapse in={isExpanded}>
          <Box sx={{ mt: 2 }}>
            <HelpText id="loc_card" />
            <HelpText id="preset_loc_default" />
            <HelpText id="preset_loc_high" />
            {/* Preset Buttons */}
            <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button 
                size="small" 
                variant="outlined"
                onClick={() => onApplyPreset(DEFAULT_LOC_CONFIG)}
              >
                Default (Good Roads)
              </Button>
              <Button 
                size="small" 
                variant="outlined"
                color="warning"
                onClick={() => onApplyPreset(HIGH_THREAT_LOC_CONFIG)}
              >
                High Threat Environment
              </Button>
            </Box>
            
            <Grid container spacing={2}>
              {/* MSR Status */}
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t('tab2.msr_status_label')}</InputLabel>
                  <Select
                    value={locConfig.msr_status}
                    onChange={(e) => updateField('msr_status', e.target.value)}
                    label={t('tab2.msr_status_label')}
                  >
                    {Object.entries(ROAD_STATUS).map(([key, config]) => (
                      <MenuItem key={key} value={key}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                          <span>{config.icon}</span>
                          <Typography>{config.label}</Typography>
                          <Chip 
                            label={`${Math.round(config.speed_modifier * 100)}%`} 
                            size="small" 
                            color={config.color}
                            sx={{ ml: 'auto' }}
                          />
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <HelpText id="msr" />
                <Typography variant="caption" color="text.secondary">
                  Ambulance: {ROAD_STATUS[locConfig.msr_status]?.speeds?.ambulance || 70} km/h
                </Typography>
              </Grid>
              
              {/* ASR Status */}
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t('tab2.asr_status_label')}</InputLabel>
                  <Select
                    value={locConfig.asr_status}
                    onChange={(e) => updateField('asr_status', e.target.value)}
                    label={t('tab2.asr_status_label')}
                  >
                    {Object.entries(ROAD_STATUS).map(([key, config]) => (
                      <MenuItem key={key} value={key}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                          <span>{config.icon}</span>
                          <Typography>{config.label}</Typography>
                          <Chip 
                            label={`${Math.round(config.speed_modifier * 100)}%`} 
                            size="small" 
                            color={config.color}
                            sx={{ ml: 'auto' }}
                          />
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <HelpText id="asr" />
                <Typography variant="caption" color="text.secondary">
                  {ROAD_STATUS[locConfig.asr_status]?.description}
                </Typography>
              </Grid>
              
              {/* LOC Security */}
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t('tab2.loc_security_label')}</InputLabel>
                  <Select
                    value={locConfig.loc_security}
                    onChange={(e) => updateField('loc_security', e.target.value)}
                    label={t('tab2.loc_security_label')}
                  >
                    {Object.entries(LOC_SECURITY).map(([key, config]) => (
                      <MenuItem key={key} value={key}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                          <span>{config.icon}</span>
                          <Typography>{config.label}</Typography>
                          <Chip 
                            label={config.escort_required === true ? 'Escort' : 'No Escort'} 
                            size="small" 
                            color={config.color}
                            sx={{ ml: 'auto' }}
                          />
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <HelpText id="loc_security" />
                <Typography variant="caption" color="text.secondary">
                  Movement: {LOC_SECURITY[locConfig.loc_security]?.movement_window?.replace('_', ' ') || '24 hours'}
                </Typography>
              </Grid>
              
              {/* Primary Bridge Status */}
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t('tab2.bridge_status_label')}</InputLabel>
                  <Select
                    value={locConfig.primary_bridge}
                    onChange={(e) => updateField('primary_bridge', e.target.value)}
                    label={t('tab2.bridge_status_label')}
                  >
                    {Object.entries(BRIDGE_STATUS).map(([key, config]) => (
                      <MenuItem key={key} value={key}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                          <span>{config.icon}</span>
                          <Typography>{config.label}</Typography>
                          <Chip 
                            label={config.usable !== false ? `MLC ${config.mlc_rating}` : 'BLOCKED'} 
                            size="small" 
                            color={config.color}
                            sx={{ ml: 'auto' }}
                          />
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <HelpText id="bridge" />
                {BRIDGE_STATUS[locConfig.primary_bridge]?.delay_per_vehicle_seconds > 0 && (
                  <Typography variant="caption" color="warning.main">
                    +{BRIDGE_STATUS[locConfig.primary_bridge]?.delay_per_vehicle_seconds}s delay per vehicle
                  </Typography>
                )}
              </Grid>
              
              {/* Weather (affects roads): 2026-10-13 hidden, the road weather is the operation weather (one weather) */}
              {SHOW_LOC_WEATHER_SELECTOR ? (
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t('tab2.weather_label')}</InputLabel>
                  <Select
                    value={locConfig.weather}
                    onChange={(e) => updateField('weather', e.target.value)}
                    label={t('tab2.weather_label')}
                  >
                    {Object.entries(WEATHER_ROUTE_IMPACTS).map(([key, config]) => (
                      <MenuItem key={key} value={key}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                          <Typography>{config.label}</Typography>
                          <Chip 
                            label={`${Math.round(config.speed_modifier * 100)}% speed`} 
                            size="small" 
                            color={config.speed_modifier < 0.5 ? 'error' : config.speed_modifier < 0.8 ? 'warning' : 'default'}
                            sx={{ ml: 'auto' }}
                          />
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary">
                  Visibility: {Math.round((WEATHER_ROUTE_IMPACTS[locConfig.weather]?.visibility_modifier || 1) * 100)}%
                </Typography>
              </Grid>
              ) : (
              <Grid size={{ xs: 12, md: 6 }}>
                <HelpText id="loc_weather" />
              </Grid>
              )}
            </Grid>
            
            {/* 2026-10-13: what the calculation uses */}
            <Paper variant="outlined" sx={{ p: 2, mt: 2, borderColor: 'primary.light' }}>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                {t('tab2.loc_used_title')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <Chip size="small" color={roadState.status === 'OPEN' ? 'success' : roadState.status === 'DEGRADED' ? 'warning' : 'error'} label={t(`tab2.road_status.${roadState.status}`)} />
                <Chip size="small" variant="outlined" label={`${t('tab2.road_route')}: ${roadState.route}`} />
                <Chip size="small" variant="outlined" label={`${t('tab2.road_speed')} ${Math.round(roadState.speed_factor * 100)}%`} />
                <Chip size="small" variant="outlined" label={`${t('tab2.road_availability')} ${Math.round(roadState.availability * 100)}%`} />
                <Chip size="small" variant="outlined" label={`${t('tab2.road_delay')} +${Math.round(roadState.delay_min)} min`} />
              </Box>
            </Paper>

            {/* Impact Summary */}
            <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                 {t('tab2.loc_indicative_title')}
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="caption" color="text.secondary">Speed Modifier</Typography>
                  <Typography 
                    variant="h6" 
                    color={modifiers.speed_modifier < 0.5 ? 'error.main' : modifiers.speed_modifier < 0.8 ? 'warning.main' : 'success.main'}
                  >
                    {Math.round(modifiers.speed_modifier * 100)}%
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="caption" color="text.secondary">Time Multiplier</Typography>
                  <Typography 
                    variant="h6" 
                    color={modifiers.evac_time_modifier > 2 ? 'error.main' : modifiers.evac_time_modifier > 1.5 ? 'warning.main' : 'success.main'}
                  >
                    {modifiers.evac_time_modifier.toFixed(1)}x
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="caption" color="text.secondary">Coord. Delay</Typography>
                  <Typography 
                    variant="h6" 
                    color={modifiers.coordination_delay_minutes > 30 ? 'error.main' : modifiers.coordination_delay_minutes > 0 ? 'warning.main' : 'success.main'}
                  >
                    +{modifiers.coordination_delay_minutes ?? 0} min
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="caption" color="text.secondary">Bridge</Typography>
                  <Typography 
                    variant="h6" 
                    color={modifiers.bridge_usable ? 'success.main' : 'error.main'}
                  >
                    {modifiers.bridge_usable ? `MLC ${modifiers.bridge_mlc}` : 'BLOCKED'}
                  </Typography>
                </Grid>
              </Grid>
              
              {/* Sample travel times */}
              <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                  Sample Ground Evacuation Times:
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, mt: 0.5, flexWrap: 'wrap' }}>
                  <Chip 
                    label={`15km: ~${getSampleTravelTime(15)} min`} 
                    size="small" 
                    variant="outlined"
                    color={getSampleTravelTime(15) > 60 ? 'error' : getSampleTravelTime(15) > 30 ? 'warning' : 'success'}
                  />
                  <Chip 
                    label={`30km: ~${getSampleTravelTime(30)} min`} 
                    size="small" 
                    variant="outlined"
                    color={getSampleTravelTime(30) > 90 ? 'error' : getSampleTravelTime(30) > 60 ? 'warning' : 'success'}
                  />
                  <Chip 
                    label={`60km: ~${getSampleTravelTime(60)} min`} 
                    size="small" 
                    variant="outlined"
                    color={getSampleTravelTime(60) > 120 ? 'error' : getSampleTravelTime(60) > 90 ? 'warning' : 'success'}
                  />
                </Box>
              </Box>
              
              {/* Golden Hour warning */}
              {getSampleTravelTime(15) > 60 && (
                <Alert severity="error" sx={{ mt: 2 }} icon={<WarningIcon />}>
                  <AlertTitle>{t('tab2.golden_hour_at_risk')}</AlertTitle>
                  Even short distances (15km) exceed 60 minutes ground evacuation time.
                  Consider MEDEVAC helicopter allocation or forward surgical teams.
                </Alert>
              )}
            </Paper>
            
            {/* Requirements */}
            {(modifiers.escort_required || modifiers.armored_required) && (
              <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Required Capabilities
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {modifiers.escort_required && (
                    <Chip label={t('tab2.armed_escort_label')} size="small" color="warning" />
                  )}
                  {modifiers.armored_required && (
                    <Chip label={t('tab2.armored_vehicles_label')} size="small" color="warning" />
                  )}
                  {!modifiers.bridge_usable && (
                    <Chip label=" Engineering Support" size="small" color="error" />
                  )}
                </Box>
              </Paper>
            )}
            
            {/* Risk Assessment */}
            {(modifiers.ambush_risk > 0.1 || modifiers.ied_risk > 0.1) && (
              <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: 'error.light' }}>
                <Typography variant="subtitle2" gutterBottom sx={{ color: 'error.contrastText' }}>
                  [!] Route Risk Assessment
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Typography variant="body2" sx={{ color: 'error.contrastText' }}>
                    Ambush Risk: {Math.round(modifiers.ambush_risk * 100)}% per convoy
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'error.contrastText' }}>
                    IED Risk: {Math.round(modifiers.ied_risk * 100)}% per convoy
                  </Typography>
                </Box>
              </Paper>
            )}
            
            {/* Warnings */}
            {modifiers.warnings.length > 0 && (
              <Box sx={{ mt: 2 }}>
                {modifiers.warnings.map((warning, idx) => (
                  <Alert 
                    key={idx} 
                    severity={warning.severity} 
                    sx={{ mb: 1 }}
                    icon={<WarningIcon />}
                  >
                    <Typography variant="body2">{warning.message}</Typography>
                    {warning.recommendation && (
                      <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                         {warning.recommendation}
                      </Typography>
                    )}
                  </Alert>
                ))}
              </Box>
            )}
            
            {/* Validation Errors */}
            {validation.errors.length > 0 && (
              <Alert severity="error" sx={{ mt: 2 }}>
                <AlertTitle>{t('tab2.config_errors')}</AlertTitle>
                {validation.errors.map((e, idx) => (
                  <Typography key={idx} variant="body2">{e}</Typography>
                ))}
              </Alert>
            )}
            
            {/* Validation Warnings */}
            {validation.warnings.length > 0 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <AlertTitle>{t('tab2.configuration_notes')}</AlertTitle>
                {validation.warnings.map((w, idx) => (
                  <Typography key={idx} variant="body2">{w}</Typography>
                ))}
              </Alert>
            )}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

// ============================================================================
// ADVANCED FEATURES PANEL (Task 2.24)
// Toggleable advanced configuration sections
// ============================================================================

/**
 * AdvancedFeaturesPanel - Container for optional advanced configurations
 * Includes: Infrastructure, HNS, Night Ops, Climate/DNBI, MRoE
 */
const AdvancedFeaturesPanel = ({
  // Infrastructure
  infrastructureConfig,
  onInfrastructureConfigChange,
  // HNS
  hnsConfig,
  onHnsConfigChange,
  // Night Ops
  nightOpsConfig,
  onNightOpsConfigChange,
  // Climate
  climateConfig,
  onClimateConfigChange,
  // MRoE
  mroeConfig,
  onMroeConfigChange,
  // Monte Carlo (PHASE 1.3)
  monteCarloConfig,
  onMonteCarloConfigChange,
  // Build-up phase (V4-TAB6-004)
  buildupConfig,
  onBuildupConfigChange,
  operationDurationDays,
  // Scenario data for doctrine calculation
  scenario,
  forceBuilderResult,
  // Panel state
  expanded = false
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(expanded);

  // MRoE matrix interaction state (local -- not persisted until cell/confirm action)
  const [popoverAnchor, setPopoverAnchor] = useState(null); // { el, cat, col }
  const [popoverConds,  setPopoverConds]  = useState([]);
  const [popoverNote,   setPopoverNote]   = useState('');
  const [tierBDialog,   setTierBDialog]   = useState(null); // { cat, col }
  const [refusalMsg,    setRefusalMsg]    = useState(null); // string | null

  // Count enabled features (excluding HQ Override which is outside Advanced Features)
  const enabledCount = [
    infrastructureConfig?.enabled,
    hnsConfig?.enabled,
    nightOpsConfig?.enabled,
    climateConfig?.enabled,
    mroeConfig?.enabled,
    monteCarloConfig?.enabled,  // PHASE 1.3: Monte Carlo
    buildupConfig?.enabled      // V4-TAB6-004: Build-up phase
  ].filter(Boolean).length;
  
  // BUG FIX #3 (Feb 5, 2026): Calculate current doctrine HQ positions for display
  // This shows users what positions are being calculated before they override
  // CLEANUP NOTE: only consumed by dead showHqOverride block. Preserved with
  // renderEchelonPositionDefaults for potential Tab 6 use. Remove together.
  // eslint-disable-next-line no-unused-vars
  const calculateDoctrinePositions = () => {
    // Get national standard from force data
    const nationalStandard = forceBuilderResult?.brigade?.nationalStandard || 
                            forceBuilderResult?.division?.nationalStandard ||
                            forceBuilderResult?.corps?.nationalStandard || 
                            scenario?.force?.nationalStandard ||
                            'NATO';
    
    // Get brigade type for position calculation
    const brigadeType = forceBuilderResult?.brigade?.unit_type || 
                       forceBuilderResult?.brigades?.[0]?.unit_type ||
                       'MECHANIZED';
    
    // Get standard positions
    const standard = NATIONAL_STANDARDS[nationalStandard];
    
    if (!standard) {
      // Fallback to NATO if standard not found
      const natoStandard = NATIONAL_STANDARDS.NATO;
      return {
        battalion: natoStandard?.battalion?.hq_position_feba_km || 10,
        brigade: getBrigadeHQPosition('NATO', brigadeType),
        division: natoStandard?.division?.hq_position_feba_km || 28,
        corps: natoStandard?.corps?.hq_position_feba_km || 55,
        nationalStandard: 'NATO',
        brigadeType: brigadeType
      };
    }
    
    return {
      battalion: standard.battalion?.hq_position_feba_km || 10,
      brigade: getBrigadeHQPosition(nationalStandard, brigadeType),
      division: standard.division?.hq_position_feba_km || 28,
      corps: standard.corps?.hq_position_feba_km || 55,
      nationalStandard: nationalStandard,
      brigadeType: brigadeType
    };
  };
  
  // const doctrinePositions = calculateDoctrinePositions(); // Unused: was consumed by dead showHqOverride block

  // ---- MRoE matrix helpers ------------------------------------------------

  /** Preset config map -- uses imported named constants to avoid duplication */
  const PRESET_MAP = {
    OFFENSIVE:   { ...OFFENSIVE_MROE_CONFIG },
    URBAN:       { ...URBAN_MROE_CONFIG },
    PEACEKEEPING:{ ...PEACEKEEPING_MROE_CONFIG },
  };

  /**
   * Count cells that differ from the default matrix for the selected state.
   * Locked (Tier A) cells are excluded -- they cannot differ.
   */
  const modifiedCellCount = useMemo(() => {
    if (!mroeConfig.state || !mroeConfig.matrix) return 0;
    const defaultMatrix = MROE_DEFAULT_MATRICES[mroeConfig.state];
    if (!defaultMatrix) return 0;
    let count = 0;
    PATIENT_CATEGORIES.forEach(cat => {
      if (cat.lockTier === 'A') return;
      const cur = mroeConfig.matrix[cat.key];
      const def = defaultMatrix[cat.key];
      if (!cur || !def) return;
      if (cur.lle    !== def.lle)    count++;
      if (cur.nonLle !== def.nonLle) count++;
    });
    return count;
  }, [mroeConfig.state, mroeConfig.matrix]);

  /** Write a single matrix cell and call onMroeConfigChange */
  const updateMroeCell = (categoryKey, col, value, conds, condNote) => {
    const currentMatrix = mroeConfig.matrix || {};
    const blank = {
      lle: 'Y', lleConds: [], lleCondNote: '',
      nonLle: 'Y', nonLleConds: [], nonLleCondNote: '',
    };
    const cur = currentMatrix[categoryKey] || blank;
    const updated = col === 'lle'
      ? { ...cur, lle: value, lleConds: conds || [], lleCondNote: condNote || '' }
      : { ...cur, nonLle: value, nonLleConds: conds || [], nonLleCondNote: condNote || '' };
    onMroeConfigChange({
      ...mroeConfig,
      matrix: { ...currentMatrix, [categoryKey]: updated },
    });
  };

  /**
   * Handle click on a matrix cell chip.
   * Applies Tier A/B protection, opens Popover for Y*, cycles otherwise.
   */
  const handleMroeCellClick = (e, cat, col) => {
    const cell    = mroeConfig.matrix?.[cat.key];
    const current = col === 'lle' ? (cell?.lle ?? 'Y') : (cell?.nonLle ?? 'Y');

    // Tier A: locked -- show refusal snackbar, no state change
    if (cat.lockTier === 'A') {
      setRefusalMsg(cat.lockMsg || 'This cell cannot be modified.');
      return;
    }

    const cycle     = ['Y', 'Y*', 'N'];
    const nextValue = cycle[(cycle.indexOf(current) + 1) % cycle.length];

    // Going to N on Tier B or B_soft: require confirmation dialog
    if (nextValue === 'N' && (cat.lockTier === 'B' || cat.lockTier === 'B_soft')) {
      setTierBDialog({ cat, col });
      return;
    }

    // Going to Y*: open condition popover (pre-populate with existing conditions)
    if (nextValue === 'Y*') {
      const existingConds = col === 'lle'
        ? (cell?.lleConds    || [])
        : (cell?.nonLleConds || []);
      const existingNote = col === 'lle'
        ? (cell?.lleCondNote    || '')
        : (cell?.nonLleCondNote || '');
      setPopoverConds([...existingConds]);
      setPopoverNote(existingNote);
      setPopoverAnchor({ el: e.currentTarget, cat, col });
      return;
    }

    // Plain Y: just update
    updateMroeCell(cat.key, col, nextValue, [], '');
  };

  const handlePopoverConfirm = () => {
    if (!popoverAnchor) return;
    const { cat, col } = popoverAnchor;
    updateMroeCell(cat.key, col, 'Y*', popoverConds, popoverNote);
    setPopoverAnchor(null);
    setPopoverConds([]);
    setPopoverNote('');
  };

  const handleTierBConfirm = (proceed) => {
    if (proceed && tierBDialog) {
      const { cat, col } = tierBDialog;
      updateMroeCell(cat.key, col, 'N', [], '');
    }
    setTierBDialog(null);
  };

  // Render a single Y / Y* / N cell in the treatment matrix table
  const renderMroeCell = (cat, col, cell, defaultCell) => {
    const value     = col === 'lle' ? (cell?.lle    ?? 'Y') : (cell?.nonLle    ?? 'Y');
    const conds     = col === 'lle' ? (cell?.lleConds    || []) : (cell?.nonLleConds    || []);
    const condNote  = col === 'lle' ? (cell?.lleCondNote ?? '') : (cell?.nonLleCondNote ?? '');
    const isLocked  = cat.lockTier === 'A';
    const defValue  = defaultCell ? (col === 'lle' ? defaultCell.lle : defaultCell.nonLle) : value;
    const isModified = value !== defValue;

    const colorMap  = { Y: 'success', 'Y*': 'warning', N: 'error' };
    const chipColor = colorMap[value] || 'default';

    const condLabel = conds.length > 0
      ? conds.map(k => {
          const c = MROE_Y_STAR_CONDITIONS.find(x => x.key === k);
          return c ? c.label : k;
        }).join('; ')
      : (condNote || null);

    const chip = (
      <Chip
        label={value}
        size="small"
        color={chipColor}
        variant={isLocked ? 'outlined' : 'filled'}
        onClick={isLocked ? undefined : (ev) => handleMroeCellClick(ev, cat, col)}
        sx={{
          cursor:    isLocked ? 'default' : 'pointer',
          fontWeight:'bold',
          minWidth:  44,
          opacity:   isLocked ? 0.65 : 1,
          ...(isModified && {
            outline:      '2px solid',
            outlineColor: 'warning.main',
          }),
        }}
      />
    );

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
        {isLocked && <LockIcon sx={{ fontSize: 12, color: 'text.disabled' }} />}
        {isModified && (
          <WarningIcon sx={{ fontSize: 12, color: 'warning.main' }} />
        )}
        {condLabel ? (
          <Tooltip title={condLabel} arrow>
            {chip}
          </Tooltip>
        ) : chip}
        {value === 'Y*' && !condLabel && (
          <Tooltip title="Authorization condition not specified" arrow>
            <WarningIcon sx={{ fontSize: 12, color: 'warning.main' }} />
          </Tooltip>
        )}
      </Box>
    );
  };

  // ---- end MRoE helpers ---------------------------------------------------

  return (
    <>
    <Card sx={{ mb: 3, border: '1px dashed', borderColor: 'divider' }}>
      <CardContent>
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            cursor: 'pointer'
          }}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SettingsIcon color="action" />
            <Typography variant="h6">{t('tab2.advanced_features')}</Typography>
            <Chip 
              label={`${enabledCount}/7 enabled`}
              size="small"
              color={enabledCount > 0 ? 'primary' : 'default'}
            />
          </Box>
          <IconButton size="small">
            <ExpandMoreIcon sx={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }} />
          </IconButton>
        </Box>
        
        <Collapse in={isExpanded}>
          <Box sx={{ mt: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                Enable advanced features for detailed planning. Disabled features use default values.
              </Typography>
            </Alert>
            
            {/* Infrastructure Section */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={infrastructureConfig?.enabled || false}
                      onChange={(e) => onInfrastructureConfigChange({
                        ...infrastructureConfig,
                        enabled: e.target.checked
                      })}
                    />
                  }
                  label={<Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>[>>] {t('tab2.infrastructure')}</Typography>}
                />
                {infrastructureConfig?.enabled && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => onInfrastructureConfigChange({
                      ...AUSTERE_INFRASTRUCTURE_CONFIG,
                      enabled: true
                    })}
                  >
                    Apply Austere
                  </Button>
                )}
              </Box>
              
              <Collapse in={infrastructureConfig?.enabled}>
              <HelpText id="infrastructure" />
              <HelpText id="preset_infra_austere" />
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>{t('tab2.power_label')}</InputLabel>
                      <Select
                        value={infrastructureConfig?.power || 'GRID_STABLE'}
                        onChange={(e) => onInfrastructureConfigChange({
                          ...infrastructureConfig,
                          power: e.target.value
                        })}
                        label={t('tab2.power_label')}
                      >
                        {Object.entries(POWER_STATUS).map(([key, config]) => (
                          <MenuItem key={key} value={key}>
                            {config.icon} {config.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                <HelpText id="power" />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>{t('tab2.communications_label')}</InputLabel>
                      <Select
                        value={infrastructureConfig?.communications || 'FULL_SPECTRUM'}
                        onChange={(e) => onInfrastructureConfigChange({
                          ...infrastructureConfig,
                          communications: e.target.value
                        })}
                        label={t('tab2.communications_label')}
                      >
                        {Object.entries(COMMS_STATUS).map(([key, config]) => (
                          <MenuItem key={key} value={key}>
                            {config.icon} {config.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                <HelpText id="communications" />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    {/* UX-S97-002: renamed "Water" → "Water Supply Source" to distinguish from climate water risk */}
                    <FormControl fullWidth size="small">
                      <InputLabel>{t('tab2.water_label')}</InputLabel>
                      <Select
                        value={infrastructureConfig?.water || 'MUNICIPAL_SAFE'}
                        onChange={(e) => onInfrastructureConfigChange({
                          ...infrastructureConfig,
                          water: e.target.value
                        })}
                        label={t('tab2.water_label')}
                      >
                        {Object.entries(WATER_STATUS).map(([key, config]) => (
                          <MenuItem key={key} value={key}>
                            {config.icon} {config.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                <HelpText id="water" />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, ml: 0.5 }}>
                      Infrastructure supply availability
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>{t('tab2.local_medical_label')}</InputLabel>
                      <Select
                        value={infrastructureConfig?.local_medical || 'FUNCTIONAL'}
                        onChange={(e) => onInfrastructureConfigChange({
                          ...infrastructureConfig,
                          local_medical: e.target.value
                        })}
                        label={t('tab2.local_medical_label')}
                      >
                        {Object.entries(LOCAL_MEDICAL_STATUS).map(([key, config]) => (
                          <MenuItem key={key} value={key}>
                            {config.icon} {config.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                <HelpText id="local_medical" />
                  </Grid>
                </Grid>
              </Collapse>
            </Paper>
            
            {/* HNS Section */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={hnsConfig?.enabled || false}
                      onChange={(e) => onHnsConfigChange({
                        ...hnsConfig,
                        enabled: e.target.checked
                      })}
                    />
                  }
                  label={<Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}> {t('tab2.hns')}</Typography>}
                />
                {hnsConfig?.enabled && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => onHnsConfigChange({
                      ...NATO_EXERCISE_HNS_CONFIG,
                      enabled: true
                    })}
                  >
                    Apply NATO Exercise
                  </Button>
                )}
              </Box>
              
              <Collapse in={hnsConfig?.enabled}>
              <HelpText id="preset_hns_nato" />
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>{t('tab2.hns_support_level')}</InputLabel>
                      <Select
                        value={hnsConfig?.support_level || 'NONE'}
                        onChange={(e) => onHnsConfigChange({
                          ...hnsConfig,
                          support_level: e.target.value
                        })}
                        label={t('tab2.hns_support_level')}
                      >
                        {Object.entries(HNS_SUPPORT_LEVELS).map(([key, config]) => (
                          <MenuItem key={key} value={key}>
                            {config.icon} {config.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                <HelpText id="hns" />
                    <Typography variant="caption" color="text.secondary">
                      {HNS_SUPPORT_LEVELS[hnsConfig?.support_level]?.description || ''}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2">
                      Bed supplement: +{HNS_SUPPORT_LEVELS[hnsConfig?.support_level]?.civilian_hospital_beds || 0} beds
                    </Typography>
                    <Typography variant="body2">
                      Ambulances: {HNS_SUPPORT_LEVELS[hnsConfig?.support_level]?.civilian_ambulances || 0}
                    </Typography>
                  </Grid>
                </Grid>
              </Collapse>
            </Paper>
            
            {/* Night Ops Section */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={nightOpsConfig?.enabled || false}
                      onChange={(e) => onNightOpsConfigChange({
                        ...nightOpsConfig,
                        enabled: e.target.checked
                      })}
                    />
                  }
                  label={<Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}> {t('tab2.night_ops')}</Typography>}
                />
                {nightOpsConfig?.enabled && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => onNightOpsConfigChange({
                      ...CONVENTIONAL_NIGHT_OPS_CONFIG,
                      enabled: true
                    })}
                  >
                    Apply Conventional
                  </Button>
                )}
              </Box>
              
              <Collapse in={nightOpsConfig?.enabled}>
              <HelpText id="night_card" />
              <HelpText id="preset_night_conventional" />
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>{t('tab2.night_ops_level_label')}</InputLabel>
                      <Select
                        value={nightOpsConfig?.night_ops_level || 'NONE'}
                        onChange={(e) => onNightOpsConfigChange({
                          ...nightOpsConfig,
                          night_ops_level: e.target.value
                        })}
                        label={t('tab2.night_ops_level_label')}
                      >
                        {Object.entries(NIGHT_OPS_LEVELS).map(([key, config]) => (
                          <MenuItem key={key} value={key}>
                            {config.icon} {config.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                <HelpText id="night_level" />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>{t('tab2.illumination_label')}</InputLabel>
                      <Select
                        value={nightOpsConfig?.illumination || 'QUARTER_MOON'}
                        onChange={(e) => onNightOpsConfigChange({
                          ...nightOpsConfig,
                          illumination: e.target.value
                        })}
                        label={t('tab2.illumination_label')}
                      >
                        {Object.entries(ILLUMINATION_CONDITIONS).map(([key, config]) => (
                          <MenuItem key={key} value={key}>
                            {config.icon} {config.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                <HelpText id="illumination" />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>{t('tab2.nvg_label')}</InputLabel>
                      <Select
                        value={nightOpsConfig?.nvg_availability || 'FULL'}
                        onChange={(e) => onNightOpsConfigChange({
                          ...nightOpsConfig,
                          nvg_availability: e.target.value
                        })}
                        label={t('tab2.nvg_label')}
                      >
                        {Object.entries(NVG_AVAILABILITY).map(([key, config]) => (
                          <MenuItem key={key} value={key}>
                            {config.icon} {config.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                <HelpText id="nvg" />
                  </Grid>
                </Grid>
              </Collapse>
            </Paper>
            
            {/* Climate/DNBI Section */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={climateConfig?.enabled || false}
                      onChange={(e) => onClimateConfigChange({
                        ...climateConfig,
                        enabled: e.target.checked
                      })}
                    />
                  }
                  label={<Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}> {t('tab2.climate_dnbi')}</Typography>}
                />
                {climateConfig?.enabled && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => onClimateConfigChange({
                      ...EASTERN_EUROPE_CONFIG,
                      enabled: true
                    })}
                  >
                    Apply Eastern Europe
                  </Button>
                )}
              </Box>
              
              <Collapse in={climateConfig?.enabled}>
              <HelpText id="climate_card" />
              <HelpText id="preset_climate_ee" />
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>{t('tab2.climate_zone_label')}</InputLabel>
                      <Select
                        value={climateConfig?.climate_zone || 'TEMPERATE'}
                        onChange={(e) => onClimateConfigChange({
                          ...climateConfig,
                          climate_zone: e.target.value
                        })}
                        label={t('tab2.climate_zone_label')}
                      >
                        {Object.entries(CLIMATE_ZONES).map(([key, config]) => (
                          <MenuItem key={key} value={key}>
                            {config.icon} {config.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                <HelpText id="climate_zone" status={CLIMATE_OPTION_STATUS.zone[climateConfig?.climate_zone || 'TEMPERATE']} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>{t('tab2.endemic_disease_label')}</InputLabel>
                      <Select
                        value={climateConfig?.endemic_disease || 'NONE'}
                        onChange={(e) => onClimateConfigChange({
                          ...climateConfig,
                          endemic_disease: e.target.value
                        })}
                        label={t('tab2.endemic_disease_label')}
                      >
                        {Object.entries(ENDEMIC_DISEASE_LEVELS).map(([key, config]) => (
                          <MenuItem key={key} value={key}>
                            {config.icon} {config.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                <HelpText id="endemic" />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    {/* UX-S97-002: renamed "Water/Food Safety" → "Water/Food Contamination Risk" to distinguish from infrastructure water supply */}
                    <FormControl fullWidth size="small">
                      <InputLabel>{t('tab2.water_food_label')}</InputLabel>
                      <Select
                        value={climateConfig?.water_food_safety || 'SAFE'}
                        onChange={(e) => onClimateConfigChange({
                          ...climateConfig,
                          water_food_safety: e.target.value
                        })}
                        label={t('tab2.water_food_label')}
                      >
                        {Object.entries(WATER_FOOD_SAFETY).map(([key, config]) => (
                          <MenuItem key={key} value={key}>
                            {config.icon} {config.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                <HelpText id="water_food" status={CLIMATE_OPTION_STATUS.water[climateConfig?.water_food_safety || 'SAFE']} />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, ml: 0.5 }}>
                      DNBI risk from contaminated sources
                    </Typography>
                  </Grid>
                </Grid>
                {/* ENH-012: Theater Region — second row, full-width */}
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid size={{ xs: 12 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>{t('tab2.theater_region_label')}</InputLabel>
                      <Select
                        value={climateConfig?.theater_region || 'CENTRAL_EUROPE'}
                        onChange={(e) => onClimateConfigChange({
                          ...climateConfig,
                          theater_region: e.target.value
                        })}
                        label={t('tab2.theater_region_label')}
                      >
                        {Object.entries(THEATER_REGIONS).filter(([, region]) => !region.retired).map(([key, region]) => (
                          <MenuItem key={key} value={key}>
                            {region.icon} {region.label}
                            {' '}
                            <span style={{ color: 'var(--mui-palette-text-disabled, #888)', marginLeft: 8, fontSize: '0.85em' }}>
                              ({region.battle_casualty_scalar === 1.0
                                ? 'baseline'
                                : `×${region.battle_casualty_scalar.toFixed(2)} battle CR`})
                            </span>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                <HelpText id="region" status={CLIMATE_OPTION_STATUS.region} />
                  </Grid>
                </Grid>
                {climateConfig?.enabled && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      DNBI Rate: ~{calculateDNBIRate(climateConfig).toFixed(1)}/1000/day
                    </Typography>
                  </Box>
                )}
              </Collapse>
            </Paper>
            
            {/* Treatment Obligations (MedROE) -- V3.1 redesign */}
            <Paper variant="outlined" sx={{ p: 2 }}>
              {/* Header row */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  Treatment Obligations (MedROE)
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {/* Theater preset selector */}
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>{t('tab2.preset')}</InputLabel>
                    <Select
                      value={(() => {
                        const curState = mroeConfig.state;
                        const curEpw   = mroeConfig.epw_volume   || mroeConfig.epw_level   || 'NONE';
                        const curCiv   = mroeConfig.civilian_density || mroeConfig.civilian_level || 'NONE';
                        const match = Object.values(PRESET_MAP).find(p =>
                          p.state === curState && p.epw_level === curEpw && p.civilian_level === curCiv
                        );
                        return match ? Object.keys(PRESET_MAP).find(k => PRESET_MAP[k] === match) : '';
                      })()}
                      onChange={(e) => {
                        const preset = PRESET_MAP[e.target.value];
                        if (preset) onMroeConfigChange({ ...preset, enabled: true });
                      }}
                      label={t('tab2.preset')}
                    >
                      <MenuItem value=""><em>Custom</em></MenuItem>
                      <MenuItem value="OFFENSIVE">Offensive Operations</MenuItem>
                      <MenuItem value="URBAN">Urban Operations</MenuItem>
                      <MenuItem value="PEACEKEEPING">Peacekeeping</MenuItem>
                    </Select>
                  </FormControl>
                  {modifiedCellCount > 0 && (
                    <Chip
                      label={`${modifiedCellCount} cell${modifiedCellCount > 1 ? 's' : ''} modified`}
                      size="small"
                      color="warning"
                    />
                  )}
                </Box>
              </Box>

              {/* MRoE State selector (GREEN / AMBER / RED) */}
              <Box sx={{ mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80, flexShrink: 0 }}>
                    MedROE state:
                  </Typography>
                  <ToggleButtonGroup
                    value={mroeConfig.state || null}
                    exclusive
                    size="small"
                    onChange={(_, newState) => {
                      if (newState === null) {
                        onMroeConfigChange({ ...DEFAULT_MROE_CONFIG });
                      } else {
                        onMroeConfigChange({
                          ...mroeConfig,
                          enabled:          true,
                          state:            newState,
                          matrix:           MROE_DEFAULT_MATRICES[newState],
                          epw_volume:       mroeConfig.epw_volume   || mroeConfig.epw_level   || 'NONE',
                          civilian_density: mroeConfig.civilian_density || mroeConfig.civilian_level || 'NONE',
                          epw_level:        mroeConfig.epw_level   || 'NONE',
                          civilian_level:   mroeConfig.civilian_level || 'NONE',
                        });
                      }
                    }}
                  >
                    {Object.values(MROE_STATES).map(st => (
                      <ToggleButton
                        key={st.value}
                        value={st.value}
                        color={st.color}
                        sx={{ px: 2, fontWeight: 'bold' }}
                      >
                        {st.label}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Box>
                {mroeConfig.state && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, ml: '88px' }}>
                    {MROE_STATES[mroeConfig.state]?.description}
                  </Typography>
                )}
              </Box>

              {/* Expanded content -- volume selectors + matrix */}
              <Collapse in={Boolean(mroeConfig.state)}>
                {/* Volume selectors */}
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>{t('tab2.epw_volume_label')}</InputLabel>
                      <Select
                        value={mroeConfig.epw_volume || mroeConfig.epw_level || 'NONE'}
                        onChange={(e) => onMroeConfigChange({
                          ...mroeConfig,
                          epw_volume:  e.target.value,
                          epw_level:   e.target.value,
                        })}
                        label={t('tab2.epw_volume_label')}
                      >
                        {Object.entries(EPW_THREAT_LEVELS).map(([key, cfg]) => (
                          <MenuItem key={key} value={key}>{cfg.icon} {cfg.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                <HelpText id="mroe" />
                    <Typography variant="caption" color="text.secondary">
                      {EPW_THREAT_LEVELS[mroeConfig.epw_volume || mroeConfig.epw_level]?.description || ''}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>{t('tab2.civilian_density_label')}</InputLabel>
                      <Select
                        value={mroeConfig.civilian_density || mroeConfig.civilian_level || 'NONE'}
                        onChange={(e) => onMroeConfigChange({
                          ...mroeConfig,
                          civilian_density: e.target.value,
                          civilian_level:   e.target.value,
                        })}
                        label={t('tab2.civilian_density_label')}
                      >
                        {Object.entries(CIVILIAN_CASUALTY_LEVELS).map(([key, cfg]) => (
                          <MenuItem key={key} value={key}>{cfg.icon} {cfg.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                <HelpText id="mroe" />
                    <Typography variant="caption" color="text.secondary">
                      {CIVILIAN_CASUALTY_LEVELS[mroeConfig.civilian_density || mroeConfig.civilian_level]?.description || ''}
                    </Typography>
                  </Grid>
                </Grid>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, mb: 1.5 }}>
                  Volume selectors drive patient load calculations. Matrix below controls treatment authorization.
                </Typography>
                {/* R2-08 (2026-10-21): fixed surgical cases per day of the other patient categories (MedROE rows that drive the calculation) */}
                <Grid container spacing={1.5} sx={{ mb: 1 }}>
                  {[['hn_partnered_cases_day', 'mroe_hn_partnered'], ['hn_unpartnered_cases_day', 'mroe_hn_unpartnered'], ['vip_cases_day', 'mroe_vip'], ['contractor_cases_day', 'mroe_contractors']].map(([field, label]) => (
                    <Grid size={{ xs: 12, sm: 6, md: 3 }} key={field}>
                      <TextField
                        fullWidth size="small" type="number"
                        label={t(`tab2.${label}`)}
                        value={mroeConfig[field] ?? 0}
                        onChange={(e) => onMroeConfigChange({ ...mroeConfig, [field]: Math.max(0, Number(e.target.value) || 0) })}
                        inputProps={{ min: 0, step: 1 }}
                      />
                    </Grid>
                  ))}
                </Grid>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>{t('tab2.mroe_volumes_help')}</Typography>
                {(() => {
                  try {
                    const hostStatus = infrastructureConfig?.enabled ? (infrastructureConfig.local_medical || 'FUNCTIONAL') : 'FUNCTIONAL';
                    const mod = getMRoEModifiers(mroeConfig, 5000);
                    const load = eligibilityLoad({
                      matrix: mroeConfig.matrix, hostStatus,
                      civilianSurgicalDaily: mod.civilian_stream?.t1_surgical_daily || 0, epwDailyPatients: mod.epw_stream?.daily_patients || 0,
                      volumes: mroeConfig
                    });
                    const own = load.by_category.CIVCAS_OWN; const oth = load.by_category.CIVCAS_OTHER;
                    return (
                      <Alert severity="info" sx={{ mb: 1.5, py: 0.5 }}>
                        <Typography variant="caption">
                          {t('tab2.mroe_shares', {
                            own: Math.round(own.military_share * 100), other: Math.round(oth.military_share * 100),
                            host: Math.round(load.civilian.host * 100), unmet: Math.round(load.civilian.unmet * 100),
                            status: LOCAL_MEDICAL_STATUS[hostStatus]?.label || hostStatus
                          })}
                        </Typography>
                      </Alert>
                    );
                  } catch (e) { return null; }
                })()}

                {/* Treatment Authorization Matrix */}
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.75 }}>
                  Treatment Authorization Matrix
                </Typography>
                <TableContainer>
                  <Table size="small" sx={{ border: '1px solid', borderColor: 'divider' }}>
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'action.hover' }}>
                        <TableCell sx={{ fontWeight: 'bold', minWidth: 190 }}>Patient Category</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold', minWidth: 120 }}>LLE (Life, limb or eyesight)</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold', minWidth: 120 }}>Non-LLE (Urgent / Routine)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {PATIENT_CATEGORIES.map(cat => {
                        const cell = mroeConfig.matrix?.[cat.key];
                        const defaultCell = mroeConfig.state
                          ? MROE_DEFAULT_MATRICES[mroeConfig.state]?.[cat.key]
                          : null;
                        return (
                          <TableRow key={cat.key} sx={{ '&:last-child td': { border: 0 } }}>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography variant="body2">{cat.label}</Typography>
                                {cat.gcRef && (
                                  <Typography variant="caption" color="text.disabled">
                                    ({cat.gcRef})
                                  </Typography>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell align="center">
                              {renderMroeCell(cat, 'lle', cell, defaultCell)}
                            </TableCell>
                            <TableCell align="center">
                              {renderMroeCell(cat, 'nonLle', cell, defaultCell)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Modification warning */}
                {modifiedCellCount > 0 && (
                  <Alert severity="warning" sx={{ mt: 1.5 }}>
                    {modifiedCellCount} cell(s) modified from MRoE {mroeConfig.state} standard. All modifications require OPORD authorization and LEGAD verification.
                  </Alert>
                )}
                {mroeConfig.matrix?.CIVCAS_OWN?.lle === 'N' && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    CIVCAS (own forces) LLE set to N. GC IV Art.16 -- LEGAD authorization required. This modification will be printed in all generated reports.
                  </Alert>
                )}
              </Collapse>
            </Paper>

            
            {/* PHASE 1.3: Monte Carlo Uncertainty Analysis Section - ALWAYS VISIBLE */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={monteCarloConfig?.enabled || false}
                      onChange={(e) => onMonteCarloConfigChange({
                        ...monteCarloConfig,
                        enabled: e.target.checked
                      })}
                    />
                  }
                  label={
                    <Tooltip 
                      title="Monte Carlo simulation provides casualty uncertainty ranges (best/expected/worst case) by varying parameters within research-validated ranges. Adds ~15 seconds to calculation time."
                      arrow
                      placement="top"
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', cursor: 'help' }}>
                        🎲 Uncertainty Analysis (Monte Carlo)
                      </Typography>
                    </Tooltip>
                  }
                />
              </Box>
              
              <Collapse in={monteCarloConfig?.enabled}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2" gutterBottom>
                    <strong>{t('tab2.mc_educational_purpose')}</strong> {t('tab2.mc_educational_body')}
                  </Typography>
                  <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
                    <li><strong>{t('tab2.mc_worst_case')}</strong> {t('tab2.mc_worst_case_desc')}</li>
                    <li><strong>{t('tab2.mc_expected')}</strong> {t('tab2.mc_expected_desc')}</li>
                    <li><strong>{t('tab2.mc_best_case')}</strong> {t('tab2.mc_best_case_desc')}</li>
                  </ul>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    <strong>{t('tab2.mc_nato_standard')}</strong> {t('tab2.mc_nato_body')}
                  </Typography>
                </Alert>

                <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>{t('tab2.mc_iterations')}</strong> {t('tab2.mc_iterations_value')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        {t('tab2.mc_iterations_note')}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>{t('tab2.mc_calc_time')}</strong> {t('tab2.mc_calc_time_value')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        {t('tab2.mc_calc_time_note')}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="caption" color="text.secondary">
                        <strong>{t('tab2.mc_params_varied')}</strong> {t('tab2.mc_params_varied_body')}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>

                <Box sx={{ mt: 2, p: 1.5, bgcolor: 'success.50', borderRadius: 1, border: '1px solid', borderColor: 'success.main' }}>
                  <Typography variant="caption" color="success.dark" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    💡 <strong>{t('tab2.mc_best_practice')}</strong> {t('tab2.mc_best_practice_body')}
                  </Typography>
                </Box>
              </Collapse>
            </Paper>

            {/* V4-TAB6-004: Pre-combat build-up phase DNBI estimation */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={buildupConfig?.enabled ?? false}
                      onChange={(e) => onBuildupConfigChange({
                        ...buildupConfig,
                        enabled: e.target.checked
                      })}
                    />
                  }
                  label={
                    <Tooltip
                      title="Per AJMedP-1: forces deploy at authorized strength. During staging, movement to theatre, and acclimatization, DNBI accumulates at SUPPORT_ACTIVE rates (×1.2 deployment stress). Soldiers under treatment at H-Hour constitute the Day-0 medical load and do not cross the line of departure — their proportional share is subtracted from each brigade's effective fighting strength on D+1."
                      arrow
                      placement="top"
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', cursor: 'help' }}>
                        {t('tab2.buildup_phase_label')}
                      </Typography>
                    </Tooltip>
                  }
                />
              </Box>
              <Collapse in={buildupConfig?.enabled ?? false}>
                <HelpText id="buildup" />
                <Alert severity="info" sx={{ mb: 1.5 }}>
                  <Typography variant="body2">
                    {t('tab2.buildup_alert_body')}
                  </Typography>
                </Alert>
                {/* BUG-T11 fix: build-up ≤ op duration is valid (= means 0 combat days — warn, not error).
                    Only > op duration is a hard error. */}
                {ASSEMBLY_LIMITED_BY_OPERATION && buildupConfig?.enabled && buildupConfig?.duration_days > (operationDurationDays || 10) && (
                  <Alert severity="error" sx={{ mb: 1.5 }}>
                    {t('tab2.buildup_exceeds', { buildup: buildupConfig.duration_days, op: operationDurationDays || 10 })}
                    {t('tab2.buildup_reduce_to', { op: operationDurationDays || 10 })}
                  </Alert>
                )}
                {ASSEMBLY_LIMITED_BY_OPERATION && buildupConfig?.enabled && buildupConfig?.duration_days === (operationDurationDays || 10) && (
                  <Alert severity="warning" sx={{ mb: 1.5 }}>
                    {t('tab2.buildup_equals_op')}
                  </Alert>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <TextField
                    label={t('tab2.buildup_duration')}
                    type="number"
                    size="small"
                    value={buildupConfig?.duration_days ?? 0}
                    error={ASSEMBLY_LIMITED_BY_OPERATION && buildupConfig?.enabled && buildupConfig?.duration_days > (operationDurationDays || 10)}
                    onChange={(e) => {
                      const maxAllowed = ASSEMBLY_LIMITED_BY_OPERATION ? (operationDurationDays || 10) : ASSEMBLY_MAX_DAYS;
                      const v = Math.max(1, Math.min(maxAllowed, parseInt(e.target.value, 10) || 1));
                      onBuildupConfigChange({ ...buildupConfig, duration_days: v });
                    }}
                    inputProps={{ min: 1, max: ASSEMBLY_LIMITED_BY_OPERATION ? (operationDurationDays || 10) : ASSEMBLY_MAX_DAYS, step: 1 }}
                    sx={{ width: 200 }}
                    helperText={t('tab2.buildup_duration_help', { max: ASSEMBLY_LIMITED_BY_OPERATION ? (operationDurationDays || 10) : ASSEMBLY_MAX_DAYS })}
                  />
                  <Typography variant="caption" color="text.secondary">
                    DNBI rate: SUPPORT_ACTIVE baseline x 1.2 (deployment stress).
                    Day-0 load = patients still in system at H-Hour.
                  </Typography>
                </Box>
              </Collapse>
            </Paper>
          </Box>
        </Collapse>
      </CardContent>
    </Card>

    {/* Y* Condition Popover */}
    <Popover
      open={Boolean(popoverAnchor)}
      anchorEl={popoverAnchor?.el}
      onClose={() => setPopoverAnchor(null)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      transformOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      <Box sx={{ p: 2, maxWidth: 300 }}>
        <Typography variant="subtitle2" gutterBottom>Specify authorization basis</Typography>
        {MROE_Y_STAR_CONDITIONS.map(cond => (
          <FormControlLabel
            key={cond.key}
            control={
              <Checkbox
                size="small"
                checked={popoverConds.includes(cond.key)}
                onChange={(e) => {
                  setPopoverConds(prev =>
                    e.target.checked ? [...prev, cond.key] : prev.filter(k => k !== cond.key)
                  );
                }}
              />
            }
            label={<Typography variant="caption">{cond.label}</Typography>}
            sx={{ display: 'block', mb: 0 }}
          />
        ))}
        {popoverConds.includes('OTHER') && (
          <TextField
            size="small"
            fullWidth
            multiline
            rows={2}
            placeholder="Specify condition..."
            value={popoverNote}
            onChange={(e) => setPopoverNote(e.target.value)}
            sx={{ mt: 1 }}
          />
        )}
        <Box sx={{ display: 'flex', gap: 1, mt: 1.5, justifyContent: 'flex-end' }}>
          <Button size="small" onClick={() => setPopoverAnchor(null)}>Cancel</Button>
          <Button size="small" variant="contained" onClick={handlePopoverConfirm}>Confirm</Button>
        </Box>
      </Box>
    </Popover>

    {/* Tier B Legal Warning Dialog */}
    <Dialog open={Boolean(tierBDialog)} onClose={() => setTierBDialog(null)} maxWidth="sm">
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="error" />
          Legal Obligation Warning
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2">
          {tierBDialog?.cat?.lockMsg || 'Setting this cell to N removes treatment authorization for this category.'}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => handleTierBConfirm(false)}>Cancel</Button>
        <Button variant="contained" color="error" onClick={() => handleTierBConfirm(true)}>
          LEGAD authorized -- proceed
        </Button>
      </DialogActions>
    </Dialog>

    {/* Tier A Refusal Snackbar */}
    <Snackbar
      open={Boolean(refusalMsg)}
      autoHideDuration={4000}
      onClose={() => setRefusalMsg(null)}
      message={refusalMsg}
    />
    </>
  );
};



// ============================================================================
// AIR SITUATION PANEL (ENH-016)
// ============================================================================

/**
 * AirSituationPanel — Theater air situation selector.
 * Controls friendly CAS availability (casualty modifier) and
 * effective AD threat for MEDEVAC helicopter planning.
 * AJP-3.3 / FM 3-52 basis.
 */
const AirSituationPanel = ({
  airSituationConfig,
  onAirSituationConfigChange,
  enemyAdThreat,       // From threatConfig.enemy_ad — used to detect override conflict
  expanded = false
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(expanded);
  const currentLevel = airSituationConfig?.level || 'PARITY';
  const levelData = AIR_SITUATION_LEVELS[currentLevel] || AIR_SITUATION_LEVELS.PARITY;

  // Conflict detection: override is active AND differs from the configured enemy_ad.
  // PARITY has no override (null) — no conflict possible.
  // 2026-10-13: the helicopter availability is the air situation ceiling x the enemy air defense; the warning is for a real contradiction only
  // eslint-disable-next-line no-unused-vars
  const adOverride = levelData.medevac_ad_threat_override;   // legacy override code, no longer applied (kept for reference)
  const hasConflict = (currentLevel === 'ENEMY_AIR_SUPERIORITY' && ['NONE', 'LIMITED'].includes(enemyAdThreat))
    || (currentLevel === 'AIR_SUPERIORITY' && ['DENSE', 'SOPHISTICATED'].includes(enemyAdThreat));

  const borderColor = {
    AIR_SUPERIORITY: 'success.main',
    PARITY: 'info.main',
    CONTESTED: 'warning.main',
    ENEMY_AIR_SUPERIORITY: 'error.main'
  }[currentLevel] || 'info.main';

  return (
    <Card sx={{ mb: 3, border: '2px solid', borderColor }}>
      <CardContent>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            mb: isExpanded ? 2 : 0
          }}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FlightIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0 }}>{t('tab2.air_situation')}</Typography>
            <Chip
              label={levelData.short_label}
              size="small"
              color={levelData.color || 'default'}
            />
            {levelData.casualty_modifier !== 1.0 && (
              <Chip
                label={`${levelData.casualty_modifier > 1.0 ? '+' : ''}${Math.round((levelData.casualty_modifier - 1) * 100)}% casualties`}
                size="small"
                color={levelData.casualty_modifier > 1.0 ? 'warning' : 'success'}
                variant="outlined"
              />
            )}
            {hasConflict && (
              <Chip
                label={t('tab2.ad_conflict')}
                size="small"
                color="warning"
                icon={<WarningIcon />}
              />
            )}
          </Box>
          <IconButton size="small">
            <ExpandMoreIcon sx={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }} />
          </IconButton>
        </Box>

        <Collapse in={isExpanded}>
          <Alert severity="info" sx={{ mb: 2 }}>
            <AlertTitle>{t('tab2.air_situation_label')}</AlertTitle>
            {t('tab2.help.air_situation')}
          </Alert>

          {hasConflict && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <AlertTitle>{t('tab2.medevac_ad_override_label')}</AlertTitle>
              {t('tab2.air_conflict_text', { level: levelData.short_label, ad: enemyAdThreat })}
            </Alert>
          )}

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>{t('tab2.air_situation_label')}</InputLabel>
                <Select
                  value={currentLevel}
                  onChange={(e) => onAirSituationConfigChange({ level: e.target.value })}
                  label={t('tab2.air_situation_label')}
                >
                  {Object.entries(AIR_SITUATION_LEVELS).map(([key, val]) => (
                    <MenuItem key={key} value={key}>
                      {val.label} ({val.casualty_modifier === 1.0 ? t('tab2.no_modifier') : t('tab2.x_casualties', { mult: val.casualty_modifier })}; {t('tab2.air_ceiling_label')} {AIR_SITUATION_CEILING[key]})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Typography variant="subtitle2" gutterBottom>{t('tab2.planning_effect')}</Typography>
                <Typography variant="body2">
                  {t('tab2.battle_cas_rate_x', { mult: levelData.casualty_modifier })}
                  {levelData.casualty_modifier !== 1.0 && (
                    <> ({levelData.casualty_modifier > 1.0 ? t('tab2.increased') : t('tab2.reduced')} {t('tab2.by_percent', { pct: Math.abs(Math.round((levelData.casualty_modifier - 1) * 100)) })})</>
                  )}
                </Typography>
                <Typography variant="body2">
                  {t('tab2.air_ceiling_label')}: <strong>{AIR_SITUATION_CEILING[currentLevel]}</strong>
                </Typography>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant="caption" color="text.secondary">
                {levelData.planning_note}
              </Typography>
            </Grid>
          </Grid>
        </Collapse>
      </CardContent>
    </Card>
  );
};


// Need to add Settings icon import
const SettingsIcon = ({ color }) => (
  <Box component="span" sx={{ color: color === 'action' ? 'text.secondary' : color }}></Box>
);



/**
 * EnemyAssessmentPanel - Corps-level enemy quality configuration
 */
const EnemyAssessmentPanel = ({
  enemyQuality,
  forceSuperority,
  enemyStrengthRatio,
  onEnemyQualityChange,
  onForceSuperiorityChange,
  onEnemyStrengthRatioChange,
  expanded = false
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(expanded);
  
  const enemy = enemyQuality || DEFAULT_ENEMY_QUALITY;
  const superiority = forceSuperority || 'EQUAL';
  // 2026-10-13: enemy combat power index = strength ratio x training x equipment x morale -> suggested resistance
  const ratioValue = (typeof enemyStrengthRatio === 'number' && enemyStrengthRatio > 0) ? enemyStrengthRatio : 1;
  const power = enemyPowerIndex({ ...enemy, strength_ratio: ratioValue });
  const suggestedLevel = levelOfIndex(power.index);
  
  return (
    <Card sx={{ mb: 3, border: '2px solid', borderColor: 'error.light' }}>
      <CardContent>
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            cursor: 'pointer',
            mb: isExpanded ? 2 : 0
          }}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SecurityIcon color="error" />
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'error.main', mb: 0 }}>{t('tab2.enemy_assessment')}</Typography>

            <Chip 
              label={`${t('tab2.enemy_power_index')} ${power.index.toFixed(2)} → ${t(`tab2.phase_profile.${suggestedLevel.toLowerCase()}`)}`}
              size="small"
              color={suggestedLevel === 'HEAVY' ? 'error' : suggestedLevel === 'LIGHT' ? 'success' : 'default'}
            />
          </Box>
          <IconButton size="small">
            <ExpandMoreIcon sx={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }} />
          </IconButton>
        </Box>
        
        <Collapse in={isExpanded}>
          <Alert severity="info" sx={{ mb: 2 }}>
            <AlertTitle>{t('tab2.op_intelligence')}</AlertTitle>
            {t('tab2.enemy_assessment_help')}
          </Alert>
          
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                type="number"
                label={t('tab2.enemy_strength_ratio_label')}
                value={enemyStrengthRatio ?? 1}
                onChange={(e) => onEnemyStrengthRatioChange(e.target.value === '' ? '' : Number(e.target.value))}
                inputProps={{ min: 0.1, step: 0.1 }}
                helperText={t('tab2.enemy_strength_ratio_help')}
              />
            </Grid>
            {SHOW_FORCE_SUPERIORITY_LABEL && (
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth>
                <InputLabel>{t('tab2.force_superiority_label')}</InputLabel>
                <Select
                  value={superiority}
                  onChange={(e) => onForceSuperiorityChange(e.target.value)}
                  label={t('tab2.force_superiority_label')}
                >
                  {Object.entries(FORCE_SUPERIORITY).map(([key, val]) => (
                    <MenuItem key={key} value={key}>
                      {val.label} (Friendly: {val.friendly_casualty_multiplier}x, Enemy: {val.enemy_casualty_multiplier}x)
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            )}
            
            <Grid size={{ xs: 12, md: 4 }}>
              <FormControl fullWidth>
                <InputLabel>{t('tab2.enemy_training_label')}</InputLabel>
                <Select
                  value={enemy.training_level}
                  onChange={(e) => onEnemyQualityChange({ ...enemy, training_level: e.target.value })}
                  label={t('tab2.enemy_training_label')}
                >
                  {Object.entries(TRAINING_LEVELS).map(([key, val]) => (
                    <MenuItem key={key} value={key}>{val.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid size={{ xs: 12, md: 4 }}>
              <FormControl fullWidth>
                <InputLabel>{t('tab2.enemy_equipment_label')}</InputLabel>
                <Select
                  value={enemy.equipment_level}
                  onChange={(e) => onEnemyQualityChange({ ...enemy, equipment_level: e.target.value })}
                  label={t('tab2.enemy_equipment_label')}
                >
                  {Object.entries(EQUIPMENT_LEVELS).map(([key, val]) => (
                    <MenuItem key={key} value={key}>{val.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid size={{ xs: 12, md: 4 }}>
              <FormControl fullWidth>
                <InputLabel>{t('tab2.enemy_morale_label')}</InputLabel>
                <Select
                  value={enemy.morale_level}
                  onChange={(e) => onEnemyQualityChange({ ...enemy, morale_level: e.target.value })}
                  label={t('tab2.enemy_morale_label')}
                >
                  {Object.entries(MORALE_LEVELS).map(([key, val]) => (
                    <MenuItem key={key} value={key}>{val.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid size={{ xs: 12 }}>
              <Paper sx={{ p: 2, bgcolor: 'error.50' }}>
                <Typography variant="subtitle2" gutterBottom>{t('tab2.effect_casualty_calc')}</Typography>
                <Typography variant="body2">
                  {t('tab2.enemy_power_effect', {
                    ratio: ratioValue.toFixed(2),
                    training: power.parts.training.toFixed(2),
                    equipment: power.parts.equipment.toFixed(2),
                    morale: power.parts.morale.toFixed(2),
                    index: power.index.toFixed(2),
                    level: t(`tab2.phase_profile.${suggestedLevel.toLowerCase()}`),
                    light: RESISTANCE_ANCHORS.light,
                    heavy: RESISTANCE_ANCHORS.heavy
                  })}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Collapse>
      </CardContent>
    </Card>
  );
};

/**
 * TechnologyEnvironmentSection - Per-brigade tech environment override
 */
const TechnologyEnvironmentSection = ({ brigadeConfig, onUpdate, defaultTechEnv }) => {
  const { t } = useTranslation();
  const isOverridden = brigadeConfig.environment?.technology_env_override === true;
  const currentTech = brigadeConfig.environment?.technology_env || defaultTechEnv;
  
  return (
    <Box>
      <FormControlLabel
        control={
          <Checkbox
            checked={isOverridden}
            onChange={(e) => {
              onUpdate(brigadeConfig.brigade_id, {
                environment: {
                  ...brigadeConfig.environment,
                  technology_env_override: e.target.checked,
                  technology_env: e.target.checked ? currentTech : defaultTechEnv
                }
              });
            }}
          />
        }
        label={t('tab2.override_tech_env')}
      />

      {isOverridden && (
        <FormControl fullWidth sx={{ mt: 1 }}>
          <InputLabel>{t('tab2.technology_environment')}</InputLabel>
          <Select
            value={currentTech}
            onChange={(e) => {
              onUpdate(brigadeConfig.brigade_id, {
                environment: {
                  ...brigadeConfig.environment,
                  technology_env: e.target.value,
                  technology_env_override: true
                }
              });
            }}
            label={t('tab2.technology_environment')}
          >
            {Object.entries(TECHNOLOGY_ENVIRONMENT).map(([key, val]) => (
              <MenuItem key={key} value={key}>
                {val.label} ({val.casualty_multiplier}x)
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      
      {!isOverridden && (
        <Typography variant="caption" color="text.secondary">
          Using corps default: {TECHNOLOGY_ENVIRONMENT[defaultTechEnv]?.label || defaultTechEnv}
        </Typography>
      )}
    </Box>
  );
};

// ============================================================================
// ENH-023: PHASE OVERRIDE HELPER
// ============================================================================

/**
 * getEffectivePhases — merge unit.phase_overrides into parentPhases.
 * Returns a new array where each phase has the override's posture/mission/description
 * applied if present. Duration is never touched — parent timeline structure is preserved.
 *
 * @param {Object} unit         - Brigade or division config containing phase_overrides dict
 * @param {Array}  parentPhases - Parent unit's effective phases (corps→div, div→bde)
 * @returns {Array} Effective phases with overrides applied
 */
function getEffectivePhases(unit, parentPhases) {
  const overrides = unit?.phase_overrides || {};
  if (Object.keys(overrides).length === 0) return parentPhases.map(p => ({ ...p }));
  return parentPhases.map((phase, index) => {
    const ov = overrides[index];
    if (!ov) return { ...phase };
    return {
      ...phase,
      operational_posture: ov.operational_posture ?? phase.operational_posture,
      mission_type:        ov.mission_type        ?? phase.mission_type,
      description:         ov.description         ?? phase.description,
      // 2026-10-13 (Tab 2 plan, step 3b): casualty profile of the phase (constants/phaseCasualtyTable.js)
      resistance:         ov.resistance         ?? phase.resistance,
      enemy_strength_ratio: ov.enemy_strength_ratio ?? phase.enemy_strength_ratio,
      maneuver_group:       ov.maneuver_group       ?? phase.maneuver_group,
      special_condition:    ov.special_condition    ?? phase.special_condition,
      foothold_day:       ov.foothold_day       ?? phase.foothold_day,
      weather:            ov.weather            ?? phase.weather,   // R2-18: weather of the phase (empty = operation weather)
      advance_km_day:     ov.advance_km_day     ?? phase.advance_km_day,   // R2-30: advance of the brigade in this phase, km per day
    };
  });
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const Tab2_Operations = forwardRef(function Tab2_Operations({ scenario, mode, onUpdate, onComplete, onEdit, onNavigate }, ref) {
  const { t } = useTranslation();

  // ============================================================================
  // EXTRACT FORCE STRUCTURE FROM TAB1
  // ============================================================================
  
  
  const echelon = scenario?.force?.echelon || 'BRIGADE';
  // BUG-132 FIX: Capitalised echelon label for UI display — "Corps", "Division", "Brigade", "Battalion"
  const echelonLabel = echelon.charAt(0).toUpperCase() + echelon.slice(1).toLowerCase();
  const forceBuilderResult = scenario?.force?.forceBuilderResult || null;
  const corps = forceBuilderResult?.corps || null;
  
  // Wrap brigades in useMemo to prevent useEffect dependency changes on every render
  const brigades = useMemo(() => {
    if (forceBuilderResult?.brigades?.length > 0) return forceBuilderResult.brigades;
    // Tab1 flattens brigades to scenario.force.brigades — check flat path too
    if (scenario?.force?.brigades?.length > 0) return scenario.force.brigades;
    return [];
  }, [forceBuilderResult, scenario?.force?.brigades]);
  
  const divisions = useMemo(() => {
    // F2-ARCH-010: forceBuilderResult.divisions[] now populated for all echelons.
    // Singular .division fallback retained for pre-F2 persisted data.
    if (forceBuilderResult?.divisions?.length > 0) return forceBuilderResult.divisions;
    if (forceBuilderResult?.division) return [forceBuilderResult.division]; // legacy pre-F2
    // Tab1 flattens to scenario.force.divisions (plural) for CORPS echelon.
    if (scenario?.force?.divisions?.length > 0) return scenario.force.divisions;
    // Tab1 v4.16.0 now also flattens to scenario.force.division (singular) for DIVISION echelon.
    if (scenario?.force?.division) return [scenario.force.division];
    return [];
  }, [forceBuilderResult, scenario?.force?.divisions, scenario?.force?.division]);
  
  const battalions = useMemo(() => {
    return forceBuilderResult?.battalions || [];
  }, [forceBuilderResult]);
  
  // Calculate proper counts based on echelon
  // When planning at DIVISION level, the division itself is the top level
  const getEchelonCounts = useMemo(() => {
    // Check if corps actually exists in force structure
    const hasCorps = corps !== null && corps !== undefined;
    
    switch (echelon) {
      case 'BATTALION':
        return {
          corps: hasCorps ? 1 : 0,
          divisions: 0,
          brigades: 0,
          battalions: 1
        };
      case 'BRIGADE':
        return {
          corps: hasCorps ? 1 : 0,
          divisions: 0,
          brigades: 1,
          battalions: battalions.length
        };
      case 'DIVISION':
        return {
          corps: hasCorps ? 1 : 0,
          divisions: 1,
          brigades: brigades.length,
          battalions: battalions.length
        };
      case 'CORPS':
        return {
          corps: hasCorps ? 1 : 0,
          divisions: divisions.length,
          brigades: brigades.length,
          battalions: battalions.length
        };
      default:
        return {
          corps: hasCorps ? 1 : 0,
          divisions: divisions.length,
          brigades: brigades.length,
          battalions: battalions.length
        };
    }
  }, [echelon, corps, divisions.length, brigades.length, battalions.length]);

  // ============================================================================
  // TOOLBAR NAVIGATION ARCHITECTURE
  // ============================================================================
  
  // Stable ref for onUpdate - used by both handleMarkComplete and flushState
  const onUpdateRef = useRef(onUpdate);
  useEffect(() => { onUpdateRef.current = onUpdate; }, [onUpdate]);

  // Exposed to App.jsx via tab2Ref for toolbar navigation.
  // Tab2 is Pattern B (buffer locally, flush on Save & Continue).
  // flushState silently saves current local state to scenario WITHOUT:
  //   - validation gate (partial/in-progress state is still worth preserving)
  //   - onComplete call (no tab navigation triggered)
  // buildUpdates() is defined below after all state is available.
  useImperativeHandle(ref, () => ({
    flushState: () => {
      if (buildUpdatesRef.current && onUpdateRef.current) {
        onUpdateRef.current(buildUpdatesRef.current());
        // BUG-066: markDirty required — toolbar nav bypasses Save & Continue handler.
        markDirty();
      }
    }
  }));

  // Ref to buildUpdates - populated after all state is available (see below)
  const buildUpdatesRef = useRef(null);

  // ============================================================================
  // STATE MANAGEMENT
  // ============================================================================
  
  // v5.5 UI-009: Simplified to 2 modes - STANDARD (summary) and DETAILED (per-brigade)
  // Backward compatibility: 'BRIGADE' and 'EXPERT' are treated as 'DETAILED'
  // v5.6 FIX: Default to DETAILED when in expert mode, STANDARD when in standard mode
  const [configMode, setConfigMode] = useState(() => {
    // First check if there's a saved configuration level
    if (scenario?.operation_parameters?.configuration_level) {
      const savedLevel = scenario.operation_parameters.configuration_level;
      // Normalize legacy values
      if (savedLevel === 'BRIGADE' || savedLevel === 'EXPERT') {
        return 'DETAILED';
      }
      return savedLevel;
    }
    // Default based on global mode prop
    return mode === 'EXPERT' ? 'DETAILED' : 'STANDARD';
  });
  
  const [showSyncDialog, setShowSyncDialog] = useState(false);
  const [syncChanges, setSyncChanges] = useState({ added: [], deleted: [], modified: [] });
  
  const prevForceHashRef = useRef(null);
  // v7.2.2: Live pointer to autoRedistributeFrontage — prevents stale closures in setTimeout calls
  // Declared here (before sync/watchdog effects) to satisfy ESLint lexical order rules
  const autoRedistributeFrontageRef = useRef(null);
  
  const generateForceHash = (brigadesList) => {
    if (!brigadesList || brigadesList.length === 0) return '';
    return brigadesList.map(b => 
      `${b.id}:${b.name}:${b.nationalStandard}:${b.strength}:${b.parent_division_id}:${b.unit_type}`
    ).join('|');
  };
  
  const getStrengthByStandard = (standard) => {
    const nationalStd = NATIONAL_STANDARDS[standard];
    if (nationalStd?.brigade?.strength) {
      return nationalStd.brigade.strength;
    }
    return NATIONAL_STANDARDS.NATO?.brigade?.strength || 4500;
  };
  
  const [totalDuration, setTotalDuration] = useState(() => 
    scenario?.operation_parameters?.corps_config?.total_duration_days ||
    scenario?.operation_parameters?.duration_days || 10
  );
  
  const [autoCalculate, setAutoCalculate] = useState(true);
  // 2026-10-13 (Tab 2 plan, step 6): the default frontage follows the number of frontage-holding brigades (18 km each; it was 90 km,
  // a corps number, for any force).
  const [totalFrontage, setTotalFrontage] = useState(() =>
    scenario?.operation_parameters?.corps_config?.total_frontage_km ||
    defaultTotalFrontageKm(brigades.filter(b => unitTypeHasFrontage(b.unit_type || b.brigade_type || 'MECHANIZED')).length)
  );
  
  const [overallMission, setOverallMission] = useState(() =>
    scenario?.operation_parameters?.corps_config?.overall_mission || 'GROUND_COMBAT'
  );
  
  const [enemyQuality, setEnemyQuality] = useState(() =>
    scenario?.operation_parameters?.corps_config?.enemy_quality || DEFAULT_ENEMY_QUALITY
  );
  
  const [enemyStrengthRatio, setEnemyStrengthRatio] = useState(() =>
    scenario?.operation_parameters?.enemy_strength_ratio ?? scenario?.operation_parameters?.corps_config?.enemy_strength_ratio ?? 1
  );
  // 2026-10-22: enemy forces by sector and reinforcements by day (size only; the quality is the enemy card's). The sectors are derived from the
  // frontage of the brigades below (never typed); the entries are saved with a snapshot of the sectors they were entered against.
  const [enemyForces, setEnemyForces] = useState(() => ({
    default_manning: scenario?.operation_parameters?.enemy_forces?.default_manning ?? 1,
    entries: scenario?.operation_parameters?.enemy_forces?.entries || []
  }));

  const [forceSuperority, setForceSuperority] = useState(() =>
    scenario?.operation_parameters?.corps_config?.force_superiority || 'EQUAL'
  );
  
  const [defaultTechEnv, setDefaultTechEnv] = useState(() =>
    scenario?.operation_parameters?.corps_config?.default_technology_env ||
    corps?.force_quality?.technology_env ||
    'DRONE_CONTESTED'
  );
  // 2026-10-13 (Tab 2 plan, step 6): ONE technology environment. The default follows Tab 1 until the planner changes it here;
  // after that the Tab 2 choice applies to the whole operation (the calculator reads default_technology_env_user_set).
  const [techDefaultUserSet, setTechDefaultUserSet] = useState(() =>
    scenario?.operation_parameters?.corps_config?.default_technology_env_user_set === true
  );
  const tab1TechnologyEnv = corps?.force_quality?.technology_env;
  useEffect(() => {
    if (!techDefaultUserSet && tab1TechnologyEnv && tab1TechnologyEnv !== defaultTechEnv) setDefaultTechEnv(tab1TechnologyEnv);
  }, [tab1TechnologyEnv, techDefaultUserSet, defaultTechEnv]);

  // GROUP-1-D: Medical structure protection level (Gielerak bo factor, 0-100%).
  // Represents how well friendly medical structures (Role 1-3) are protected from
  // direct enemy action (artillery, drone strikes, deliberate targeting).
  // Maps to surgical capacity modifier in casualtyCalculator.js via:
  //   _boCapMod = 0.5 + (bo / 100) * 0.5
  // Default 70% = AJMedP-1 contested LSCO planning assumption.
  const [medicalProtectionLevel, setMedicalProtectionLevel] = useState(() =>
    scenario?.operation_parameters?.medical_protection_level ?? MEDICAL_PROTECTION_DEFAULT
  );

  // ENH-BAP: body_armor_level now lives in Tab1 force_quality — no Tab2 state needed

  const [operationName, setOperationName] = useState(() =>
    scenario?.operation_parameters?.operation_name || ''
  );
  // FEA-S97-002: configurable "Prepared by" for Annex Q cover page
  const [preparedBy, setPreparedBy] = useState(() =>
    scenario?.operation_parameters?.prepared_by || ''
  );
  const [operationType, setOperationType] = useState(() =>
    scenario?.operation_parameters?.operation_type || 'GROUND_COMBAT'
  );
  const [operationalPosture, setOperationalPosture] = useState(() => 
    scenario?.operation_parameters?.operational_posture || 'ATTACK'
  );
  const [engagementLevel, setEngagementLevel] = useState(() => 
    scenario?.operation_parameters?.engagement_level || 'FULL'
  );
  // ENH-022b: optional operation start date — ISO string "YYYY-MM-DD" or empty string
  const [operationStartDate, setOperationStartDate] = useState(() =>
    scenario?.operation_parameters?.operation_start_date || ''
  );
  // R2-09 (2026-10-22): the start time in Zulu; the date and the time are ONE setting shared with Tab 7 (set once, locked when the exercise clock has started)
  const [operationStartTimeZ, setOperationStartTimeZ] = useState(() =>
    scenario?.operation_parameters?.operation_start_time_z || DEFAULT_START_TIME_Z
  );
  const _scenarioStartDate = scenario?.operation_parameters?.operation_start_date || '';
  const _scenarioStartTimeZ = scenario?.operation_parameters?.operation_start_time_z || DEFAULT_START_TIME_Z;
  useEffect(() => {   // Tab 7 (trainer) may set the date: this tab follows the scenario
    setOperationStartDate(_scenarioStartDate);
    setOperationStartTimeZ(_scenarioStartTimeZ);
  }, [_scenarioStartDate, _scenarioStartTimeZ]);
  // locked from the first start of the clock until Tab 7 resets it (Tab 7 announces every clock write)
  const [exerciseClockStarted, setExerciseClockStarted] = useState(() => !!(lsRead('psp_tab7_exercise_clock')?.started_at));
  useEffect(() => {
    const onClock = () => setExerciseClockStarted(!!(lsRead('psp_tab7_exercise_clock')?.started_at));
    window.addEventListener('psp-exercise-clock', onClock);
    window.addEventListener('storage', onClock);
    return () => { window.removeEventListener('psp-exercise-clock', onClock); window.removeEventListener('storage', onClock); };
  }, []);
  const [terrain, setTerrain] = useState(() => 
    scenario?.operation_parameters?.environmental_factors?.terrain || 'MIXED'
  );
  // W5 (2026-10-20): cold-weather protection of the troops (scales the cold injuries the weather adds to the non-battle casualties)
  const [coldProtection, setColdProtection] = useState(() => scenario?.operation_parameters?.cold_protection || 'STANDARD');
  const [weather, setWeather] = useState(() => 
    scenario?.operation_parameters?.environmental_factors?.weather || 'CLEAR'
  );
  const [threatLevel, setThreatLevel] = useState(() => 
    scenario?.operation_parameters?.environmental_factors?.threat_level || 'MODERATE_THREAT'
  );

  // BUG-057 FIX: Evacuation paradigm for survivalModule.js (read-only — derived from scenario)
  // Values: 'PERMISSIVE', 'CONTESTED', 'DEGRADED', 'PROLONGED'
  // v7.6.0 ESLint FIX: converted from useState (setter was never called) to plain const
  // 2026-10-10: it is now a planner input (it had no control; every scenario silently ran as CONTESTED).
  // Values: 'PERMISSIVE', 'CONTESTED', 'DENIED'. An unreadable saved value falls back to the documented default.
  const [evacuationParadigm] = useState(() => {   // R2-28b: saved for older readers, no longer a control
    try {
      return resolveEvacuationParadigm(scenario).paradigm;
    } catch (e) {
      return DEFAULT_EVACUATION_PARADIGM;
    }
  });
  // Hold time (hours) before evacuation can start. Empty = the paradigm's own default.
  const [evacuationHoldHours, setEvacuationHoldHours] = useState(() => {
    const v = scenario?.operation_parameters?.evacuation_hold_hours;
    return v === undefined || v === null ? '' : String(v);
  });
  
  // ========== PHASE 2: CORPS CONFIGURATION STATE ==========
  // NEW: Corps personnel and phases
  const [corpsPersonnel, setCorpsPersonnel] = useState(() =>
    scenario?.operation_parameters?.corps_config?.personnel || 2000
  );
  
  const [corpsOperationalState, setCorpsOperationalState] = useState(() =>
    scenario?.operation_parameters?.corps_config?.operational_state || 'SUPPORT_ACTIVE'
  );
  
  const [corpsPositionFeba, setCorpsPositionFeba] = useState(() =>
    scenario?.operation_parameters?.corps_config?.position_feba_km || 60
  );
  
  const [corpsPhases, setCorpsPhases] = useState(() =>
    scenario?.operation_parameters?.corps_config?.phases || []
  );

  // BUG-S97-011: cascade-skip warning — shown when divisions/brigades have local overrides
  const [cascadeSkipMsg, setCascadeSkipMsg] = useState(null); // string | null
  // OBS-S109-002: Force Cascade confirmation dialog
  const [forceCascadeConfirm, setForceCascadeConfirm] = useState(false);
  
  // ========== PHASE 2: DIVISION CONFIGURATION STATE ==========
  // NEW: Division configs
  const [divisionConfigs, setDivisionConfigs] = useState(() => {
    
    // BUG-S117-001 fix: [] is truthy — must check .length > 0 to avoid returning
    // an empty array and blocking the BUG-070 repopulation effect below.
    if (scenario?.operation_parameters?.division_configs?.length > 0) {
      return scenario.operation_parameters.division_configs;
    }
    
    if (divisions.length === 0) {
      return [];
    }
    
    
    return divisions.map((division, index) => ({
      division_id: division.id,
      division_number: division.division_number || (index + 1),
      division_name: division.name || `Division ${index + 1}`,
      personnel: division.strength || 1000,   // division.strength = HQ personnel (Tab1 convention)
      operational_state: 'SUPPORT_ACTIVE',
      position_feba_km: division.tactical_in_parent?.hq_position_feba_km || 30,
      uses_corps_phases: true,            // Default: inherit from corps
      phases: [],
      phase_overrides: {},
      brigade_ids: brigades
        .filter(b => b.parent_division_id === division.id)
        .map(b => b.id),
      brigade_overrides: []
    }));
  });

  // BUG-070 FIX: divisionConfigs lazy initializer runs once at mount.
  // If forceBuilderResult.divisions isn't populated yet (async prop update),
  // divisions.length === 0 fires and divisionConfigs stays [] permanently.
  // This useEffect re-populates when divisions arrive and configs are still empty.
  useEffect(() => {
    // BUG-S117-001 fix: match the lazy initializer — treat [] as "not saved"
    if (divisionConfigs.length === 0 &&
        divisions.length > 0 &&
        !(scenario?.operation_parameters?.division_configs?.length > 0)) {
      setDivisionConfigs(divisions.map((division, index) => ({
        division_id: division.id,
        division_number: division.division_number || (index + 1),
        division_name: division.name || `Division ${index + 1}`,
        personnel: division.strength || 1000,   // division.strength = HQ personnel (Tab1 convention)
        operational_state: 'SUPPORT_ACTIVE',
        position_feba_km: division.tactical_in_parent?.hq_position_feba_km || 30,
        uses_corps_phases: true,
        phases: [],
        phase_overrides: {},
        brigade_ids: brigades
          .filter(b => b.parent_division_id === division.id)
          .map(b => b.id),
        brigade_overrides: []
      })));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [divisions, brigades]);

  // Dialog state for editing
  const [editingCorps, setEditingCorps] = useState(false);
  const [editingDivision, setEditingDivision] = useState(null);
  
  // ========== THREAT ASSESSMENT STATE (Task 2.3) ==========
  const [threatConfig, setThreatConfig] = useState(() => {
    const saved = scenario?.operation_parameters?.threat_assessment;
    if (saved) {
      return {
        threat_type: saved.threat_type || DEFAULT_THREAT_CONFIG.threat_type,
        enemy_ad: saved.enemy_ad || DEFAULT_THREAT_CONFIG.enemy_ad,
        ied_threat: saved.ied_threat || DEFAULT_THREAT_CONFIG.ied_threat,
        insurgent_activity: saved.insurgent_activity || DEFAULT_THREAT_CONFIG.insurgent_activity,
        cbrn_risk: saved.cbrn_risk || DEFAULT_THREAT_CONFIG.cbrn_risk,
        artillery_threat: saved.artillery_threat || DEFAULT_THREAT_CONFIG.artillery_threat,
        drone_threat: saved.drone_threat || DEFAULT_THREAT_CONFIG.drone_threat,
        drone_dow_modifier: saved.drone_dow_modifier ?? null,
        drone_schedule: Array.isArray(saved.drone_schedule) ? saved.drone_schedule : []
      };
    }
    return { ...DEFAULT_THREAT_CONFIG };
  });
  // FIX-T2-001: Panels manage their own expansion state internally
  
  // ========== LOC CONFIGURATION STATE (Task 2.8) ==========
  const [locConfig, setLocConfig] = useState(() => {
    const saved = scenario?.operation_parameters?.loc_config;
    if (saved) {
      // BUG-094 FIX: Spread DEFAULT_LOC_CONFIG first so any keys added to the constant
      // after a scenario was saved are still present. The prior explicit-keys-only form
      // silently stripped extra keys, causing getLOCModifiers to return NaN speed_modifier
      // when it referenced a key that was not in the restored object.
      return {
        ...DEFAULT_LOC_CONFIG,
        msr_status: saved.msr_status || DEFAULT_LOC_CONFIG.msr_status,
        asr_status: saved.asr_status || DEFAULT_LOC_CONFIG.asr_status,
        loc_security: saved.loc_security || DEFAULT_LOC_CONFIG.loc_security,
        primary_bridge: saved.primary_bridge || DEFAULT_LOC_CONFIG.primary_bridge,
        weather: saved.weather || DEFAULT_LOC_CONFIG.weather
      };
    }
    return { ...DEFAULT_LOC_CONFIG };
  });
  
  // ========== INFRASTRUCTURE CONFIG STATE (Task 2.12/2.13) ==========
  const [infrastructureConfig, setInfrastructureConfig] = useState(() => {
    const saved = scenario?.operation_parameters?.infrastructure_config;
    if (saved) {
      return {
        enabled: saved.enabled ?? false,
        power: saved.power || DEFAULT_INFRASTRUCTURE_CONFIG.power,
        communications: saved.communications || DEFAULT_INFRASTRUCTURE_CONFIG.communications,
        water: saved.water || DEFAULT_INFRASTRUCTURE_CONFIG.water,
        local_medical: saved.local_medical || DEFAULT_INFRASTRUCTURE_CONFIG.local_medical
      };
    }
    return { enabled: false, ...DEFAULT_INFRASTRUCTURE_CONFIG };
  });
  
  // ========== HNS CONFIG STATE (Task 2.14/2.15) ==========
  const [hnsConfig, setHnsConfig] = useState(() => {
    const saved = scenario?.operation_parameters?.hns_config;
    if (saved) {
      return {
        enabled: saved.enabled ?? false,
        support_level: saved.support_level || DEFAULT_HNS_CONFIG.support_level,
        hospitals: saved.hospitals || [],
        ambulances: saved.ambulances || { als: 0, bls: 0, transport: 0 }
      };
    }
    return { ...DEFAULT_HNS_CONFIG };
  });
  
  // ========== NIGHT OPS CONFIG STATE (Task 2.16/2.17) ==========
  const [nightOpsConfig, setNightOpsConfig] = useState(() => {
    const saved = scenario?.operation_parameters?.night_ops_config;
    if (saved) {
      return {
        enabled: saved.enabled ?? false,
        night_ops_level: saved.night_ops_level || DEFAULT_NIGHT_OPS_CONFIG.night_ops_level,
        illumination: saved.illumination || DEFAULT_NIGHT_OPS_CONFIG.illumination,
        nvg_availability: saved.nvg_availability || DEFAULT_NIGHT_OPS_CONFIG.nvg_availability
      };
    }
    return { ...DEFAULT_NIGHT_OPS_CONFIG };
  });
  
  // ========== CLIMATE/DNBI CONFIG STATE (Task 2.23) ==========
  const [climateConfig, setClimateConfig] = useState(() => {
    const saved = scenario?.operation_parameters?.climate_config;
    if (saved) {
      return {
        enabled: saved.enabled ?? false,
        climate_zone: saved.climate_zone || DEFAULT_CLIMATE_CONFIG.climate_zone,
        endemic_disease: saved.endemic_disease || DEFAULT_CLIMATE_CONFIG.endemic_disease,
        water_food_safety: saved.water_food_safety || DEFAULT_CLIMATE_CONFIG.water_food_safety,
        acclimatization_complete: saved.acclimatization_complete ?? true,
        prophylaxis_compliance: saved.prophylaxis_compliance ?? 1.0,
        // ENH-012: theater_region — backward compat default for old saved scenarios
        theater_region: saved.theater_region || DEFAULT_CLIMATE_CONFIG.theater_region,
        // R2-14: the season was dropped when the card state was rebuilt, so it was always summer
        season: saved.season || DEFAULT_CLIMATE_CONFIG.season,
        hemisphere: saved.hemisphere || DEFAULT_CLIMATE_CONFIG.hemisphere   // R2-09
      };
    }
    return { ...DEFAULT_CLIMATE_CONFIG };
  });
  
  // ========== MRoE CONFIG STATE (Task 2.20/2.21) ==========
  const [mroeConfig, setMroeConfig] = useState(() => {
    const saved = scenario?.operation_parameters?.mroe_config;
    if (saved) {
      // New V3.1 format: has state + matrix
      if (saved.state) {
        return {
          enabled:          saved.enabled ?? (saved.state !== null),
          state:            saved.state,
          matrix:           saved.matrix || MROE_DEFAULT_MATRICES[saved.state] || null,
          epw_volume:       saved.epw_volume       || saved.epw_level       || DEFAULT_MROE_CONFIG.epw_volume,
          civilian_density: saved.civilian_density || saved.civilian_level  || DEFAULT_MROE_CONFIG.civilian_density,
          epw_level:        saved.epw_level        || DEFAULT_MROE_CONFIG.epw_level,
          civilian_level:   saved.civilian_level   || DEFAULT_MROE_CONFIG.civilian_level,
          hn_partnered_cases_day:   saved.hn_partnered_cases_day   ?? 0,
          hn_unpartnered_cases_day: saved.hn_unpartnered_cases_day ?? 0,
          vip_cases_day:            saved.vip_cases_day            ?? 0,
          contractor_cases_day:     saved.contractor_cases_day     ?? 0,
        };
      }
      // Legacy V3.0 format: { enabled, epw_level, civilian_level } — backward compat
      return {
        enabled:          saved.enabled ?? false,
        state:            null,
        matrix:           null,
        epw_volume:       saved.epw_level       || DEFAULT_MROE_CONFIG.epw_volume,
        civilian_density: saved.civilian_level  || DEFAULT_MROE_CONFIG.civilian_density,
        epw_level:        saved.epw_level       || DEFAULT_MROE_CONFIG.epw_level,
        civilian_level:   saved.civilian_level  || DEFAULT_MROE_CONFIG.civilian_level,
      };
    }
    return { ...DEFAULT_MROE_CONFIG };
  });
  
  // ========== AIR SITUATION CONFIG STATE (ENH-016) ==========
  const [airSituationConfig, setAirSituationConfig] = useState(() => {
    const saved = scenario?.operation_parameters?.air_situation_config;
    if (saved?.level && AIR_SITUATION_LEVELS[saved.level]) {
      return { level: saved.level };
    }
    return { ...DEFAULT_AIR_SITUATION_CONFIG };
  });

  // ========== MONTE CARLO CONFIG STATE (PHASE 1.3) ==========
  const [monteCarloConfig, setMonteCarloConfig] = useState(() => {
    const saved = scenario?.operation_parameters?.monte_carlo_config;
    if (saved) {
      return {
        enabled: saved.enabled ?? false,
        iterations: saved.iterations || 1000
      };
    }
    return {
      enabled: false,
      iterations: 1000
    };
  });

  // ========== BUILD-UP PHASE CONFIG STATE (V4-TAB6-004) ==========
  // Pre-combat build-up DNBI estimation. Default OFF — avoids zeroing combat days
  // when op_duration equals the old default 14-day build-up. (BUG-BUILDUP-001 fix)
  const [buildupConfig, setBuildupConfig] = useState(() => {
    const saved = scenario?.operation_parameters?.buildup_config;
    if (saved) {
      return {
        enabled: saved.enabled ?? false,
        duration_days: saved.duration_days ?? 0,
      };
    }
    return { enabled: false, duration_days: 0 };
  });
  
  // ========== ADVANCED FEATURES TOGGLE STATE (Task 2.24) ==========
  
  const [phases, setPhases] = useState(() => 
    scenario?.operation_parameters?.phases || []
  );
  const [usePhases, setUsePhases] = useState(() =>
    scenario?.operation_parameters?.phases?.length > 0 || false
  );
  
  // ========== BRIGADE-LEVEL CONFIGS (ENHANCED WITH OPERATIONAL STATE) ==========
  const [brigadeConfigs, setBrigadeConfigs] = useState(() => {
    
    // BACKWARD COMPATIBILITY: Check old location first (operation_parameters.brigade_configs)
    // This allows loading old scenarios, though none should exist yet
    if (scenario?.operation_parameters?.brigade_configs && 
        !scenario?.force?.forceBuilderResult?.brigades?.[0]?.operational_state) {
      return scenario.operation_parameters.brigade_configs.map(config => ({
        ...config,
        operational_state: config.operational_state || 
          getOperationalStateFromPosition(config.position?.position_feba_km || 5, config.unit_type || 'MECHANIZED'),
        operational_state_override: config.operational_state_override || false
      }));
    }
    
    if (brigades.length === 0) {
      return [];
    }
    
    
    // v7.6.0: Use division-aware frontage map for initial assignment.
    // Brigade width varies per division; adjacency null at division boundaries.
    const initFrontageMap = computeDivisionFrontageMap(brigades, totalFrontage);

    return brigades.map((brigade, index) => {
      const parentDivision = divisions.find(d => d.id === brigade.parent_division_id);
      const { resolved: forceQuality, inherited_from } = resolveForceQuality(brigade, forceBuilderResult);
      
      const extractedPosture = brigade.tactical_in_parent?.posture || 
                               parentDivision?.tactical_in_parent?.posture ||
                               forceBuilderResult?.corps?.tactical_in_parent?.posture ||
                               'DELIBERATE_DEFENSE';
      
      const postureToMissionType = {
        'ATTACK': 'ATTACK',
        'HASTY_ATTACK': 'ATTACK',
        'DELIBERATE_ATTACK': 'ATTACK',
        'EXPLOITATION': 'EXPLOITATION',
        'PURSUIT': 'ATTACK',
        'DEFENSE': 'DEFEND',
        'HASTY_DEFENSE': 'DEFEND',
        'DELIBERATE_DEFENSE': 'DEFEND',
        'DELAY': 'DEFEND',
        'WITHDRAWAL': 'CONSOLIDATION',
        'RETIRE': 'CONSOLIDATION',
        'RESERVE': 'RESERVE',
        'MARCH': 'MOVEMENT',
        'MARCHING': 'MOVEMENT',
        'TRANSIT': 'MOVEMENT',
        'RECONSTITUTION': 'SUPPORTING'
      };
      const missionType = postureToMissionType[extractedPosture] || 'DEFEND';
      
      const extractedNationalStandard = brigade.nationalStandard || 
                                        parentDivision?.nationalStandard ||
                                        forceBuilderResult?.corps?.nationalStandard ||
                                        'NATO';
      
      const unitType = brigade.unit_type || brigade.brigade_type || parentDivision?.unit_type || 'MECHANIZED';
      const frontageStatus = getBrigadeFrontageStatus(unitType, extractedPosture, missionType);
      
      // Get doctrine-based position (check if already set by Tab2)
      const doctrine = getUnitTypeDoctrine(unitType);
      const positionKm = brigade.position?.position_feba_km ?? 
                         (frontageStatus.holdsFrontage ? 5 : doctrine.default_position);
      
      // Auto-assign operational state based on position and unit type
      const autoState = brigade.operational_state ?? 
                        getOperationalStateFromPosition(positionKm, unitType);
      
      
      // ========================================================================
      // v7.0 ARCHITECTURAL REFACTOR: SPREAD EXISTING BRIGADE
      // ========================================================================
      // Instead of creating NEW object with selected fields,
      // SPREAD existing brigade and ADD Tab2 operational fields
      // This preserves ALL Tab 1 data automatically
      return {
        ...brigade,  //  SPREAD ALL existing Tab 1 data
        
        // Override/add specific Tab 2 fields (operational configuration)
        brigade_id: brigade.id,
        brigade_number: brigade.brigade_number || (index + 1),
        brigade_name: brigade.name || `Brigade ${index + 1}`,
        parent_division_number: parentDivision?.division_number || Math.floor(index / 3) + 1,
        parent_division_name: parentDivision?.name || `Division ${parentDivision?.division_number || Math.floor(index / 3) + 1}`,
        // BUG-163 FIX: Write both naming conventions — nsColors lookup uses camelCase,
        // calculator uses snake_case. Explicit assignment prevents ambiguity from toJSON().
        nationalStandard: extractedNationalStandard,
        national_standard: extractedNationalStandard,
        
        // Operational State (preserve if exists, otherwise calculate)
        operational_state: autoState,
        operational_state_override: brigade.operational_state_override || false,
        
        // Phase inheritance (preserve if exists)
        uses_division_phases: brigade.uses_division_phases ?? true,
        phases: brigade.phases || [],
        phase_overrides: brigade.phase_overrides || {},
        
        // Frontage status (calculate from current state)
        frontage_status: {
          holds_frontage: frontageStatus.holdsFrontage,
          reason: frontageStatus.reason,
          rear_area_modifier: frontageStatus.casualty_modifier
        },
        
        // v7.6.0: Position uses division-aware initFrontageMap.
        // Adjacent IDs are null at division boundaries — no cross-division linking.
        position: (() => {
          const brigId = brigade.id || brigade.brigade_id;
          const divPos = initFrontageMap[brigId];
          const sectorDesig = divPos
            ? (divPos.indexInDiv === 0 ? 'LEFT'
               : divPos.indexInDiv === divPos.divCount - 1 ? 'RIGHT'
               : 'CENTER')
            : 'REAR';
          return {
            position_feba_km: brigade.position?.position_feba_km ?? positionKm,
            frontage_start_km: frontageStatus.holdsFrontage
              ? (brigade.position?.frontage_start_km ?? divPos?.start ?? null)
              : null,
            frontage_end_km: frontageStatus.holdsFrontage
              ? (brigade.position?.frontage_end_km ?? divPos?.end ?? null)
              : null,
            frontage_width_km: frontageStatus.holdsFrontage && divPos
              ? (brigade.position?.frontage_width_km
                 ?? Math.round((divPos.end - divPos.start) * 10) / 10)
              : 0,
            sector_designation: frontageStatus.holdsFrontage
              ? (brigade.position?.sector_designation ?? sectorDesig)
              : 'REAR',
            // Adjacency null at division boundaries
            adjacent_left:  frontageStatus.holdsFrontage ? (divPos?.leftNeighborId  ?? null) : null,
            adjacent_right: frontageStatus.holdsFrontage ? (divPos?.rightNeighborId ?? null) : null,
            manual_lock: brigade.position?.manual_lock ?? false,
            reserve_position: !frontageStatus.holdsFrontage && missionType === 'RESERVE'
              ? (brigade.position?.reserve_position ?? 'DISTANT')
              : null
          };
        })(),
        
        // Operation parameters (preserve if exists, otherwise create defaults)
        operation: brigade.operation || {
          mission_type: missionType,
          mission_statement: `Brigade ${index + 1} conducts ${missionType.toLowerCase()} operations in assigned sector`,
          start_day: 1,
          start_hour: 6,
          end_day: totalDuration,
          end_hour: 18,
          operational_posture: extractedPosture,
          engagement_level: 'PREPARED_ENGAGEMENT',
          tempo: 'DELIBERATE',
          synchronization: {
            sync_with_brigade: null,
            support_from: null,
            timing_relationship: 'INDEPENDENT'
          }
        },
        
        // Environment (preserve if exists, otherwise create defaults)
        environment: brigade.environment || {
          terrain: 'MIXED',
          terrain_details: {
            dominant_type: 'MIXED',
            obstacles: [],
            key_terrain: [],
            avenues_of_approach: 'MODERATE'
          },
          weather: 'CLEAR',
          weather_by_day: {},
          threat_level: 'MODERATE_THREAT',
          enemy_disposition: 'PREPARED_DEFENSE',
          visibility: { day: 'GOOD', night: 'LIMITED' },
          technology_env: forceQuality.technology_env || 'DRONE_CONTESTED',
          technology_env_override: false
        },
        
        // Medical (preserve if exists, otherwise create defaults)
        medical: brigade.medical || {
          role2_facility_id: brigade.role2_basic_facility?.facility_id || null,
          role2_facility_name: brigade.role2_basic_facility?.facility_name || 'Role 2 Basic',
          expected_casualty_rate: 'MODERATE',
          evacuation_priority: 'ROUTINE',
          medical_support_source: 'ORGANIC',
          evacuation_factors: {
            ground_route_quality: 'FAIR',
            helicopter_availability: getHelicopterAvailability(brigade.evacuation_assets?.helicopters || 0),
            distance_to_role2e: 15,
            evacuation_under_fire: false
          }
        },
        
        // Logistics (preserve if exists, otherwise create defaults)
        logistics: brigade.logistics || {
          supply_route: `MSR_${String.fromCharCode(65 + Math.floor(index / 3))}`,
          supply_priority: 'MODERATE',
          ammunition_consumption_rate: 'MODERATE',
          fuel_consumption_rate: 'MODERATE',
          water_requirement: 'STANDARD',
          resupply_schedule: {
            ammunition: 'DAILY',
            fuel: 'EVERY_3_DAYS',
            rations: 'EVERY_3_DAYS',
            water: 'DAILY'
          }
        },
        
        // Modifiers (preserve if exists, otherwise calculate)
        modifiers: brigade.modifiers || {
          casualty_modifier: 1.0,
          environmental_modifier: 1.0,
          national_standard_modifier: getNationalStandardModifier(brigade.nationalStandard || 'NATO'),
          force_quality_modifier: calculateForceQualityMultiplier(forceQuality),
          operational_state_modifier: getBattleCasualtyMultiplier(autoState),
          combined_modifier: 1.0
        },
        
        // Force quality (always recalculate)
        force_quality: forceQuality,
        force_quality_inherited_from: inherited_from,
        
        // NOTE: national_standard + nationalStandard written earlier (BUG-163 fix).
        // sub_units, composition_source, and ALL other Tab 1 fields
        // are automatically preserved by the spread operator (...brigade)
        // No need to manually list them! This is the power of v7.0 refactor.
      };
    });
  });

  // 2026-10-22: sectors of the front for the enemy forces: the k-th frontage slice of each division ('Division 1 / Sector 2'), from the brigade frontage
  const enemySectors = useMemo(() => buildSectors((brigadeConfigs || [])
    .filter((c) => c.frontage_status?.holds_frontage !== false)
    .map((c) => ({
      id: c.id || c.brigade_id,
      name: c.brigade_name || c.name,
      division_id: c.parent_division_id,
      division_name: c.parent_division_name,
      division_number: c.parent_division_number,
      start_km: c.position?.frontage_start_km,
      end_km: c.position?.frontage_end_km
    }))), [brigadeConfigs]);
  const enemyBrigades = useMemo(() => (brigadeConfigs || []).map((c) => ({ id: c.id || c.brigade_id, name: c.brigade_name || c.name, strength: c.strength || c.calculated_strength || 0 })).filter((b) => b.strength > 0), [brigadeConfigs]);
  
  // ============================================================================
  // UPDATE FUNCTIONS (ENHANCED FOR OPERATIONAL STATE)
  // ============================================================================
  
  const updateBrigadeConfig = (brigadeId, updates) => {
    setBrigadeConfigs(prevConfigs => 
      prevConfigs.map(config => 
        config.brigade_id === brigadeId 
          ? { ...config, ...updates }
          : config
      )
    );
  };
  
  /**
   * Update operational state (Task 2.1 - NEW)
   */
  const updateBrigadeOperationalState = (brigadeId, newState, isOverride = true) => {
    setBrigadeConfigs(prevConfigs =>
      prevConfigs.map(config => {
        if (config.brigade_id !== brigadeId) return config;
        
        return {
          ...config,
          operational_state: newState,
          operational_state_override: isOverride,
          modifiers: {
            ...config.modifiers,
            operational_state_modifier: getBattleCasualtyMultiplier(newState)
          }
        };
      })
    );
  };
  
  /**
   * Update brigade position and auto-update operational state (Task 2.1 - NEW)
   */
  const updateBrigadePositionWithState = (brigadeId, newPositionKm) => {
    setBrigadeConfigs(prevConfigs =>
      prevConfigs.map(config => {
        if (config.brigade_id !== brigadeId) return config;
        
        const newAutoState = getOperationalStateFromPosition(newPositionKm, config.unit_type);
        
        // Only auto-update state if not manually overridden
        const newState = config.operational_state_override ? config.operational_state : newAutoState;
        
        return {
          ...config,
          position: {
            ...config.position,
            position_feba_km: newPositionKm,
            manual_lock: true
          },
          operational_state: newState,
          modifiers: {
            ...config.modifiers,
            operational_state_modifier: getBattleCasualtyMultiplier(newState)
          }
        };
      })
    );
  };
  
  const updateBrigadePosition = (brigadeId, field, value) => {
    setBrigadeConfigs(prevConfigs =>
      prevConfigs.map(config =>
        config.brigade_id === brigadeId
          ? {
              ...config,
              position: {
                ...config.position,
                [field]: value,
                ...(field === 'frontage_start_km' ? {
                  frontage_end_km: value + config.position.frontage_width_km
                } : {}),
                ...(field === 'frontage_width_km' ? {
                  frontage_end_km: config.position.frontage_start_km + value
                } : {}),
                ...(field === 'frontage_end_km' ? {
                  frontage_width_km: value - config.position.frontage_start_km
                } : {}),
                ...(field !== 'manual_lock' ? { manual_lock: true } : {})
              }
            }
          : config
      )
    );
  };
  
  const updateBrigadeOperation = (brigadeId, field, value) => {
    const isChangingMissionType = field === 'mission_type';
    
    setBrigadeConfigs(prevConfigs => {
      return prevConfigs.map(config => {
        if (config.brigade_id === brigadeId) {
          const updatedConfig = {
            ...config,
            operation: { ...config.operation, [field]: value }
          };
          
          if (isChangingMissionType && value === 'RESERVE') {
            updatedConfig.position = {
              ...updatedConfig.position,
              position_feba_km: Math.abs(RESERVE_POSITIONS.DISTANT_RESERVE),
              frontage_start_km: null,
              frontage_end_km: null,
              frontage_width_km: 0,
              reserve_position: 'DISTANT'
            };
            // Auto-assign TRUE_RESERVE state
            updatedConfig.operational_state = 'TRUE_RESERVE';
            updatedConfig.operational_state_override = false;
            updatedConfig.modifiers = {
              ...updatedConfig.modifiers,
              operational_state_modifier: getBattleCasualtyMultiplier('TRUE_RESERVE')
            };
          }
          
          return updatedConfig;
        }
        return config;
      });
    });
  };
  
  const updateBrigadeEnvironment = (brigadeId, field, value) => {
    setBrigadeConfigs(prevConfigs =>
      prevConfigs.map(config =>
        config.brigade_id === brigadeId
          ? { ...config, environment: { ...config.environment, [field]: value } }
          : config
      )
    );
  };
  
  const updateBrigadeMedical = (brigadeId, field, value) => {
    setBrigadeConfigs(prevConfigs =>
      prevConfigs.map(config =>
        config.brigade_id === brigadeId
          ? { ...config, medical: { ...config.medical, [field]: value } }
          : config
      )
    );
  };
  
  const updateBrigadeLogistics = (brigadeId, field, value) => {
    setBrigadeConfigs(prevConfigs =>
      prevConfigs.map(config =>
        config.brigade_id === brigadeId
          ? { ...config, logistics: { ...config.logistics, [field]: value } }
          : config
      )
    );
  };
  
  const getBrigadeConfig = (brigadeId) => {
    return brigadeConfigs.find(config => config.brigade_id === brigadeId);
  };
  
  const getAdjacentBrigadeName = (brigadeId) => {
    if (!brigadeId) return null;
    const config = getBrigadeConfig(brigadeId);
    return config ? config.brigade_name : null;
  };
  
  // ============================================================================
  // PHASE 2: PHASE CASCADE FUNCTIONS
  // ============================================================================
  
  /**
   * Calculate phase start/end days from durations
   * Ensures phases are sequential with no gaps/overlaps
   */
  const calculatePhaseDays = useCallback((phases) => {
    let currentDay = 1;
    return phases.map(phase => {
      const startDay = currentDay;
      const endDay = currentDay + phase.duration_days - 1;
      currentDay = endDay + 1;
      return {
        ...phase,
        start_day: startDay,
        end_day: endDay
      };
    });
  }, []);
  
  /**
   * Cascade all divisions' phases to their brigades
   * BUG-151 FIX: cascadeDivisionPhasesWithData — version of cascadeDivisionPhasesToBrigades
   * that receives phases directly from the caller instead of reading divisionConfigs state.
   * Avoids stale-closure bug where cascadeDivisionPhasesToBrigades captured the pre-save
   * divisionConfigs → read division.phases=[] → brigade Phases tab showed "0 phases".
   * Called by handleSaveDivisionConfig with the freshly-merged phases array.
   */
  const cascadeDivisionPhasesWithData = useCallback((divisionId, newPhases) => {
    if (!Array.isArray(newPhases) || newPhases.length === 0) return;
    setBrigadeConfigs(prevBrigades =>
      prevBrigades.map(brigade => {
        if (brigade.parent_division_id === divisionId && brigade.uses_division_phases) {
          // ENH-023: apply brigade's own phase_overrides on top of the incoming division phases
          return {
            ...brigade,
            phases: calculatePhaseDays(getEffectivePhases(brigade, newPhases))
          };
        }
        return brigade;
      })
    );
  }, [calculatePhaseDays]);
/**
   * NOTE (v7.11.0): cascadeAllDivisionPhasesToBrigades was removed — it was the
   * only call site removed by BUG-164 fix (direct setBrigadeConfigs replaced setTimeout).
   * cascadeDivisionPhasesToBrigades (below) is still used by toggleDivisionInheritance.
   */
  
  /**
   * Cascade Division phases to brigades in that division that inherit
   */
  const cascadeDivisionPhasesToBrigades = useCallback((divisionId) => {
    
    const division = divisionConfigs.find(d => d.division_id === divisionId);
    if (!division) {
      return;
    }
    
    setBrigadeConfigs(prevBrigades =>
      prevBrigades.map(brigade => {
        if (brigade.parent_division_id === divisionId && brigade.uses_division_phases) {
          // ENH-023: apply brigade's own phase_overrides on top of the division phases
          return {
            ...brigade,
            phases: calculatePhaseDays(getEffectivePhases(brigade, division.phases)) // Deep copy + overrides
          };
        }
        return brigade;
      })
    );
  }, [divisionConfigs, calculatePhaseDays]);
  
  /**
   * Cascade Corps phases to all divisions that inherit
   * PHASE2 FIX: This function is defined LAST because it depends on cascadeAllDivisionPhasesToBrigades
   */
  const cascadeCorpsPhasesToDivisions = useCallback((corpsPhases) => {
    // BUG-164 FIX: Do NOT use setTimeout(cascadeAllDivisionPhasesToBrigades) here.
    // cascadeAllDivisionPhasesToBrigades closes over divisionConfigs at creation time —
    // reads phases=[] on all divisions → brigades receive empty arrays. Same stale
    // closure as BUG-151. Fix: capture inheriting division IDs inside the functional
    // updater (which receives the latest state), then directly update brigades
    // using the in-hand corpsPhases — no closure, no stale state.
    // Works for both CORPS echelon (divisionConfigs has multiple divisions) and
    // DIVISION echelon (divisionConfigs[0] IS the top-level division).
    let inheritingDivisionIds = [];
    let skippedDivisionCount  = 0;
    let skippedBrigadeCount   = 0;
    setDivisionConfigs(prevDivisions => {
      inheritingDivisionIds = prevDivisions
        .filter(d => d.uses_corps_phases)
        .map(d => d.division_id);
      // BUG-S97-011: count divisions that have local overrides and will be skipped
      skippedDivisionCount = prevDivisions.filter(d => !d.uses_corps_phases).length;
      return prevDivisions.map(div => {
        if (div.uses_corps_phases) {
          // ENH-023: apply division's own phase_overrides on top of incoming corps phases
          return {
            ...div,
            phases: calculatePhaseDays(getEffectivePhases(div, corpsPhases))
          };
        }
        return div;
      });
    });

    // Directly cascade to brigades using the in-hand phases — setTimeout(0) only
    // to let React commit the divisionConfigs update first (cosmetic, not required
    // for correctness since brigades read from their own phases, not divisionConfigs).
    setTimeout(() => {
      setBrigadeConfigs(prevBrigades => {
        // BUG-S97-011: count brigades that will be skipped (not inheriting from a cascaded div)
        skippedBrigadeCount = prevBrigades.filter(
          b => !b.uses_division_phases || !inheritingDivisionIds.includes(b.parent_division_id)
        ).length;
        return prevBrigades.map(brigade => {
          if (
            brigade.uses_division_phases &&
            inheritingDivisionIds.includes(brigade.parent_division_id)
          ) {
            // ENH-023: apply brigade's own phase_overrides on top of incoming corps phases
            return {
              ...brigade,
              phases: calculatePhaseDays(getEffectivePhases(brigade, corpsPhases))
            };
          }
          return brigade;
        });
      });
      // BUG-S97-011: surface warning about skipped units — called AFTER setBrigadeConfigs,
      // outside the updater (updaters must be pure — no side-effects allowed inside them).
      if (skippedDivisionCount > 0 || skippedBrigadeCount > 0) {
        const parts = [];
        if (skippedDivisionCount > 0) parts.push(`${skippedDivisionCount} division${skippedDivisionCount > 1 ? 's' : ''}`);
        if (skippedBrigadeCount   > 0) parts.push(`${skippedBrigadeCount} brigade${skippedBrigadeCount > 1 ? 's' : ''}`);
        setCascadeSkipMsg(
          `Cascade warning: ${parts.join(' and ')} have local overrides and were NOT updated. Open each unit\u2019s configuration and click Reset to inherit the new corps phases.`
        );
      }
    }, 0);
  }, [calculatePhaseDays]);
  
  /**
   * SECTION 12 FIX: Helper function to map posture to mission_type
   * Auto-sync mission when posture changes to prevent invalid combinations
   */
  const getMissionFromPosture = useCallback((posture) => {
    const postureToMissionType = {
      'ATTACK': 'ATTACK',
      'HASTY_ATTACK': 'ATTACK',
      'DELIBERATE_ATTACK': 'ATTACK',
      // BUG-PHASE-002 FIX: EXPLOITATION posture primary mission IS exploitation, not ATTACK.
      // Mapping to ATTACK caused false mismatch warning when phase correctly had EXPLOITATION mission.
      'EXPLOITATION': 'EXPLOITATION',
      'PURSUIT': 'ATTACK',
      'DEFENSE': 'DEFEND',
      'HASTY_DEFENSE': 'DEFEND',
      'DELIBERATE_DEFENSE': 'DEFEND',
      'DELAY': 'DEFEND',
      'WITHDRAWAL': 'CONSOLIDATION',
      'RETIRE': 'CONSOLIDATION',
      'RESERVE': 'RESERVE',
      'MARCH': 'MOVEMENT',
      'MARCH_TACTICAL': 'MOVEMENT',
      'MARCH_ADMINISTRATIVE': 'MOVEMENT',
      'MARCHING': 'MOVEMENT',
      'TRANSIT': 'MOVEMENT',
      'RECONSTITUTION': 'SUPPORTING',
      'SUPPORT_OPERATIONS': 'SUPPORTING',
      'CONSOLIDATION': 'CONSOLIDATION'
    };
    return postureToMissionType[posture] || 'DEFEND';
  }, []);

  // REMOVED: updateCorpsPhase, addCorpsPhase, deleteCorpsPhase (Lines 4143-4211)
  // REASON: Corps phase management now handled internally by CorpsConfigDialog.jsx
  // The dialog manages its own state and passes final config to handleSaveCorpsConfig
  // These functions were created for inline editing but became obsolete when dialog was built
  
  /**
   * Handle Division phase inheritance toggle
   * READY FOR UI: This function is fully implemented and ready to be called
   * by the "Inherit from Corps" switch in the Division Config Dialog (to be added in next session).
   * Called when user toggles between inheriting Corps phases vs custom phases
   */
  const toggleDivisionInheritance = useCallback((divisionId, useCorpsPhases) => {
    setDivisionConfigs(prevDivisions =>
      prevDivisions.map(div => {
        if (div.division_id === divisionId) {
          if (useCorpsPhases) {
            // Switching TO inherit: apply division's phase_overrides on top of Corps phases
            // ENH-023: getEffectivePhases preserves any existing per-phase overrides
            return {
              ...div,
              uses_corps_phases: true,
              phases: calculatePhaseDays(getEffectivePhases(div, corpsPhases))
            };
          } else {
            // Switching TO custom: Keep current phases or copy Corps phases as starting point
            return {
              ...div,
              uses_corps_phases: false,
              phases: div.phases.length > 0 ? div.phases : calculatePhaseDays(corpsPhases.map(p => ({...p})))
            };
          }
        }
        return div;
      })
    );
    
    // Cascade to brigades
    setTimeout(() => cascadeDivisionPhasesToBrigades(divisionId), 100);
  }, [corpsPhases, calculatePhaseDays, cascadeDivisionPhasesToBrigades]);
  
  /**
   * Handle Brigade phase inheritance toggle
   * READY FOR UI: This function is fully implemented and ready to be called
   * by the "Inherit from Division" switch in the Brigade Phase Tab (to be added in next session).
   * Called when user toggles between inheriting Division phases vs custom phases
   */
  const toggleBrigadeInheritance = useCallback((brigadeId, useDivisionPhases) => {
    const brigade = brigadeConfigs.find(b => b.brigade_id === brigadeId);
    if (!brigade) return;
    
    const division = divisionConfigs.find(d => d.division_id === brigade.parent_division_id);
    
    setBrigadeConfigs(prevBrigades =>
      prevBrigades.map(b => {
        if (b.brigade_id === brigadeId) {
          if (useDivisionPhases) {
            // Switching TO inherit: apply brigade's phase_overrides on top of Division phases
            // ENH-023: getEffectivePhases preserves any existing per-phase overrides
            return {
              ...b,
              uses_division_phases: true,
              phases: division ? calculatePhaseDays(getEffectivePhases(b, division.phases)) : []
            };
          } else {
            // Switching TO custom: Keep current phases or copy Division phases as starting point
            return {
              ...b,
              uses_division_phases: false,
              phases: b.phases.length > 0 ? b.phases : (division ? calculatePhaseDays(division.phases.map(p => ({...p}))) : [])
            };
          }
        }
        return b;
      })
    );
    
    // Update division's override list
    if (division) {
      setDivisionConfigs(prevDivisions =>
        prevDivisions.map(d => {
          if (d.division_id === division.division_id) {
            const overrides = useDivisionPhases
              ? d.brigade_overrides.filter(id => id !== brigadeId)
              : [...d.brigade_overrides, brigadeId];
            return {
              ...d,
              brigade_overrides: overrides
            };
          }
          return d;
        })
      );
    }
  }, [brigadeConfigs, divisionConfigs, calculatePhaseDays]);
  
  /**
   * ============================================================================
   * PHASE 2 FUNCTION USAGE STATUS
   * ============================================================================
   * 
   *  HANDLER FUNCTIONS (Added above, ready for use):
   *    - handleCorpsPersonnelChange() - Updates Corps personnel
   *    - handleCorpsOperationalStateChange() - Updates Corps operational state
   *    - handleCorpsPositionChange() - Updates Corps position
   *    - handleEditCorpsConfig() - Opens Corps config dialog
   *    - handleCloseCorpsDialog() - Closes Corps config dialog
   *    - handleSaveCorpsConfig() - Saves and cascades Corps config
   *    - handleEditDivisionConfig() - Opens Division config dialog
   *    - handleCloseDivisionDialog() - Closes Division config dialog
   *    - handleSaveDivisionConfig() - Saves and cascades Division config
   * 
   *  PHASE MANAGEMENT FUNCTIONS (Ready but not called yet - awaiting UI):
   *    These functions are FULLY IMPLEMENTED and PRODUCTION-READY.
   *    They will be called by UI components to be added in next session:
   * 
   *    - updateCorpsPhase() - Will be called by Corps Phase Editor
   *      Usage: When user edits phase duration, posture, mission, or description
   * 
   *    - addCorpsPhase() - Will be called by "Add Phase" button
   *      Usage: When user clicks to add new Corps phase
   * 
   *    - deleteCorpsPhase() - Will be called by phase delete button
   *      Usage: When user clicks trash icon to delete a phase
   * 
   *    - toggleDivisionInheritance() - Will be called by Division dialog switch
   *      Usage: When user toggles "Inherit from Corps" switch in Division Config
   * 
   *    - toggleBrigadeInheritance() - Will be called by Brigade dialog switch
   *      Usage: When user toggles "Inherit from Division" switch in Brigade Config
   * 
   *  WHY THESE FUNCTIONS EXIST WITHOUT CALLERS:
   *    Phase 2 implementation is split into 2 sessions:
   *    - Session 1 (COMPLETE): Backend integration - state, functions, save/load
   *    - Session 2 (PENDING): UI components - dialogs, editors, switches
   * 
   *    All phase management functions are complete and tested via save/load.
   *    They will be wired up to UI components in Session 2.
   * 
   *  CASCADE FUNCTIONS (Fully functional):
   *    - calculatePhaseDays() -  Used by all phase operations
   *    - cascadeCorpsPhasesToDivisions() -  Used by Corps phase changes
   *    - cascadeDivisionPhasesToBrigades() -  Used by Division config saves
   *    - cascadeAllDivisionPhasesToBrigades() -  Used by Corps cascade
   * 
   *  SAVE/LOAD STATUS:
   *    - Corps config with phases:  Saves correctly
   *    - Division configs:  Saves correctly
   *    - Brigade phase inheritance:  Saves correctly
   *    - All Phase 2 data persists to scenario.operation_parameters
   * 
   * ============================================================================
   */
  
  // ============================================================================
  // PHASE 2: CORPS/DIVISION CONFIGURATION HANDLERS
  // ============================================================================
  
  /**
   * Handle Corps Personnel change
   * Used by future Corps Configuration Dialog
   */
  
  // REMOVED: handleCorpsPersonnelChange, handleCorpsOperationalStateChange, handleCorpsPositionChange
  // REASON: CorpsConfigDialog manages state internally, only calls handleSaveCorpsConfig
  // Individual field handlers are unused - dialog saves all config at once
  
  /**
   * Open Corps Configuration Dialog
   * Will be called by future "Edit Corps Config" button
   */
  const handleEditCorpsConfig = useCallback(() => {
    setEditingCorps(true);
  }, []);
  
  /**
   * Close Corps Configuration Dialog
   * Will be called by dialog Cancel/Close buttons
   */
  const handleCloseCorpsDialog = useCallback(() => {
    setEditingCorps(false);
  }, []);
  
  /**
   * Save Corps Configuration
   * Will be called by dialog Save button
   */
  const handleSaveCorpsConfig = useCallback((config) => {
    setCorpsPersonnel(config.personnel);
    setCorpsOperationalState(config.operational_state);
    setCorpsPositionFeba(config.position_feba_km);
    setCorpsPhases(config.phases);
    setEditingCorps(false);


    // Auto-cascade to divisions and brigades
    if (config.phases && config.phases.length > 0) {
      cascadeCorpsPhasesToDivisions(config.phases);
    }
  }, [cascadeCorpsPhasesToDivisions]);

  // OBS-S109-002 FIX: Force Cascade — clears all division/brigade phase_overrides and
  // re-enables full inheritance, then applies current corps phases to all units.
  // Used for CoA B configuration: ensures subordinate units reflect the new corps posture
  // instead of retaining CoA A overrides that the normal cascade skips.
  const handleForceCascade = useCallback(() => {
    if (!corpsPhases || corpsPhases.length === 0) {
      setForceCascadeConfirm(false);
      return;
    }
    // Reset all divisions: clear overrides, re-enable corps inheritance, apply corps phases
    setDivisionConfigs(prev => prev.map(div => ({
      ...div,
      uses_corps_phases: true,
      phase_overrides:   {},
      phases:            calculatePhaseDays(corpsPhases),
    })));
    // Reset all brigades: clear overrides, re-enable division inheritance, apply corps phases.
    // setTimeout mirrors cascadeCorpsPhasesToDivisions pattern — lets divisionConfigs commit first.
    setTimeout(() => {
      setBrigadeConfigs(prev => prev.map(brig => ({
        ...brig,
        uses_division_phases: true,
        phase_overrides:      {},
        phases:               calculatePhaseDays(corpsPhases),
      })));
      setCascadeSkipMsg(null); // all units updated — no skips remain
    }, 0);
    setForceCascadeConfirm(false);
  }, [corpsPhases, calculatePhaseDays]);
  
  /**
   * Open Division Configuration Dialog
   * Will be called by future "Edit Division X Config" button
   * @param {string} divisionId - ID of division to edit
   */
  const handleEditDivisionConfig = useCallback((divisionId) => {
    setEditingDivision(divisionId);
  }, []);
  
  /**
   * Close Division Configuration Dialog
   * Will be called by dialog Cancel/Close buttons
   */
  const handleCloseDivisionDialog = useCallback(() => {
    setEditingDivision(null);
  }, []);
  
  /**
   * Save Division Configuration
   * Will be called by dialog Save button
   */
  const handleSaveDivisionConfig = useCallback((divisionId, config) => {
    // Compute merged division first so we can pass phases directly to the cascade helper.
    // BUG-151 FIX: DO NOT use setTimeout(cascadeDivisionPhasesToBrigades) here.
    // cascadeDivisionPhasesToBrigades closes over divisionConfigs at creation time and
    // reads stale phases (pre-save). Pass the freshly-merged phases directly to
    // cascadeDivisionPhasesWithData which never touches divisionConfigs state.
    setDivisionConfigs(prevDivisions =>
      prevDivisions.map(div => {
        if (div.division_id === divisionId) {
          return { ...div, ...config };
        }
        return div;
      })
    );
    setEditingDivision(null);

    // Cascade using caller-supplied phases — no closure, no stale state.
    if (config.phases && config.phases.length > 0) {
      cascadeDivisionPhasesWithData(divisionId, config.phases);
    }

    // OBS-S102-003: When this division is saved as MAIN_EFFORT for a phase,
    // auto-suggest SUPPORTING for all sibling divisions (only if not already set).
    // OBS-S102-004: Also suggest triangle brigade roles within this division
    // (MAIN_EFFORT → SUPPORTING → RESERVE for combat; SUPPORTING for CS; EoF for CSS).
    const mainEffortPhaseIndices = Object.entries(config.phase_overrides || {})
      .filter(([, ov]) => ov.division_role === 'MAIN_EFFORT')
      .map(([idx]) => Number(idx));

    if (mainEffortPhaseIndices.length > 0) {
      // OBS-S102-003: cascade SUPPORTING to sibling divisions
      setDivisionConfigs(prevDivisions =>
        prevDivisions.map(div => {
          if (div.division_id === divisionId) return div; // skip the division we just saved
          const newOverrides = { ...(div.phase_overrides || {}) };
          let changed = false;
          mainEffortPhaseIndices.forEach(phaseIdx => {
            if (!newOverrides[phaseIdx]?.division_role) {
              newOverrides[phaseIdx] = { ...(newOverrides[phaseIdx] || {}), division_role: 'SUPPORTING' };
              changed = true;
            }
          });
          return changed ? { ...div, phase_overrides: newOverrides } : div;
        })
      );

      // OBS-S102-004: triangle formation suggestion for brigades in this division.
      // Triangle: combat[0]=MAIN_EFFORT, combat[1]=SUPPORTING, combat[2+]=RESERVE
      //           COMBAT_SUPPORT (arty/ADA/engineer) → SUPPORTING
      //           CSS (log/signal/med/aviation/HQ)  → ECONOMY_OF_FORCE
      const TRIANGLE_ROLES = ['MAIN_EFFORT', 'SUPPORTING', 'RESERVE'];
      setBrigadeConfigs(prevBrigades => {
        const myBrigades = prevBrigades.filter(b => b.parent_division_id === divisionId);
        const combatBrigades = myBrigades.filter(b => {
          const cat = getUnitTypeDoctrine(b.unit_type || b.brigade_type || 'MECHANIZED').category;
          return cat === 'COMBAT';
        });
        return prevBrigades.map(brig => {
          if (brig.parent_division_id !== divisionId) return brig;
          const cat = getUnitTypeDoctrine(brig.unit_type || brig.brigade_type || 'MECHANIZED').category;
          const combatIdx = combatBrigades.findIndex(b => b.brigade_id === brig.brigade_id);
          const newOverrides = { ...(brig.phase_overrides || {}) };
          let changed = false;
          mainEffortPhaseIndices.forEach(phaseIdx => {
            if (!newOverrides[phaseIdx]?.brigade_role) {
              let suggestedRole;
              if (cat === 'COMBAT') {
                suggestedRole = TRIANGLE_ROLES[combatIdx] ?? 'RESERVE';
              } else if (cat === 'COMBAT_SUPPORT') {
                suggestedRole = 'SUPPORTING';
              } else {
                suggestedRole = 'ECONOMY_OF_FORCE'; // CSS
              }
              newOverrides[phaseIdx] = { ...(newOverrides[phaseIdx] || {}), brigade_role: suggestedRole };
              changed = true;
            }
          });
          return changed ? { ...brig, phase_overrides: newOverrides } : brig;
        });
      });
    }
  }, [cascadeDivisionPhasesWithData]);
  
  // ============================================================================
  // SYNC DETECTION
  // ============================================================================
  
  // CRIT-002 FIX: Auto-populate brigadeConfigs when brigades array becomes available
  // This handles the case where:
  //   1. Tab2 mounts with empty brigades (Tab1 not loaded yet)
  //   2. brigadeConfigs initializes to []
  //   3. Tab1 data arrives, brigades gets populated
  //   4. WITHOUT THIS FIX: brigadeConfigs stays empty  calculator fails
  useEffect(() => {
    // Only auto-populate if:
    // 1. brigadeConfigs is empty
    // 2. brigades has items
    // 3. No saved config exists (don't overwrite user's work)
    if (brigadeConfigs.length === 0 && 
        brigades.length > 0 && 
        !scenario?.operation_parameters?.brigade_configs) {
      
      
      // v7.6.0: Use division-aware frontage map for auto-populate path.
      const autoPopFrontageMap = computeDivisionFrontageMap(brigades, totalFrontage);

      const newConfigs = brigades.map((brigade, index) => {
        const parentDivision = divisions.find(d => d.id === brigade.parent_division_id);
        const { resolved: forceQuality, inherited_from } = resolveForceQuality(brigade, forceBuilderResult);
        
        const extractedPosture = brigade.tactical_in_parent?.posture || 
                                 parentDivision?.tactical_in_parent?.posture ||
                                 forceBuilderResult?.corps?.tactical_in_parent?.posture ||
                                 'DELIBERATE_DEFENSE';
        
        const postureToMissionType = {
          'ATTACK': 'ATTACK', 'HASTY_ATTACK': 'ATTACK', 'DELIBERATE_ATTACK': 'ATTACK',
          'EXPLOITATION': 'EXPLOITATION', 'PURSUIT': 'ATTACK',
          'DEFENSE': 'DEFEND', 'HASTY_DEFENSE': 'DEFEND', 'DELIBERATE_DEFENSE': 'DEFEND',
          'DELAY': 'DEFEND', 'WITHDRAWAL': 'CONSOLIDATION', 'RETIRE': 'CONSOLIDATION',
          'RESERVE': 'RESERVE', 'MARCH': 'MOVEMENT', 'MARCHING': 'MOVEMENT',
          'TRANSIT': 'MOVEMENT', 'RECONSTITUTION': 'SUPPORTING'
        };
        const missionType = postureToMissionType[extractedPosture] || 'DEFEND';
        
        const extractedNationalStandard = brigade.nationalStandard || 
                                          parentDivision?.nationalStandard ||
                                          forceBuilderResult?.corps?.nationalStandard ||
                                          'NATO';
        
        const unitType = brigade.unit_type || brigade.brigade_type || parentDivision?.unit_type || 'MECHANIZED';
        const frontageStatus = getBrigadeFrontageStatus(unitType, extractedPosture, missionType);
        
        const doctrine = getUnitTypeDoctrine(unitType);
        const positionKm = frontageStatus.holdsFrontage ? 5 : doctrine.default_position;
        const autoState = getOperationalStateFromPosition(positionKm, unitType);
        
        
        return {
          ...brigade,  // ✅ FIX: Spread existing brigade to preserve Tab1 data (sub_units, breakdown, etc)
          
          brigade_id: brigade.id,
          brigade_number: brigade.brigade_number || (index + 1),
          brigade_name: brigade.name || `Brigade ${index + 1}`,
          parent_division_id: brigade.parent_division_id,
          parent_division_number: parentDivision?.division_number || Math.floor(index / 3) + 1,
          // BUG-163 FIX: Both naming conventions — nsColors (camelCase) + calculator (snake_case)
          nationalStandard: extractedNationalStandard,
          national_standard: extractedNationalStandard,
          unit_type: unitType,
          strength: brigade.strength || getStrengthByStandard(extractedNationalStandard),
          evacuation_assets: brigade.evacuation_assets || {
            wheeled_ambulances: 8, tracked_ambulances: 6, helicopters: 0, stretcher_teams: 13
          },
          force_quality: forceQuality,
          force_quality_inherited_from: inherited_from,
          operational_state: autoState,
          operational_state_override: false,
          // NEW: Phase inheritance (Phase 2)
          uses_division_phases: true,
          phases: [],
          phase_overrides: {},
          frontage_status: {
            holds_frontage: frontageStatus.holdsFrontage,
            reason: frontageStatus.reason,
            rear_area_modifier: frontageStatus.casualty_modifier
          },
          // v7.6.0: Division-aware position for auto-populate path
          position: (() => {
            const brigId = brigade.id || brigade.brigade_id;
            const divPos = autoPopFrontageMap[brigId];
            const sectorDesig = divPos
              ? (divPos.indexInDiv === 0 ? 'LEFT'
                 : divPos.indexInDiv === divPos.divCount - 1 ? 'RIGHT'
                 : 'CENTER')
              : 'REAR';
            return {
              position_feba_km: positionKm,
              frontage_start_km: frontageStatus.holdsFrontage ? (divPos?.start ?? null) : null,
              frontage_end_km:   frontageStatus.holdsFrontage ? (divPos?.end   ?? null) : null,
              frontage_width_km: frontageStatus.holdsFrontage && divPos
                ? Math.round((divPos.end - divPos.start) * 10) / 10
                : 0,
              sector_designation: frontageStatus.holdsFrontage ? sectorDesig : 'REAR',
              adjacent_left:  frontageStatus.holdsFrontage ? (divPos?.leftNeighborId  ?? null) : null,
              adjacent_right: frontageStatus.holdsFrontage ? (divPos?.rightNeighborId ?? null) : null,
              manual_lock: false,
              reserve_position: !frontageStatus.holdsFrontage && missionType === 'RESERVE' ? 'DISTANT' : null
            };
          })(),
          operation: {
            mission_type: missionType,
            mission_statement: `Brigade ${index + 1} conducts ${missionType.toLowerCase()} operations in assigned sector`,
            start_day: 1, start_hour: 6,
            end_day: totalDuration, end_hour: 18,
            operational_posture: extractedPosture,
            engagement_level: 'PREPARED_ENGAGEMENT',
            tempo: 'DELIBERATE',
            synchronization: { sync_with_brigade: null, support_from: null, timing_relationship: 'INDEPENDENT' }
          },
          environment: {
            terrain: 'MIXED',
            terrain_details: { dominant_type: 'MIXED', obstacles: [], key_terrain: [], avenues_of_approach: 'MODERATE' },
            weather: 'CLEAR', weather_by_day: {},
            threat_level: 'MODERATE_THREAT',
            enemy_disposition: 'PREPARED_DEFENSE',
            visibility: { day: 'GOOD', night: 'LIMITED' },
            technology_env: forceQuality.technology_env || 'DRONE_CONTESTED',
            technology_env_override: false
          },
          medical: {
            medevac_priority: index === 0 ? 'HIGH' : 'NORMAL',
            expected_casualty_rate: 1.5,
            medical_notes: ''
          }
        };
      });
      
      setBrigadeConfigs(newConfigs);
    }
  }, [brigades, brigadeConfigs.length, scenario?.operation_parameters?.brigade_configs,
      totalFrontage, divisions, forceBuilderResult, totalDuration]);

  // BUG-NEXT-010 FIX: Sync brigadeConfigs when brigades are added/removed in Tab 1 after
  // initial Tab 2 load. The CRIT-002 effect above only fires when brigadeConfigs is empty
  // (guard: brigadeConfigs.length === 0). This effect handles incremental add/remove by
  // using functional setState — brigadeConfigs accessed via `prev`, NOT in dep array,
  // so no infinite-loop risk. setBrigadeConfigs(prev => prev) when nothing changed → React
  // bails out with no re-render.
  useEffect(() => {
    if (brigades.length === 0) return;
    setBrigadeConfigs(prev => {
      const configuredIds = new Set(prev.map(c => c.brigade_id || c.id));
      const incomingIds   = new Set(brigades.map(b => b.id));
      const hasAdded   = brigades.some(b => !configuredIds.has(b.id));
      const hasRemoved = prev.some(c => !incomingIds.has(c.brigade_id || c.id));
      if (!hasAdded && !hasRemoved) return prev;

      // Preserve config for existing brigades, drop configs for deleted brigades
      const kept = prev.filter(c => incomingIds.has(c.brigade_id || c.id));

      // Build default config for each new brigade (same logic as CRIT-002 body)
      const autoPopFrontageMap = computeDivisionFrontageMap(brigades, totalFrontage);
      const newEntries = brigades
        .filter(b => !configuredIds.has(b.id))
        .map((brigade) => {
          const index = brigades.indexOf(brigade);
          const parentDivision = divisions.find(d => d.id === brigade.parent_division_id);
          const { resolved: forceQuality, inherited_from } = resolveForceQuality(brigade, forceBuilderResult);
          const extractedPosture = brigade.tactical_in_parent?.posture ||
                                   parentDivision?.tactical_in_parent?.posture ||
                                   forceBuilderResult?.corps?.tactical_in_parent?.posture ||
                                   'DELIBERATE_DEFENSE';
          const postureToMissionType = {
            'ATTACK': 'ATTACK', 'HASTY_ATTACK': 'ATTACK', 'DELIBERATE_ATTACK': 'ATTACK',
            'EXPLOITATION': 'EXPLOITATION', 'PURSUIT': 'ATTACK',
            'DEFENSE': 'DEFEND', 'HASTY_DEFENSE': 'DEFEND', 'DELIBERATE_DEFENSE': 'DEFEND',
            'DELAY': 'DEFEND', 'WITHDRAWAL': 'CONSOLIDATION', 'RETIRE': 'CONSOLIDATION',
            'RESERVE': 'RESERVE', 'MARCH': 'MOVEMENT', 'MARCHING': 'MOVEMENT',
            'TRANSIT': 'MOVEMENT', 'RECONSTITUTION': 'SUPPORTING'
          };
          const missionType = postureToMissionType[extractedPosture] || 'DEFEND';
          const extractedNationalStandard = brigade.nationalStandard ||
                                            parentDivision?.nationalStandard ||
                                            forceBuilderResult?.corps?.nationalStandard ||
                                            'NATO';
          const unitType = brigade.unit_type || brigade.brigade_type || parentDivision?.unit_type || 'MECHANIZED';
          const frontageStatus = getBrigadeFrontageStatus(unitType, extractedPosture, missionType);
          const doctrine = getUnitTypeDoctrine(unitType);
          const positionKm = frontageStatus.holdsFrontage ? 5 : doctrine.default_position;
          const autoState = getOperationalStateFromPosition(positionKm, unitType);
          const brigId = brigade.id || brigade.brigade_id;
          const divPos = autoPopFrontageMap[brigId];
          const sectorDesig = divPos
            ? (divPos.indexInDiv === 0 ? 'LEFT'
               : divPos.indexInDiv === divPos.divCount - 1 ? 'RIGHT'
               : 'CENTER')
            : 'REAR';
          return {
            ...brigade,
            brigade_id: brigade.id,
            brigade_number: brigade.brigade_number || (index + 1),
            brigade_name: brigade.name || `Brigade ${index + 1}`,
            parent_division_id: brigade.parent_division_id,
            parent_division_number: parentDivision?.division_number || Math.floor(index / 3) + 1,
            nationalStandard: extractedNationalStandard,
            national_standard: extractedNationalStandard,
            unit_type: unitType,
            strength: brigade.strength || getStrengthByStandard(extractedNationalStandard),
            evacuation_assets: brigade.evacuation_assets || {
              wheeled_ambulances: 8, tracked_ambulances: 6, helicopters: 0, stretcher_teams: 13
            },
            force_quality: forceQuality,
            force_quality_inherited_from: inherited_from,
            operational_state: autoState,
            operational_state_override: false,
            uses_division_phases: true,
            phases: [],
            phase_overrides: {},
            frontage_status: {
              holds_frontage: frontageStatus.holdsFrontage,
              reason: frontageStatus.reason,
              rear_area_modifier: frontageStatus.casualty_modifier
            },
            position: {
              position_feba_km: positionKm,
              frontage_start_km: frontageStatus.holdsFrontage ? (divPos?.start ?? null) : null,
              frontage_end_km:   frontageStatus.holdsFrontage ? (divPos?.end   ?? null) : null,
              frontage_width_km: frontageStatus.holdsFrontage && divPos
                ? Math.round((divPos.end - divPos.start) * 10) / 10 : 0,
              sector_designation: frontageStatus.holdsFrontage ? sectorDesig : 'REAR',
              adjacent_left:  frontageStatus.holdsFrontage ? (divPos?.leftNeighborId  ?? null) : null,
              adjacent_right: frontageStatus.holdsFrontage ? (divPos?.rightNeighborId ?? null) : null,
              manual_lock: false,
              reserve_position: !frontageStatus.holdsFrontage && missionType === 'RESERVE' ? 'DISTANT' : null
            },
            operation: {
              mission_type: missionType,
              mission_statement: `Brigade ${index + 1} conducts ${missionType.toLowerCase()} operations in assigned sector`,
              start_day: 1, start_hour: 6,
              end_day: totalDuration, end_hour: 18,
              operational_posture: extractedPosture,
              engagement_level: 'PREPARED_ENGAGEMENT',
              tempo: 'DELIBERATE',
              synchronization: { sync_with_brigade: null, support_from: null, timing_relationship: 'INDEPENDENT' }
            },
            environment: {
              terrain: 'MIXED',
              terrain_details: { dominant_type: 'MIXED', obstacles: [], key_terrain: [], avenues_of_approach: 'MODERATE' },
              weather: 'CLEAR', weather_by_day: {},
              threat_level: 'MODERATE_THREAT',
              enemy_disposition: 'PREPARED_DEFENSE',
              visibility: { day: 'GOOD', night: 'LIMITED' },
              technology_env: forceQuality.technology_env || 'DRONE_CONTESTED',
              technology_env_override: false
            },
            medical: {
              medevac_priority: index === 0 ? 'HIGH' : 'NORMAL',
              expected_casualty_rate: 1.5,
              medical_notes: ''
            }
          };
        });
      return [...kept, ...newEntries];
    });
  }, [brigades, totalFrontage, divisions, forceBuilderResult, totalDuration]);

  useEffect(() => {
    if (!brigades || brigades.length === 0) return;

    const currentHash = generateForceHash(brigades);
    
    if (!scenario?.operation_parameters?.brigade_configs) {
      prevForceHashRef.current = currentHash;
      return;
    }
    
    if (currentHash === prevForceHashRef.current) {
      return;
    }
    
    
    const currentBrigadeIds = brigades.map(b => b.id);
    const savedBrigadeIds = brigadeConfigs.map(c => c.brigade_id);
    
    const added = brigades.filter(b => !savedBrigadeIds.includes(b.id));
    const deleted = brigadeConfigs.filter(c => !currentBrigadeIds.includes(c.brigade_id));
    const modified = brigades.filter(b => {
      const config = brigadeConfigs.find(c => c.brigade_id === b.id);
      if (!config) return false;
      return (
        config.parent_division_id !== b.parent_division_id ||
        config.national_standard !== b.nationalStandard ||
        config.brigade_name !== b.name ||
        config.strength !== b.strength ||
        config.unit_type !== b.unit_type
      );
    });
    
    if (added.length > 0 || deleted.length > 0 || modified.length > 0) {
      setSyncChanges({ added, deleted, modified });
      setShowSyncDialog(true);
    }
    
    prevForceHashRef.current = currentHash;
    
  }, [brigades, brigadeConfigs, scenario?.operation_parameters?.brigade_configs]);
  
  // ============================================================================
  // v7.2.2 UNIT_TYPE WATCHDOG: Direct silent update when brigade type changes
  // ============================================================================
  // The main sync detection (above) can miss unit_type changes in certain race
  // conditions (effect sees stale brigades, hash comparison uses wrong snapshot).
  // This watchdog runs independently, compares unit_type directly, and silently
  // patches any mismatch WITHOUT requiring a sync dialog or hash comparison.
  //
  // It also recalculates frontage_status based on the new unit_type and
  // repositions the brigade according to doctrinal default_position.
  // The 100ms delay lets setBrigadeConfigs settle before redistribute runs.
  // ============================================================================
  useEffect(() => {
    if (!brigades || brigades.length === 0 || brigadeConfigs.length === 0) return;
    
    const mismatched = brigades.filter(b => {
      const config = brigadeConfigs.find(c => c.brigade_id === b.id);
      return config && b.unit_type && config.unit_type !== b.unit_type;
    });
    
    if (mismatched.length === 0) return;
    
    setBrigadeConfigs(prev => prev.map(config => {
      const brigade = mismatched.find(b => b.id === config.brigade_id);
      if (!brigade) return config;
      
      const newUnitType = brigade.unit_type;
      const doctrine = getUnitTypeDoctrine(newUnitType);
      const newFrontageStatus = getBrigadeFrontageStatus(
        newUnitType,
        config.operation?.operational_posture || 'DELIBERATE_DEFENSE',
        config.operation?.mission_type || 'DEFEND'
      );
      const newPosition = newFrontageStatus.holdsFrontage
        ? (doctrine.default_position || config.position?.position_feba_km || 5)
        : doctrine.default_position;
      const newState = config.operational_state_override
        ? config.operational_state
        : getOperationalStateFromPosition(newPosition, newUnitType);
      
      return {
        ...config,
        unit_type: newUnitType,
        operational_state: newState,
        frontage_status: {
          holds_frontage: newFrontageStatus.holdsFrontage,
          reason: newFrontageStatus.reason,
          rear_area_modifier: newFrontageStatus.casualty_modifier
        },
        position: {
          ...config.position,
          position_feba_km: newPosition,
          sector_designation: newFrontageStatus.holdsFrontage ? (config.position?.sector_designation || 'CENTER') : 'REAR',
          // Clear manual frontage assignment — will be re-distributed
          frontage_start_km: newFrontageStatus.holdsFrontage ? config.position?.frontage_start_km : null,
          frontage_end_km: newFrontageStatus.holdsFrontage ? config.position?.frontage_end_km : null,
        }
      };
    }));
    
    // Redistribute frontage after patching
    setTimeout(() => {
      if (autoRedistributeFrontageRef.current) autoRedistributeFrontageRef.current(totalFrontage, 'RESET');
    }, 100);
    
  }, [brigades, brigadeConfigs, totalFrontage]);
  
  // ============================================================================
  // PHASE 1 CLEANUP: divisionConfigs state removed - was orphaned
  // Division-level phases will be added in Phase 2
  // ============================================================================
  
  // ============================================================================
  // BRIGADE DIALOG STATE
  // ============================================================================
  
  const [editingBrigade, setEditingBrigade] = useState(null);
  const [brigadeDialogTab, setBrigadeDialogTab] = useState(0);
  
  // ============================================================================
  // VALIDATION
  // ============================================================================
  
  const [validationErrors, setValidationErrors] = useState([]);
  const [validationWarnings, setValidationWarnings] = useState([]);
  const [isValid, setIsValid] = useState(false);

  // V4-014: CoA B detection dialog state
  // mode: 'new_b' = CoA A saved, CoA B not yet; 'update_both' = both saved
  const [coaDetectOpen, setCoaDetectOpen] = useState(false);
  const [coaDetectMode, setCoaDetectMode] = useState('new_b'); // 'new_b' | 'update_both'
  
  const isFirstMount = useRef(true);
  const hasRedistributed = useRef(false);
  
  useEffect(() => {
    isFirstMount.current = false;
  }, []);
  
  // ============================================================================
  // FRONTAGE MANAGEMENT (moved before useEffect that uses it)
  // ============================================================================
  
  // v7.6.0: Division-aware frontage redistribution.
  // Uses computeDivisionFrontageMap so brigades from different divisions are never
  // placed adjacent to each other and each division gets its own contiguous sector.
  const autoRedistributeFrontage = useCallback((totalWidth, mode = 'RESET') => {
    // Build source list filtering out posture/mission ineligible brigades
    const frontageSource = brigadeConfigs.filter(config => {
      const posture = config.operation?.operational_posture || 'DELIBERATE_DEFENSE';
      const mission  = config.operation?.mission_type || 'DEFEND';
      if (!unitTypeHasFrontage(config.unit_type || 'MECHANIZED')) return false;
      if (NON_FRONTAGE_POSTURES.includes(posture)) return false;
      if (NON_FRONTAGE_MISSIONS.includes(mission)) return false;
      if (mode === 'FILL_GAPS' && config.position?.manual_lock) return false;
      return true;
    });

    if (frontageSource.length === 0) return;

    // positionMap: brigadeId -> { start, end, leftNeighborId, rightNeighborId, divisionId }
    // Adjacency is null at division boundaries — no cross-division neighbour links.
    const positionMap = computeDivisionFrontageMap(frontageSource, totalWidth);

    setBrigadeConfigs(prev => prev.map(config => {
      const id = config.brigade_id || config.id;
      const pos = positionMap[id];

      if (!pos) {
        // Non-frontage unit — clear frontage fields
        const unitType = config.unit_type || 'MECHANIZED';
        const posture  = config.operation?.operational_posture || 'DELIBERATE_DEFENSE';
        const mission  = config.operation?.mission_type || 'DEFEND';
        const status  = getBrigadeFrontageStatus(unitType, posture, mission);
        const doctrine = getUnitTypeDoctrine(unitType);
        return {
          ...config,
          frontage_status: {
            holds_frontage: false,
            reason: status.reason,
            rear_area_modifier: status.casualty_modifier
          },
          position: {
            ...config.position,
            position_feba_km: doctrine.default_position,
            frontage_start_km: null,
            frontage_end_km: null,
            frontage_width_km: 0,
            sector_designation: 'REAR'
          }
        };
      }

      if (mode === 'FILL_GAPS' && config.position?.manual_lock) return config;

      // Division-aware sector designation: LEFT/CENTER/RIGHT within the division
      const sectorDesig = pos.indexInDiv === 0 ? 'LEFT'
                        : pos.indexInDiv === pos.divCount - 1 ? 'RIGHT'
                        : 'CENTER';

      return {
        ...config,
        frontage_status: {
          holds_frontage: true,
          reason: 'Combat unit on frontage',
          rear_area_modifier: 1.0
        },
        position: {
          ...config.position,
          position_feba_km: 5,
          frontage_start_km: pos.start,
          frontage_end_km:   pos.end,
          frontage_width_km: Math.round((pos.end - pos.start) * 10) / 10,
          sector_designation: sectorDesig,
          // Adjacency null at division boundaries (no cross-division linking)
          adjacent_left:  pos.leftNeighborId  ?? null,
          adjacent_right: pos.rightNeighborId ?? null,
          manual_lock: mode === 'FILL_GAPS' ? config.position?.manual_lock : false
        },
        operational_state: config.operational_state_override ? config.operational_state : 'COMBAT_ENGAGED',
        modifiers: {
          ...config.modifiers,
          operational_state_modifier: config.operational_state_override
            ? config.modifiers?.operational_state_modifier
            : OPERATIONAL_STATES.COMBAT_ENGAGED.battle_casualty_multiplier
        }
      };
    }));
  }, [brigadeConfigs]);

  // v7.2.2: Keep ref pointing to the latest autoRedistributeFrontage (fresh brigadeConfigs in closure)
  useEffect(() => {
    autoRedistributeFrontageRef.current = autoRedistributeFrontage;
  }, [autoRedistributeFrontage]);
  
  // Auto-redistribute frontage on initial load
  useEffect(() => {
    if (hasRedistributed.current) return;
    if (brigadeConfigs.length === 0) return;
    
    const needsRedistribution = brigadeConfigs.some(config => {
      const frontageStatus = getBrigadeFrontageStatus(
        config.unit_type,
        config.operation?.operational_posture,
        config.operation?.mission_type
      );
      return frontageStatus.holdsFrontage !== config.frontage_status?.holds_frontage;
    });
    
    if (needsRedistribution) {
      autoRedistributeFrontage(totalFrontage, 'RESET');
    }
    
    hasRedistributed.current = true;
  }, [brigadeConfigs, totalFrontage, autoRedistributeFrontage]);
  
  // Validation effect
  useEffect(() => {
    const errors = [];
    const warnings = [];
    
    brigadeConfigs.forEach(config => {
      // Duration validation
      if (config.operation.start_day < 1 || config.operation.start_day > totalDuration) {
        errors.push(`Brigade ${config.brigade_number}: Start day must be between 1 and ${totalDuration}`);
      }
      if (config.operation.end_day < config.operation.start_day) {
        errors.push(`Brigade ${config.brigade_number}: End day must be after start day`);
      }
      if (config.operation.end_day > totalDuration) {
        errors.push(`Brigade ${config.brigade_number}: End day cannot exceed operation duration (${totalDuration} days)`);
      }
      
      // Position validation
      const positionWarnings = validatePositionAgainstDoctrine(
        config.position?.position_feba_km || 0,
        config.unit_type || 'MECHANIZED'
      );
      positionWarnings.forEach(w => {
        if (w.severity === 'error') {
          warnings.push({
            brigade: config.brigade_number,
            message: w.message,
            recommendations: ['Consider adjusting position to doctrine range']
          });
        }
      });
      
      // BUG-T13 fix: warning was ROMANIAN-only and persisted after Tab3 assigns MEDEVAC.
      // Broaden to all standards; Tab3 MEDEVAC assignment does not write back to brigade
      // evacuation_assets, so suppress the warning — Tab3 is the authoritative source for MEDEVAC.
      // (No code needed — warning removed to prevent false positives.)
      
      // Phase validation
      if (config.phases && config.phases.length > 0) {
        const sortedPhases = [...config.phases].sort((a, b) => a.start_day - b.start_day);
        for (let i = 1; i < sortedPhases.length; i++) {
          if (sortedPhases[i].start_day <= sortedPhases[i - 1].end_day) {
            errors.push(`Brigade ${config.brigade_number}: Phases overlap`);
          }
        }
      }
    });
    
    // Check for frontage gaps
    const frontageUnits = brigadeConfigs.filter(c => c.frontage_status?.holds_frontage);
    if (frontageUnits.length > 0) {
      const sorted = [...frontageUnits].sort((a, b) => 
        (a.position?.frontage_start_km || 0) - (b.position?.frontage_start_km || 0)
      );
      
      let lastEnd = 0;
      sorted.forEach(config => {
        const start = config.position?.frontage_start_km || 0;
        if (start > lastEnd + 1) {
          warnings.push(`Frontage gap detected: ${lastEnd}km to ${start}km`);
        }
        lastEnd = config.position?.frontage_end_km || start;
      });
      
      if (lastEnd < totalFrontage - 1) {
        warnings.push(`Uncovered frontage: ${lastEnd}km to ${totalFrontage}km`);
      }
    }
    
    // BUG-T06 / BUG-84-007: cross-brigade MAIN_EFFORT validation (soft advisories only — never blocks save)
    // V5-012: also check phase_overrides[i].brigade_role (per-phase role designation)
    // BUG-INC-custom-role: also check phases[i].brigade_role for custom-phase brigades (was missing)
    const mainEffortBrigades = brigadeConfigs.filter(c =>
      (c.mission_type === 'MAIN_EFFORT') ||
      (c.phases || []).some(p => p.mission_type === 'MAIN_EFFORT') ||
      (c.phases || []).some(p => p.brigade_role === 'MAIN_EFFORT') ||
      Object.values(c.phase_overrides || {}).some(ov => ov.brigade_role === 'MAIN_EFFORT')
    );
    if (mainEffortBrigades.length === 0 && brigadeConfigs.length > 1) {
      warnings.push(t('tab2.no_main_effort_warning'));
    } else if (mainEffortBrigades.length > 1) {
      // BUG-84-007: converging attacks can validly have >1 Main Effort — soft advisory only
      warnings.push(t('tab2.multi_main_effort_warning', { count: mainEffortBrigades.length }));
    }

    // BUG-84-007: Per-phase advisory — check each phase independently for role assignment consistency
    const phaseRoleMap = {}; // phaseIndex → { mainEffort: [], set: [] }
    brigadeConfigs.forEach(c => {
      Object.entries(c.phase_overrides || {}).forEach(([phaseIdx, ov]) => {
        if (!phaseRoleMap[phaseIdx]) phaseRoleMap[phaseIdx] = { mainEffort: [], set: [] };
        if (ov.brigade_role === 'MAIN_EFFORT') phaseRoleMap[phaseIdx].mainEffort.push(c.brigade_id);
        if (ov.brigade_role != null) phaseRoleMap[phaseIdx].set.push(c.brigade_id);
      });
    });
    Object.entries(phaseRoleMap).forEach(([phaseIdx, data]) => {
      const phaseLabel = `Phase ${Number(phaseIdx) + 1}`;
      if (data.mainEffort.length > 1) {
        warnings.push(`${phaseLabel}: ${data.mainEffort.length} brigades designated MAIN EFFORT — confirm converging attack intent.`);
      }
      const unset = brigadeConfigs.length - data.set.length;
      if (data.set.length > 0 && unset > 0) {
        warnings.push(`${phaseLabel}: ${unset} brigade${unset > 1 ? 's' : ''} without phase role — will use the operational posture configured at Tab 1 (force structure) level.`);
      }
    });

    // OBS-S96-002: Doctrinal guard — CSS brigades should not receive combat postures
    // Combat postures assigned to logistics/medical/signal/aviation brigades are doctrinally
    // invalid. These units are CSS (combat service support) and can only be assigned
    // Economy of Force, Reserve, or Support postures.
    const COMBAT_POSTURES = new Set([
      'ATTACK', 'HASTY_ATTACK', 'DELIBERATE_ATTACK', 'RIVER_CROSSING',
      'DELIBERATE_OFFENSE', 'EXPLOITATION', 'PURSUIT'
    ]);
    const CSS_CATEGORIES = new Set(['CSS']);
    brigadeConfigs.forEach(c => {
      const doctrine = getUnitTypeDoctrine(c.unit_type || 'MECHANIZED');
      if (!CSS_CATEGORIES.has(doctrine.category)) return;
      // Check corps-level phase postures (inherited by CSS brigade unless overridden)
      corpsPhases.forEach((phase, idx) => {
        const ov = c.phase_overrides?.[idx];
        const effectivePosture = ov?.operational_posture ?? phase.operational_posture;
        if (effectivePosture && COMBAT_POSTURES.has(effectivePosture)) {
          const unitLabel = c.name || c.brigade_id || `Brigade ${c.brigade_number}`;
          const phaseLabel = `Phase ${phase.phase_number || idx + 1}`;
          warnings.push(`${unitLabel} (${c.unit_type}) is a CSS unit assigned ${effectivePosture} posture in ${phaseLabel}. CSS units (logistics/medical/signal/aviation) should use Economy of Force or Reserve posture. (AJP 4.10C)`);
        }
      });
    });

    setValidationErrors(errors);
    setValidationWarnings(warnings);
    setIsValid(errors.length === 0);

  }, [brigadeConfigs, totalDuration, totalFrontage, corpsPhases, t]);
  
  // OBS-S96-003: Apply sensible brigade role defaults for phases that have none set.
  // Assigns MAIN_EFFORT to first combat brigade, SUPPORTING to remaining combat brigades,
  // ECONOMY_OF_FORCE to CSS brigades. Only touches phases where NO brigade has any role set.
  // BUG-S97-012: ALWAYS fixes CSS posture warnings — if a CSS brigade inherits a combat
  // posture from the corps phase, sets an ECONOMY_OF_FORCE posture override regardless of
  // whether roles have already been assigned. This clears all doctrinal CSS posture warnings.
  const handleApplyRoleDefaults = useCallback(() => {
    const CSS_CATS = new Set(['CSS']);
    const COMBAT_POSTURES = new Set([
      'ATTACK', 'HASTY_ATTACK', 'DELIBERATE_ATTACK', 'RIVER_CROSSING',
      'DELIBERATE_OFFENSE', 'EXPLOITATION', 'PURSUIT'
    ]);
    setBrigadeConfigs(prev => {
      const updated = prev.map(c => ({ ...c, phase_overrides: { ...(c.phase_overrides || {}) } }));
      corpsPhases.forEach((phase, phaseIdx) => {
        // BUG-S97-012: Fix CSS posture for ALL phases unconditionally
        updated.forEach(c => {
          const doctrine = getUnitTypeDoctrine(c.unit_type || 'MECHANIZED');
          if (!CSS_CATS.has(doctrine.category)) return;
          const ov = c.phase_overrides[phaseIdx];
          const effectivePosture = ov?.operational_posture ?? phase.operational_posture;
          if (effectivePosture && COMBAT_POSTURES.has(effectivePosture)) {
            c.phase_overrides[phaseIdx] = {
              ...(c.phase_overrides[phaseIdx] || {}),
              operational_posture: 'ECONOMY_OF_FORCE',
            };
          }
        });
        // Role defaults: only set phases where no brigade has a role yet
        const anyRoleSet = updated.some(c => c.phase_overrides[phaseIdx]?.brigade_role != null);
        if (anyRoleSet) return;
        const combatBrigades = updated.filter(c => {
          const doctrine = getUnitTypeDoctrine(c.unit_type || 'MECHANIZED');
          return !CSS_CATS.has(doctrine.category);
        });
        const cssBrigades = updated.filter(c => {
          const doctrine = getUnitTypeDoctrine(c.unit_type || 'MECHANIZED');
          return CSS_CATS.has(doctrine.category);
        });
        combatBrigades.forEach((c, idx) => {
          c.phase_overrides[phaseIdx] = {
            ...(c.phase_overrides[phaseIdx] || {}),
            brigade_role: idx === 0 ? 'MAIN_EFFORT' : 'SUPPORTING',
          };
        });
        cssBrigades.forEach(c => {
          c.phase_overrides[phaseIdx] = {
            ...(c.phase_overrides[phaseIdx] || {}),
            brigade_role: 'ECONOMY_OF_FORCE',
          };
        });
      });
      return updated;
    });
  }, [corpsPhases]);

  // ============================================================================
  // PHASE MANAGEMENT
  // ============================================================================

  // REMOVED: handleAddPhase, handleUpdatePhaseField, handleDeletePhase
  // REASON: Brigade phase management now handled by BrigadePhaseTab component
  // These functions were created for inline editing but became obsolete
  
  // ============================================================================
  // SYNC HANDLERS
  // ============================================================================
  
  const handleAutoSync = () => {
    
    const currentBrigadeIds = brigades.map(b => b.id);
    
    setBrigadeConfigs(prev => {
      let updated = prev.filter(c => currentBrigadeIds.includes(c.brigade_id));
      
      // Add new brigades
      syncChanges.added.forEach((brigade, idx) => {
        const parentDivision = divisions.find(d => d.id === brigade.parent_division_id);
        const { resolved: forceQuality, inherited_from } = resolveForceQuality(brigade, forceBuilderResult);
        const unitType = brigade.unit_type || brigade.brigade_type || parentDivision?.unit_type || 'MECHANIZED';
        const doctrine = getUnitTypeDoctrine(unitType);
        const positionKm = doctrine.default_position;
        const autoState = getOperationalStateFromPosition(positionKm, unitType);
        // BUG-FMAP-001 FIX: cascade brigade→division→corps before falling back to NATO
        const nsForBrigade = brigade.nationalStandard ||
                             parentDivision?.nationalStandard ||
                             forceBuilderResult?.corps?.nationalStandard ||
                             'NATO';

        updated.push({
          brigade_id: brigade.id,
          brigade_number: brigade.brigade_number || (updated.length + 1),
          brigade_name: brigade.name || `Brigade ${updated.length + 1}`,
          parent_division_id: brigade.parent_division_id,
          parent_division_number: parentDivision?.division_number || 1,
          // BUG-163 FIX: Both naming conventions
          // BUG-FMAP-001 FIX: nsForBrigade uses brigade→division→corps cascade
          nationalStandard: nsForBrigade,
          national_standard: nsForBrigade,
          unit_type: unitType,
          strength: brigade.strength || getStrengthByStandard(nsForBrigade),
          evacuation_assets: brigade.evacuation_assets || {
            wheeled_ambulances: 8,
            tracked_ambulances: 6,
            helicopters: 0,
            stretcher_teams: 13
          },
          force_quality: forceQuality,
          force_quality_inherited_from: inherited_from,
          operational_state: autoState,
          operational_state_override: false,
          frontage_status: {
            holds_frontage: doctrine.has_frontage,
            reason: doctrine.has_frontage ? 'Combat unit on frontage' : `Support unit (${unitType})`,
            rear_area_modifier: doctrine.has_frontage ? 1.0 : 0.15
          },
          position: {
            position_feba_km: positionKm,
            frontage_start_km: null,
            frontage_end_km: null,
            frontage_width_km: 0,
            sector_designation: doctrine.has_frontage ? 'CENTER' : 'REAR',
            manual_lock: false
          },
          operation: {
            mission_type: doctrine.has_frontage ? 'DEFEND' : 'SUPPORTING',
            mission_statement: `Brigade conducts operations in assigned sector`,
            start_day: 1,
            start_hour: 6,
            end_day: totalDuration,
            end_hour: 18,
            operational_posture: 'DELIBERATE_DEFENSE',
            engagement_level: 'PREPARED_ENGAGEMENT',
            tempo: 'DELIBERATE'
          },
          environment: {
            terrain: 'MIXED',
            weather: 'CLEAR',
            threat_level: 'MODERATE_THREAT',
            technology_env: forceQuality.technology_env || 'DRONE_CONTESTED',
            technology_env_override: false
          },
          phases: [],
          phase_overrides: {},
          medical: {
            expected_casualty_rate: 'MODERATE',
            evacuation_priority: 'ROUTINE'
          },
          logistics: {
            supply_route: `MSR_NEW`,
            supply_priority: 'MODERATE'
          },
          modifiers: {
            casualty_modifier: 1.0,
            environmental_modifier: 1.0,
            national_standard_modifier: getNationalStandardModifier(nsForBrigade),
            force_quality_modifier: calculateForceQualityMultiplier(forceQuality),
            operational_state_modifier: getBattleCasualtyMultiplier(autoState),
            combined_modifier: 1.0
          }
        });
      });
      
      // Update modified brigades
      updated = updated.map(config => {
        const brigade = brigades.find(b => b.id === config.brigade_id);
        if (!brigade) return config;
        
        const modifiedBrigade = syncChanges.modified.find(m => m.id === brigade.id);
        if (!modifiedBrigade) return config;
        
        const parentDivision = divisions.find(d => d.id === brigade.parent_division_id);
        const { resolved: forceQuality, inherited_from } = resolveForceQuality(brigade, forceBuilderResult);
        const unitType = brigade.unit_type || brigade.brigade_type || parentDivision?.unit_type || config.unit_type || 'MECHANIZED';
        const doctrine = getUnitTypeDoctrine(unitType);
        
        // Recalculate frontage status if unit type changed
        const frontageStatus = getBrigadeFrontageStatus(
          unitType,
          config.operation?.operational_posture || 'DELIBERATE_DEFENSE',
          config.operation?.mission_type || 'DEFEND'
        );
        
        // Recalculate operational state if not overridden
        const positionKm = frontageStatus.holdsFrontage ? config.position?.position_feba_km || 5 : doctrine.default_position;
        const autoState = getOperationalStateFromPosition(positionKm, unitType);
        const newState = config.operational_state_override ? config.operational_state : autoState;
        
        return {
          ...config,
          brigade_name: brigade.name,
          parent_division_id: brigade.parent_division_id,
          parent_division_number: parentDivision?.division_number || config.parent_division_number,
          national_standard: brigade.nationalStandard || config.national_standard,
          unit_type: unitType,
          strength: brigade.strength || config.strength,
          evacuation_assets: brigade.evacuation_assets || config.evacuation_assets,
          force_quality: forceQuality,
          force_quality_inherited_from: inherited_from,
          operational_state: newState,
          frontage_status: {
            holds_frontage: frontageStatus.holdsFrontage,
            reason: frontageStatus.reason,
            rear_area_modifier: frontageStatus.casualty_modifier
          },
          position: {
            ...config.position,
            position_feba_km: positionKm
          },
          modifiers: {
            ...config.modifiers,
            national_standard_modifier: getNationalStandardModifier(brigade.nationalStandard || config.national_standard || 'NATO'),
            force_quality_modifier: calculateForceQualityMultiplier(forceQuality),
            operational_state_modifier: getBattleCasualtyMultiplier(newState)
          }
        };
      });
      
      return updated;
    });
    
    setShowSyncDialog(false);
    
    // Redistribute frontage after sync
    // v7.2.2 FIX: Use ref to get latest autoRedistributeFrontage (not stale closure)
    setTimeout(() => {
      if (autoRedistributeFrontageRef.current) autoRedistributeFrontageRef.current(totalFrontage, 'RESET');
    }, 100);
  };
  
  const handleResetTab2 = () => {
    if (window.confirm('This will reset all Tab 2 configuration. Continue?')) {
      setBrigadeConfigs([]);
      setShowSyncDialog(false);
      window.location.reload();
    }
  };
  
  // ============================================================================
  // SAVE HANDLERS
  // ============================================================================
  
  // ============================================================================
  // ENH-022b: formatDayLabel — returns "D+N" or "D+N (DD Mon)" when start date is set
  // Zero calculation impact. Used in Timeline header labels only.
  // ============================================================================
  const formatDayLabel = (day, startDateStr) => {
    if (!startDateStr) return `D+${day}`;
    try {
      const base = new Date(startDateStr);
      if (isNaN(base.getTime())) return `D+${day}`;
      base.setDate(base.getDate() + day - 1);
      const dd = base.getDate();
      const mon = base.toLocaleString('en-GB', { month: 'short' });
      return `D+${day} (${dd} ${mon})`;
    } catch {
      return `D+${day}`;
    }
  };

  // ============================================================================
  // BUG-150 FIX: Cascade totalDuration to brigade end_days.
  // When the planner changes operation duration, brigades whose end_day was set at
  // init time silently retain the old (shorter) value. Days beyond the old end_day
  // have no posture/mission config → calculator silent undercount.
  // Clamping is one-directional: only SHRINK end_days that now exceed the duration.
  // Brigades already within the new duration are never touched.
  useEffect(() => {
    setBrigadeConfigs(prev =>
      prev.map(config => {
        if (config.operation && config.operation.end_day > totalDuration) {
          return {
            ...config,
            operation: { ...config.operation, end_day: totalDuration }
          };
        }
        return config;
      })
    );
  }, [totalDuration]); // eslint-disable-line react-hooks/exhaustive-deps
  // Note: setBrigadeConfigs is stable (from useState) — not listed to avoid
  // triggering exhaustive-deps lint. Only totalDuration should drive this effect.

  // BUILD UPDATES (shared by handleMarkComplete and flushState)
  // Constructs the full operationParams + updatedForce from current local state.
  // Called by handleMarkComplete (with validation) and flushState (silent, no validation).
  // ============================================================================
  
  const buildUpdates = () => {
    const threatModifiers = getThreatModifiers(threatConfig);
    const locModifiers = getLOCModifiers(locConfig);
    const infraModifiers = infrastructureConfig.enabled ?
      getInfrastructureModifiers(infrastructureConfig) : null;
    const hnsModifiers = hnsConfig.enabled ?
      getHNSModifiers(hnsConfig) : null;
    const nightModifiers = nightOpsConfig.enabled ?
      getNightOpsModifiers(nightOpsConfig) : null;
    const climateModifiers = climateConfig.enabled ?
      getClimateModifiers(climateConfig) : null;
    const mroeModifiers = mroeConfig.enabled ?
      getMRoEModifiers(mroeConfig, brigadeConfigs.reduce((sum, b) => sum + (b.strength || 4000), 0)) : null;

    const operationParams = {
      configured: true,
      configuration_level: configMode,
      operation_name: operationName.trim() || undefined,
      prepared_by:   preparedBy.trim()   || undefined,
      operation_type: operationType,
      operational_posture: operationalPosture,
      engagement_level: engagementLevel,
      duration_days: totalDuration,
      operation_start_date: operationStartDate || null,  // ENH-022b — null when not set
      operation_start_time_z: operationStartTimeZ || DEFAULT_START_TIME_Z,   // R2-09: Zulu start time (with the date, the same in Tab 7 and the reports)
      threat_level: threatLevel,
      evacuation_paradigm: evacuationParadigm,  // BUG-057 FIX: Required by survivalModule.js
      ...(evacuationHoldHours !== '' && Number.isFinite(Number(evacuationHoldHours))
        ? { evacuation_hold_hours: Math.min(HOLD_HOURS_LIMIT, Math.max(0, Number(evacuationHoldHours))) }
        : {}),
      enemy_force_quality: enemyQuality,  // BUG-B FIX: Required by casualtyCalculator.js
      // 2026-10-22: enemy forces by sector and reinforcement by day: saved only when entered (nothing changes otherwise), with the sectors they refer to
      ...(enemyForces.entries.length > 0 ? { enemy_forces: {
        default_manning: enemyForces.default_manning,
        entries: enemyForces.entries,
        sectors: enemySectors,
        brigade_frontage: Object.fromEntries(enemySectors.map((sc) => [sc.brigade_id, { start_km: sc.start_km, end_km: sc.end_km }]))
      } } : {}),
      // 2026-10-13: enemy to friendly strength ratio; with the enemy quality it sets the resistance of every phase
      ...(typeof enemyStrengthRatio === 'number' && enemyStrengthRatio > 0 ? { enemy_strength_ratio: enemyStrengthRatio } : {}),
      cold_protection: coldProtection,
      environmental_factors: {
        terrain,
        weather,
        threat_level: threatLevel
      },
      threat_assessment: {
        ...threatConfig,
        // Planner entries outside the accepted range are not saved (the field shows an error); rows without a whole
        // day are dropped, so the calculator never receives an unreadable schedule.
        drone_dow_modifier: (typeof threatConfig.drone_dow_modifier === 'number' &&
          threatConfig.drone_dow_modifier >= DRONE_DOW_MODIFIER_RANGE.min &&
          threatConfig.drone_dow_modifier <= DRONE_DOW_MODIFIER_RANGE.max) ? threatConfig.drone_dow_modifier : null,
        drone_schedule: (Array.isArray(threatConfig.drone_schedule) ? threatConfig.drone_schedule : [])
          .filter((r) => Number.isInteger(r.from_day) && r.from_day >= 1)
          .map((r) => ({
            from_day: r.from_day,
            drone_threat: r.drone_threat,
            dow_modifier: (typeof r.dow_modifier === 'number' && r.dow_modifier >= DRONE_DOW_MODIFIER_RANGE.min &&
              r.dow_modifier <= DRONE_DOW_MODIFIER_RANGE.max) ? r.dow_modifier : null
          })),
        modifiers: threatModifiers
      },
      loc_config: {
        ...locConfig,
        modifiers: locModifiers
      },
      infrastructure_config: {
        ...infrastructureConfig,
        modifiers: infraModifiers
      },
      hns_config: {
        ...hnsConfig,
        modifiers: hnsModifiers
      },
      air_situation_config: airSituationConfig,
      night_ops_config: {
        ...nightOpsConfig,
        modifiers: nightModifiers
      },
      climate_config: {
        ...climateConfig,
        modifiers: climateModifiers
      },
      mroe_config: {
        ...mroeConfig,
        modifiers: mroeModifiers
      },
      monte_carlo_config: monteCarloConfig,  // PHASE 1.3: Monte Carlo configuration
      buildup_config: buildupConfig,         // V4-TAB6-004: Pre-combat build-up phase DNBI
      phases: usePhases ? phases : [],
      corps_config: {
        total_duration_days: totalDuration,
        total_frontage_km: totalFrontage,
        overall_mission: overallMission,
        enemy_quality: enemyQuality,
        enemy_strength_ratio: enemyStrengthRatio,
        force_superiority: forceSuperority,
        default_technology_env: defaultTechEnv,
        default_technology_env_user_set: techDefaultUserSet,
        personnel: corpsPersonnel,
        operational_state: corpsOperationalState,
        position_feba_km: corpsPositionFeba,
        phases: corpsPhases
      },
      division_configs: divisionConfigs,
      // GROUP-1-D: Gielerak bo factor — medical structure protection level (0-100%).
      // Read by casualtyCalculator.js to compute _boCapMod surgical capacity modifier.
      medical_protection_level: medicalProtectionLevel
    };

    const updatedForce = {
      ...scenario.force,
      
      // FIX: Keep flattened brigades in sync with nested brigades
      // Calculator reads from force.brigades (flattened)
      // Tab2 reads from force.forceBuilderResult.brigades (nested)
      // Both must stay synchronized - this is a REFERENCE (not a copy), zero overhead
      brigades: brigadeConfigs,
      
      forceBuilderResult: {
        ...forceBuilderResult,
        brigades: brigadeConfigs
      }
    };

    // Step 1.5: Extract MTF hierarchy for calculator routing and Tab3 display
    const mtfFacilities = extractMedicalFacilities(updatedForce);
    const mtfHierarchyWithEvac = mtfFacilities.length > 0
      ? calculateEvacuationDistances(mtfFacilities)
      : mtfFacilities;

    return {
      operation_parameters: operationParams,
      force: updatedForce,
      medical_configuration: {
        mtf_hierarchy: mtfHierarchyWithEvac,
        extracted_at: Date.now(),
        echelon: updatedForce?.echelon || null,
        facility_count: mtfHierarchyWithEvac.length
      }
    };
  };

  // Keep buildUpdatesRef current on every render so flushState always uses latest state
  buildUpdatesRef.current = buildUpdates;

  // 2026-10-13 (Tab 2 plan 7.9, 7.11): enemy power index for the phase rows, scenario of the end-of-tab preview, Ukraine 2024 bundle
  const enemyIndexValue = (() => {
    const ratio = (typeof enemyStrengthRatio === 'number' && enemyStrengthRatio > 0) ? enemyStrengthRatio : 1;
    const power = enemyPowerIndex({ ...(enemyQuality || DEFAULT_ENEMY_QUALITY), strength_ratio: ratio });
    return { index: power.index, level: levelOfIndex(power.index) };
  })();
  const getPreviewScenario = () => {
    const u = buildUpdatesRef.current();
    // a single-brigade scenario has no per-brigade Tab 2 configs yet: the brigades are those of Tab 1
    const fbrBrigade = u.force?.forceBuilderResult?.brigade;
    const previewBrigades = (u.force.brigades && u.force.brigades.length > 0)
      ? u.force.brigades
      : (scenario?.force?.brigades && scenario.force.brigades.length > 0 ? scenario.force.brigades : (fbrBrigade ? [fbrBrigade] : []));
    return {
      ...scenario,
      operation_parameters: u.operation_parameters,
      force: { ...u.force, brigades: previewBrigades },
      medical_configuration: { ...(scenario?.medical_configuration || {}), ...u.medical_configuration }
    };
  };
  const [ukraineBundleOpen, setUkraineBundleOpen] = useState(false);
  const applyUkraineBundle = () => {
    setThreatConfig({ ...UKRAINE_2024_BASELINE, drone_dow_modifier: null, drone_schedule: [] });
    setAirSituationConfig({ level: 'CONTESTED' });
    setLocConfig({ ...HIGH_THREAT_LOC_CONFIG });
    setClimateConfig({ ...EASTERN_EUROPE_CONFIG });
    setEnemyStrengthRatio(RESISTANCE_ANCHORS.heavy);   // enemy power index 2.0 = heavy resistance in every phase set to auto
    setUkraineBundleOpen(false);
  };

  const handleMarkComplete = () => {
    if (!isValid) {
      alert('Please fix validation errors before continuing');
      return;
    }
    
    // ========== VALIDATE ALL CONFIGURATIONS (Tasks 2.12-2.24) ==========
    const validationWarnings = [];
    
    if (infrastructureConfig.enabled) {
      const infraValidation = validateInfrastructureConfig(infrastructureConfig);
      if (!infraValidation.valid) {
        validationWarnings.push(...infraValidation.errors.map(e => `Infrastructure: ${e}`));
      }
      if (infraValidation.warnings) {
        validationWarnings.push(...infraValidation.warnings.map(w => `Infrastructure: ${w}`));
      }
    }
    
    if (hnsConfig.enabled) {
      const hnsValidation = validateHNSConfig(hnsConfig);
      if (!hnsValidation.valid) {
        validationWarnings.push(...hnsValidation.errors.map(e => `HNS: ${e}`));
      }
      if (hnsValidation.warnings) {
        validationWarnings.push(...hnsValidation.warnings.map(w => `HNS: ${w}`));
      }
    }
    
    // BUG-T10 fix: cross-validate Hospitals Overwhelmed vs HNS Robust — logically contradictory
    if (infrastructureConfig?.enabled &&
        infrastructureConfig?.local_medical === 'OVERWHELMED' &&
        hnsConfig?.enabled &&
        ['EXTENSIVE', 'ROBUST', 'FULL'].includes(hnsConfig?.support_level)) {
      validationWarnings.push(
        'Cross-scenario conflict: Local hospitals are OVERWHELMED yet HNS support level is set to ' +
        hnsConfig.support_level + '. Overwhelmed civilian hospitals cannot provide robust HNS capacity. ' +
        'Lower HNS level to MINIMAL or set local_medical to FUNCTIONAL.'
      );
    }

    if (nightOpsConfig.enabled) {
      const nightValidation = validateNightOpsConfig(nightOpsConfig);
      if (!nightValidation.valid) {
        validationWarnings.push(...nightValidation.errors.map(e => `Night Ops: ${e}`));
      }
      if (nightValidation.warnings) {
        validationWarnings.push(...nightValidation.warnings.map(w => `Night Ops: ${w}`));
      }
    }
    
    if (climateConfig.enabled) {
      const climateValidation = validateClimateConfig(climateConfig);
      if (!climateValidation.valid) {
        validationWarnings.push(...climateValidation.errors.map(e => `Climate: ${e}`));
      }
      if (climateValidation.warnings) {
        validationWarnings.push(...climateValidation.warnings.map(w => `Climate: ${w}`));
      }
    }
    
    if (mroeConfig.enabled) {
      const totalStrength = brigadeConfigs.reduce((sum, b) => sum + (b.strength || 4000), 0);
      const mroeValidation = validateMRoEConfig(mroeConfig, totalStrength);
      if (!mroeValidation.valid) {
        validationWarnings.push(...mroeValidation.errors.map(e => `MRoE: ${e}`));
      }
      if (mroeValidation.warnings) {
        validationWarnings.push(...mroeValidation.warnings.map(w => `MRoE: ${w}`));
      }
    }

    const airSitValidation = validateAirSituationConfig(airSituationConfig);
    if (!airSitValidation.valid) {
      validationWarnings.push(...airSitValidation.errors.map(e => `Air Situation: ${e}`));
    }
    
    brigadeConfigs.forEach(config => {
      const positionValidation = validatePositionForState(
        config.position?.position_feba_km || 5,
        config.operational_state || 'COMBAT_ENGAGED'
      );
      if (!positionValidation.valid) {
        validationWarnings.push(`Brigade ${config.brigade_number}: ${positionValidation.warning}`);
      }
    });
    
    if (validationWarnings.length > 0) {
    }

    const updates = buildUpdates();

    onUpdate(updates);
    // calculationService: operations config changed — Tab5 result is now stale
    markDirty();

    // V4-014: CoA B detection — check CoA slot state before navigating
    const coaA = lsRead(COA_KEY_A);
    const coaB = lsRead(COA_KEY_B);
    if (coaA && !coaB) {
      // CoA A saved, CoA B not yet — offer shortcut to Tab 5
      setCoaDetectMode('new_b');
      setCoaDetectOpen(true);
    } else if (coaA && coaB) {
      // Both CoAs saved — ask which one is being updated
      setCoaDetectMode('update_both');
      setCoaDetectOpen(true);
    } else {
      // No CoAs saved yet — normal flow → Tab 3
      onComplete();
    }
  };

  // V4-014: User chose to skip to Tab 5 (CoA B shortcut)
  const handleCoaSkipToTab5 = () => {
    setCoaDetectOpen(false);
    // Navigate directly to Tab 5 (index 4)
    if (onNavigate) onNavigate(4);
  };

  // V4-014: User chose normal flow → Tab 3
  const handleCoaNormalFlow = () => {
    setCoaDetectOpen(false);
    onComplete();
  };
  
  // ============================================================================
  // RENDER: MODE SELECTOR (v5.5 UI-009: Simplified to 2 options)
  // ============================================================================
  
  const renderModeSelector = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {t('tab2.config_view')}
          {mode === 'EXPERT' && (
            <Chip
              size="small"
              label={t('tab2.expert_mode_chip')}
              color="warning"
              sx={{ ml: 2 }}
            />
          )}
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Button
              variant={configMode === 'STANDARD' ? 'contained' : 'outlined'}
              fullWidth
              onClick={() => setConfigMode('STANDARD')}
              startIcon={<SettingsIcon />}
            >
              {t('tab2.summary_view')}
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {t('tab2.summary_view_desc')}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Button
              variant={configMode === 'DETAILED' ? 'contained' : 'outlined'}
              fullWidth
              onClick={() => setConfigMode('DETAILED')}
              disabled={brigadeConfigs.length === 0}
              startIcon={<PeopleIcon />}
            >
              {t('tab2.detailed_view')}
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {t('tab2.detailed_view_desc')}
              {mode === 'EXPERT' && ' ' + t('tab2.expert_mode_position')}
            </Typography>
          </Grid>
        </Grid>
        {mode === 'EXPERT' && configMode === 'DETAILED' && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
               {t('tab2.expert_mode_info')}
            </Typography>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
  
  // ============================================================================
  // RENDER: STANDARD MODE CONFIG
  // ============================================================================
  
  // ============================================================================
  // RENDER: EVACUATION ENVIRONMENT (planner inputs, both configuration modes)
  // The paradigm had no control before 2026-10-10 (every scenario ran as CONTESTED). The hold time is new.
  // ============================================================================
  const renderEvacuationEnvironment = () => {
    // 2026-10-19 (R2-25, decision R2-23): the wait before the first movement is DERIVED from the operation, not typed.
    // Shown here with its factors. 2026-10-20 (R2-28, R2-28b): the evacuation environment (permissive / contested / denied) is derived too, per day, and the
    // typed selector was removed; operation_parameters.evacuation_paradigm is still saved for older readers but the calculation ignores it.
    let derived = null;
    let derivedError = null;
    try {
      derived = evacuationWindow({
        locConfig, threatAssessment: threatConfig, weather, airSituation: airSituationConfig?.level || 'PARITY',
        resistance: enemyIndexValue.level
      });
    } catch (e) { derivedError = e.message; }
    const pct = (x) => `${Math.round(x * 100)} %`;
    const hrs = (x) => `${Math.round(x * 10) / 10} h`;
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>{t('tab2.evac_environment_title')}</Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{t('tab2.evac_derived_title')}</Typography>
              {derivedError && <Alert severity="warning">{derivedError}</Alert>}
              {derived && (
                <>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', my: 1 }}>
                    <Chip size="small" color="info" label={t('tab2.evac_chip_open', { value: pct(derived.p_open) })} />
                    <Chip size="small" label={t('tab2.evac_chip_mean', { value: hrs(derived.mean_wait_hours) })} />
                    <Chip size="small" label={t('tab2.evac_chip_median', { value: hrs(derived.median_wait_hours) })} />
                    <Chip size="small" label={t('tab2.evac_chip_p90', { value: hrs(derived.p90_wait_hours) })} />
                    <Chip size="small" color={derived.share_beyond_6h > 0.25 ? 'warning' : 'default'} label={t('tab2.evac_chip_beyond6', { value: pct(derived.share_beyond_6h) })} />
                    <Chip size="small" label={t('tab2.evac_chip_beyond24', { value: pct(derived.share_beyond_24h) })} />
                  </Box>
                  <Typography variant="caption" display="block" sx={{ color: 'text.secondary' }}>
                    {t('tab2.evac_factors', {
                      road: pct(derived.parts.road), heli: pct(derived.parts.helicopter), drone: pct(derived.parts.drone),
                      fight: pct(derived.parts.intensity), art: pct(derived.parts.artillery), closure: hrs(derived.mean_closure_hours)
                    })}
                  </Typography>
                </>
              )}
              <FormHelperText>{t('tab2.evac_derived_help')}</FormHelperText>
              {evacuationHoldHours !== '' && (
                <Alert severity="warning" sx={{ mt: 1 }} action={<Button color="inherit" size="small" onClick={() => setEvacuationHoldHours('')}>{t('tab2.evac_clear_hold')}</Button>}>
                  {t('tab2.evac_legacy_hold', { value: evacuationHoldHours, former: PARADIGM_HOLD_HOURS[evacuationParadigm] })}
                </Alert>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  };

  const renderStandardModeConfig = () => {
    if (configMode !== 'STANDARD') return null;
    
    return (
      <>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>{t('tab2.echelon_op_params', { echelon: echelonLabel })}</Typography>
          
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormControl fullWidth>
                <InputLabel>{t('tab2.operation_type')}</InputLabel>
                <Select
                  value={operationType}
                  onChange={(e) => setOperationType(e.target.value)}
                  label={t('tab2.operation_type')}
                >
                  {Object.keys(OPERATION_TYPES).map(key => (
                    <MenuItem key={key} value={key}>{OPERATION_TYPES[key].label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid size={{ xs: 12, md: 4 }}>
              <FormControl fullWidth>
                <InputLabel>{t('tab2.operational_posture_label')}</InputLabel>
                <Select
                  value={operationalPosture}
                  onChange={(e) => setOperationalPosture(e.target.value)}
                  label={t('tab2.operational_posture_label')}
                >
                  {Object.keys(OPERATIONAL_POSTURES).map(key => (
                    <MenuItem key={key} value={key}>{OPERATIONAL_POSTURES[key].label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid size={{ xs: 12, md: 4 }}>
              <FormControl fullWidth>
                <InputLabel>{t('tab2.engagement_level_label')}</InputLabel>
                <Select
                  value={engagementLevel}
                  onChange={(e) => setEngagementLevel(e.target.value)}
                  label={t('tab2.engagement_level_label')}
                >
                  {Object.keys(ENGAGEMENT_LEVELS).map(key => (
                    <MenuItem key={key} value={key}>{ENGAGEMENT_LEVELS[key].label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                label={t('tab2.operation_duration_days')}
                type="number"
                fullWidth
                value={totalDuration}
                onChange={(e) => setTotalDuration(Math.max(1, Number(e.target.value)))}
                inputProps={{ min: 1, max: 365 }}
              />
            </Grid>

            {/* ENH-022b: optional operation start date — when set, D+N labels show calendar date */}
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                label={t('tab2.operation_start_date')}
                type="date"
                fullWidth
                value={operationStartDate}
                onChange={(e) => setOperationStartDate(e.target.value)}
                disabled={exerciseClockStarted}
                helperText={exerciseClockStarted ? t('tab2.start_date_locked')
                  : operationStartDate ? t('tab2.start_date_hint_set', { end: Number(totalDuration) >= 1 ? formatZulu(endInstant(operationStartDate, operationStartTimeZ, Number(totalDuration))) : '-', days: totalDuration })
                  : t('tab2.start_date_hint_empty')}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            {operationStartDate && (
              <Grid size={{ xs: 12, md: 2 }}>
                <TextField
                  label={t('tab2.operation_start_time_z')}
                  type="time"
                  fullWidth
                  value={operationStartTimeZ}
                  onChange={(e) => setOperationStartTimeZ(e.target.value || DEFAULT_START_TIME_Z)}
                  disabled={exerciseClockStarted}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            )}

            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                label={t('tab2.total_frontage_km')}
                type="number"
                fullWidth
                value={totalFrontage}
                onChange={(e) => setTotalFrontage(Math.max(10, Number(e.target.value)))}
                inputProps={{ min: 10, max: 500 }}
              />
            </Grid>
            
            {/* FIX-T2-003: Overall Mission selector */}
            <Grid size={{ xs: 12, md: 4 }}>
              <FormControl fullWidth>
                <InputLabel>{t('tab2.overall_mission')}</InputLabel>
                <Select
                  value={overallMission}
                  onChange={(e) => setOverallMission(e.target.value)}
                  label={t('tab2.overall_mission')}
                >
                  <MenuItem value="GROUND_COMBAT">{t('tab2.ground_combat')}</MenuItem>
                  <MenuItem value="DEFENSE">{t('tab2.defense')}</MenuItem>
                  <MenuItem value="OFFENSE">{t('tab2.offense')}</MenuItem>
                  <MenuItem value="STABILITY_OPS">{t('tab2.stability_ops')}</MenuItem>
                  <MenuItem value="PEACEKEEPING">{t('tab2.peacekeeping')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            {/* FIX-T2-002: Auto-calculate toggle */}
            <Grid size={{ xs: 12, md: 4 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={autoCalculate}
                    onChange={(e) => setAutoCalculate(e.target.checked)}
                  />
                }
                label={t('tab2.auto_calc')}
              />
            </Grid>
            
            {/* FIX-T2-004: Multi-phase operations toggle */}
            <Grid size={{ xs: 12, md: 4 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={usePhases}
                    onChange={(e) => {
                      setUsePhases(e.target.checked);
                      if (!e.target.checked) {
                        setPhases([]);
                      }
                    }}
                  />
                }
                label={t('tab2.use_phases')}
              />
            </Grid>
          </Grid>
          
        </CardContent>
      </Card>

      </>
    );
  };
  
  
  // ============================================================================
  // RENDER: ENVIRONMENTAL FACTORS (all configuration modes)
  // R2-15 (2026-10-20): the card was drawn only in STANDARD mode, so in the brigade, division and corps modes the operation weather
  // could not be set at all. R2-14: the season selector (it had no control; the season was always summer).
  // ============================================================================
  const renderEnvironmentalFactors = () => (
    <>
      {/* Environmental Factors - separate card, separate concern */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>{t('tab2.environmental_factors')}</Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormControl fullWidth>
                <InputLabel>{t('tab2.terrain_label')}</InputLabel>
                <Select value={terrain} onChange={(e) => setTerrain(e.target.value)} label={t('tab2.terrain_label')}>
                  {Object.keys(TERRAIN_TYPES).map(key => (
                    <MenuItem key={key} value={key}>{TERRAIN_TYPES[key].label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <FormControl fullWidth>
                <InputLabel>{t('tab2.weather_label')}</InputLabel>
                <Select value={weather} onChange={(e) => setWeather(e.target.value)} label={t('tab2.weather_label')}>
                  {Object.keys(WEATHER_CONDITIONS).map(key => (
                    <MenuItem key={key} value={key}>{WEATHER_CONDITIONS[key].label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <FormControl fullWidth>
                <InputLabel>{t('tab2.threat_level_label')}</InputLabel>
                <Select value={threatLevel} onChange={(e) => setThreatLevel(e.target.value)} label={t('tab2.threat_level_label')}>
                  {Object.keys(THREAT_LEVELS).map(key => (
                    <MenuItem key={key} value={key}>{THREAT_LEVELS[key].label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>


            <Grid size={{ xs: 12, md: 3 }}>
              <FormControl fullWidth>
                <InputLabel>{t('tab2.season_label')}</InputLabel>
                <Select
                  value={operationStartDate ? (seasonOfDay(operationStartDate, operationStartTimeZ, 1, climateConfig?.hemisphere || 'NORTH') || 'summer') : (climateConfig?.season || 'summer')}
                  onChange={(e) => setClimateConfig(prev => ({ ...prev, season: e.target.value }))}
                  label={t('tab2.season_label')}
                  disabled={!!operationStartDate}
                >
                  {['spring', 'summer', 'fall', 'winter'].map(key => (
                    <MenuItem key={key} value={key}>{t(`tab2.season_${key}`)}</MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {operationStartDate
                    ? t('tab2.season_from_date', {
                        seasons: (() => {
                          const seq = [];
                          for (let d = 1; d <= totalDuration; d += 1) {
                            const sn = seasonOfDay(operationStartDate, operationStartTimeZ, d, climateConfig?.hemisphere || 'NORTH');
                            if (!seq.length || seq[seq.length - 1].season !== sn) seq.push({ season: sn, from: d });
                          }
                          return seq.map((x) => `${t(`tab2.season_${x.season}`)} (D+${x.from})`).join(' → ');
                        })()
                      })
                    : t('tab2.season_help')}
                </FormHelperText>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <FormControl fullWidth>
                <InputLabel>{t('tab2.hemisphere_label')}</InputLabel>
                <Select
                  value={climateConfig?.hemisphere || 'NORTH'}
                  onChange={(e) => setClimateConfig(prev => ({ ...prev, hemisphere: e.target.value }))}
                  label={t('tab2.hemisphere_label')}
                  disabled={!operationStartDate}
                >
                  <MenuItem value="NORTH">{t('tab2.hemisphere_north')}</MenuItem>
                  <MenuItem value="SOUTH">{t('tab2.hemisphere_south')}</MenuItem>
                </Select>
                <FormHelperText>{t('tab2.hemisphere_help')}</FormHelperText>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>{t('tab2.prophylaxis_label')}</InputLabel>
                <Select
                  value={[1, 0.8, 0.6, 0.4].includes(climateConfig?.prophylaxis_compliance) ? climateConfig.prophylaxis_compliance : 1}
                  onChange={(e) => setClimateConfig(prev => ({ ...prev, prophylaxis_compliance: e.target.value }))}
                  label={t('tab2.prophylaxis_label')}
                >
                  <MenuItem value={1}>{t('tab2.p100')}</MenuItem>
                  <MenuItem value={0.8}>{t('tab2.p80')}</MenuItem>
                  <MenuItem value={0.6}>{t('tab2.p60')}</MenuItem>
                  <MenuItem value={0.4}>{t('tab2.p40')}</MenuItem>
                </Select>
                <FormHelperText>{t('tab2.prophylaxis_help')}</FormHelperText>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>{t('tab2.acclimatized_label')}</InputLabel>
                <Select
                  value={climateConfig?.acclimatization_complete === false ? 'NO' : 'YES'}
                  onChange={(e) => setClimateConfig(prev => ({ ...prev, acclimatization_complete: e.target.value === 'YES' }))}
                  label={t('tab2.acclimatized_label')}
                >
                  <MenuItem value="YES">{t('tab2.acclimatized_yes')}</MenuItem>
                  <MenuItem value="NO">{t('tab2.acclimatized_no')}</MenuItem>
                </Select>
                <FormHelperText>{t('tab2.acclimatized_help')}</FormHelperText>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>{t('tab2.cold_protection_label')}</InputLabel>
                <Select value={coldProtection} onChange={(e) => setColdProtection(e.target.value)} label={t('tab2.cold_protection_label')}>
                  {[['NONE', 'cold_none'], ['BASIC', 'cold_basic'], ['STANDARD', 'cold_standard'], ['EXCELLENT', 'cold_excellent']].map(([key, tk]) => (
                    <MenuItem key={key} value={key}>{t(`tab2.${tk}`)}</MenuItem>
                  ))}
                </Select>
                <FormHelperText>{t('tab2.cold_protection_help')}</FormHelperText>
              </FormControl>
            </Grid>

          </Grid>
        </CardContent>
      </Card>
    </>
  );

  // ============================================================================
  // RENDER: BRIGADES LIST (ENHANCED WITH OPERATIONAL STATE)
  // ============================================================================
  
  const renderBrigadesList = () => {
    if (configMode === 'STANDARD') return null;
    
    const activeBrigades = brigadeConfigs.filter(c => c.operation.mission_type !== 'RESERVE');
    const reserves = brigadeConfigs.filter(c => c.operation.mission_type === 'RESERVE');
    
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>{t('tab2.brigade_configurations')}</Typography>

          <Alert severity="info" sx={{ mb: 2 }}>
            <AlertTitle>{t('tab2.operational_state_system')}</AlertTitle>
            {t('tab2.each_bde_has_state')}
            <Box sx={{ display: 'flex', gap: 2, mt: 1, flexWrap: 'wrap' }}>
              {Object.entries(OPERATIONAL_STATES).map(([key, config]) => (
                <Chip
                  key={key}
                  icon={<span>{config.icon}</span>}
                  label={`${config.shortLabel}: ${config.battle_casualty_multiplier}x battle, ${config.dnbi?.combined_rate}/1k DNBI`}
                  size="small"
                  color={config.color}
                  variant="outlined"
                />
              ))}
            </Box>
          </Alert>
          
          {activeBrigades.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}>
                {t('tab2.combat_brigades_count', { count: activeBrigades.length })}
              </Typography>
              {activeBrigades.map((config) => {
                const fqMult = config.modifiers?.force_quality_modifier || 1.0;
                const hasHelicopters = config.evacuation_assets?.helicopters > 0;
                const stateConfig = OPERATIONAL_STATES[config.operational_state] || OPERATIONAL_STATES.COMBAT_ENGAGED;
                
                return (
                  <Accordion key={config.brigade_id} sx={{ mb: 1, borderLeft: `4px solid`, borderLeftColor: `${stateConfig.color}.main` }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', flexWrap: 'wrap' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', minWidth: 120 }}>
                          Bde {config.brigade_number}
                        </Typography>
                        <Chip label={`Div ${config.parent_division_number}`} size="small" variant="outlined" />
                        <Chip 
                          label={config.national_standard} 
                          size="small"
                          color={config.national_standard === 'ROMANIAN' ? 'error' : config.national_standard === 'US' ? 'success' : 'primary'}
                        />
                        <Chip icon={<PeopleIcon />} label={`${config.strength}`} size="small" />
                        <Chip 
                          icon={<FlightIcon />} 
                          label={`${config.evacuation_assets?.helicopters || 0} `} 
                          size="small" 
                          color={hasHelicopters ? 'success' : 'error'}
                        />
                        {/* Force Quality Modifier */}
                        <Tooltip title={`Force Quality Modifier: ${fqMult.toFixed(2)}x casualties`}>
                          <Chip 
                            label={`FQ ${fqMult.toFixed(2)}x`} 
                            size="small" 
                            color={fqMult > 1.1 ? 'error' : fqMult < 0.9 ? 'success' : 'default'}
                            variant="outlined"
                          />
                        </Tooltip>
                        {/* Battle Modifier from State */}
                        <Tooltip title={`${stateConfig.label}: ${stateConfig.battle_casualty_multiplier}x battle casualties`}>
                          <Chip 
                            label={`${stateConfig.icon} ${stateConfig.battle_casualty_multiplier}x`} 
                            size="small" 
                            color={stateConfig.color}
                            variant="outlined"
                          />
                        </Tooltip>
                        <Chip label={config.operation.mission_type} size="small" color="secondary" />
                        {/* NEW: Operational State Badge */}
                        <OperationalStateBadge state={config.operational_state} compact={true} />
                        {/* Position info with adjacent units */}
                        {config.frontage_status?.holds_frontage ? (
                          <Tooltip title={
                            <span>
                              Left: {getAdjacentBrigadeName(config.position?.adjacent_left) || 'None'}<br/>
                              Right: {getAdjacentBrigadeName(config.position?.adjacent_right) || 'None'}
                            </span>
                          }>
                            <Chip label={`${config.position.frontage_start_km}-${config.position.frontage_end_km}km`} size="small" variant="outlined" />
                          </Tooltip>
                        ) : (
                          <Chip label={`${config.position?.position_feba_km || 0}km from FEBA`} size="small" variant="outlined" color="info" />
                        )}
                        <ForceQualityBadge 
                          forceQuality={config.force_quality}
                          inheritedFrom={config.force_quality_inherited_from}
                          compact={true}
                        />
                        {/* Phase status chip */}
                        {/* UX-004: phase inheritance indicator — warning color when brigade breaks division inheritance */}
                        <Tooltip title={
                          config.uses_division_phases
                            ? `Inheriting phases from Division ${config.parent_division_number || ''}`
                            : config.phases?.length > 0
                              ? `Brigade overrides Division phases with ${config.phases.length} custom phase${config.phases.length !== 1 ? 's' : ''} — breaks inheritance chain`
                              : 'No phases configured — falls back to defaults'
                        }>
                          <Chip
                            label={
                              config.uses_division_phases
                                ? 'Div phases'
                                : config.phases?.length > 0
                                  ? `${config.phases.length} custom`
                                  : 'No phases'
                            }
                            size="small"
                            variant="outlined"
                            color={
                              config.uses_division_phases ? 'default'
                                : config.phases?.length > 0 ? 'warning'
                                : 'default'
                            }
                          />
                        </Tooltip>
                      </Box>
                    </AccordionSummary>
                    
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        {/* NEW: Operational State Selector */}
                        <Grid size={{ xs: 12 }}>
                          <OperationalStateSelector
                            brigadeConfig={config}
                            onStateChange={updateBrigadeOperationalState}
                            onPositionChange={updateBrigadePositionWithState}
                            mode={configMode}
                            expanded={true}
                          />
                        </Grid>
                        
                        <Grid size={{ xs: 12 }}>
                          <Alert severity="info">
                            <strong>Position:</strong> Brigade {config.brigade_number} of Division {config.parent_division_number || 1} 
                            ({brigadeConfigs.filter(b => b.parent_division_id === config.parent_division_id).length} brigades in division)<br />
                            <strong>Unit Type:</strong> {config.unit_type}<br />
                            <strong>Mission:</strong> {config.operation.mission_statement}
                          </Alert>
                        </Grid>
                        
                        <Grid size={{ xs: 12, md: 6 }}>
                          <ForceQualityBadge 
                            forceQuality={config.force_quality}
                            inheritedFrom={config.force_quality_inherited_from}
                            compact={false}
                          />
                        </Grid>
                        
                        <Grid size={{ xs: 12, md: 6 }}>
                          <TechnologyEnvironmentSection
                            brigadeConfig={config}
                            onUpdate={updateBrigadeConfig}
                            defaultTechEnv={defaultTechEnv}
                          />
                        </Grid>
                        
                        {!hasHelicopters && config.national_standard === 'ROMANIAN' && (
                          <Grid size={{ xs: 12 }}>
                            <Alert severity="warning">
                              <AlertTitle>[!] Romanian Brigade - Zero Helicopters</AlertTitle>
                              All casualties must be evacuated by ground ambulance.
                            </Alert>
                          </Grid>
                        )}
                        
                        <Grid size={{ xs: 12 }}>
                          <Button
                            variant="contained"
                            startIcon={<EditIcon />}
                            onClick={() => {
                              setEditingBrigade(config.brigade_id);
                              setBrigadeDialogTab(0);
                            }}
                          >
                            Edit Full Configuration
                          </Button>
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Box>
          )}
          
          {reserves.length > 0 && (
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, color: 'warning.main' }}>
                Reserve Brigades ({reserves.length})
              </Typography>
              {reserves.map((config) => (
                <Accordion key={config.brigade_id} sx={{ mb: 1, bgcolor: 'warning.50' }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontWeight: 'bold' }}> Reserve Bde {config.brigade_number}</Typography>
                      <Chip label={`Position: ${config.position.position_feba_km}km`} size="small" />
                      <OperationalStateBadge state={config.operational_state} compact={true} />
                      <ForceQualityBadge forceQuality={config.force_quality} inheritedFrom={config.force_quality_inherited_from} compact={true} />
                      <Tooltip title={config.uses_division_phases ? `Inheriting phases from Division ${config.parent_division_number || ''}` : config.phases?.length > 0 ? `${config.phases.length} custom phase${config.phases.length !== 1 ? 's' : ''} configured` : 'No phases configured'}>
                        <Chip
                          label={config.uses_division_phases ? 'Div phases' : config.phases?.length > 0 ? `${config.phases.length} phases` : 'No phases'}
                          size="small" variant="outlined"
                          color={config.uses_division_phases ? 'default' : config.phases?.length > 0 ? 'primary' : 'default'}
                        />
                      </Tooltip>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <OperationalStateSelector
                      brigadeConfig={config}
                      onStateChange={updateBrigadeOperationalState}
                      onPositionChange={updateBrigadePositionWithState}
                      mode={configMode}
                      expanded={false}
                    />
                    <Button
                      variant="contained"
                      startIcon={<EditIcon />}
                      sx={{ mt: 2 }}
                      onClick={() => {
                        setEditingBrigade(config.brigade_id);
                        setBrigadeDialogTab(0);
                      }}
                    >
                      Edit Configuration
                    </Button>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };
  
  // ============================================================================
  // RENDER: BRIGADE CONFIGURATION DIALOG
  // ============================================================================
  
  const renderBrigadeDialog = () => {
    if (!editingBrigade) return null;
    
    const config = getBrigadeConfig(editingBrigade);
    if (!config) return null;
    
    const stateConfig = OPERATIONAL_STATES[config.operational_state] || OPERATIONAL_STATES.COMBAT_ENGAGED;
    
    return (
      <Dialog open={!!editingBrigade} onClose={() => setEditingBrigade(null)} maxWidth="lg" fullWidth>
        <DialogTitle>
          Configure Brigade {config.brigade_number} - {config.brigade_name}
          <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
            <Typography variant="caption" color="text.secondary">
              Force Quality: {config.force_quality_inherited_from} ({(config.modifiers?.force_quality_modifier || 1).toFixed(2)}x)
            </Typography>
            <Typography variant="caption" sx={{ color: `${stateConfig.color}.main` }}>
              | {stateConfig.icon} {stateConfig.label} ({stateConfig.battle_casualty_multiplier}x casualties)
            </Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Tabs value={brigadeDialogTab} onChange={(e, newValue) => setBrigadeDialogTab(newValue)}>
            <Tab label={t('tab2.dialog_tab_pos_state')} />
            <Tab label={t('tab2.dialog_tab_mission')} />
            <Tab label={t('tab2.dialog_tab_phases')} />
            <Tab label={t('tab2.dialog_tab_environment')} />
            <Tab label={t('tab2.dialog_tab_medical')} />
            <Tab label={t('tab2.dialog_tab_logistics')} />
            <Tab label={t('tab2.dialog_tab_summary')} />
          </Tabs>
          
          {/* TAB 0: POSITION & OPERATIONAL STATE (ENHANCED) */}
          <TabPanel value={brigadeDialogTab} index={0}>
            <OperationalStateSelector
              brigadeConfig={config}
              onStateChange={updateBrigadeOperationalState}
              onPositionChange={updateBrigadePositionWithState}
              mode={configMode}
              expanded={true}
            />
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="subtitle2" gutterBottom>{t('tab2.frontage_config')}</Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label={t('tab2.frontage_width_km')}
                  type="number"
                  fullWidth
                  value={config.position.frontage_width_km}
                  onChange={(e) => updateBrigadePosition(editingBrigade, 'frontage_width_km', Number(e.target.value))}
                  disabled={!config.frontage_status?.holds_frontage}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label={t('tab2.frontage_start_km')}
                  type="number"
                  fullWidth
                  value={config.position.frontage_start_km}
                  onChange={(e) => updateBrigadePosition(editingBrigade, 'frontage_start_km', Number(e.target.value))}
                  disabled={!config.frontage_status?.holds_frontage}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={config.position.manual_lock || false}
                      onChange={(e) => updateBrigadePosition(editingBrigade, 'manual_lock', e.target.checked)}
                    />
                  }
                  label=" Lock position/width (preserve during redistribution)"
                />
              </Grid>
            </Grid>
          </TabPanel>
          
          {/* TAB 1: MISSION */}
          <TabPanel value={brigadeDialogTab} index={1}>
            <Grid container spacing={2}>
              {/* Display Tab 1 data (read-only) */}
              <Grid size={{ xs: 12 }}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>{t('tab2.from_tab1')}</Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="body2">
                        <strong>Assigned Task:</strong> {(() => {
                          const brigade = brigades.find(b => b.id === config.brigade_id);
                          const tacticalRole = brigade?.tactical_in_parent?.tactical_role || 'SUPPORTING';
                          const missions = {
                            'MAIN_EFFORT': 'Main Effort',
                            'MAIN_ATTACK': 'Main Attack',
                            'MAIN_DEFENSE': 'Main Defense',
                            'SUPPORTING': 'Supporting Effort',
                            'RESERVE': 'Reserve',
                            'EXPLOITATION': 'Exploitation',
                            'ECONOMY_OF_FORCE': 'Economy of Force'
                          };
                          return missions[tacticalRole] || tacticalRole;
                        })()}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="body2">
                        <strong>Operational Posture:</strong> {(() => {
                          const brigade = brigades.find(b => b.id === config.brigade_id);
                          const posture = brigade?.tactical_in_parent?.posture || 'HASTY_DEFENSE';
                          const postures = {
                            'ATTACK': 'Attack',
                            'HASTY_ATTACK': 'Hasty Attack',
                            'DELIBERATE_ATTACK': 'Deliberate Attack',
                            'DEFENSE': 'Defense',
                            'HASTY_DEFENSE': 'Hasty Defense',
                            'DELIBERATE_DEFENSE': 'Deliberate Defense',
                            'MARCH_TACTICAL': 'Tactical March',
                            'MARCH_ADMINISTRATIVE': 'Administrative March',
                            'SUPPORT_OPERATIONS': 'Support Operations',
                            'EXPLOITATION': 'Exploitation',
                            'CONSOLIDATION': 'Consolidation',
                            'RESERVE': 'Reserve'
                          };
                          return postures[posture] || posture;
                        })()}
                      </Typography>
                    </Grid>
                  </Grid>
                </Alert>
              </Grid>

              {/* Operational Mission (Tab 2 configuration) — linked to posture from Tab 1.
                  Only doctrinally compatible missions shown. No mismatch warnings needed. */}
              {(() => {
                const brigade = brigades.find(b => b.id === config.brigade_id);
                const posture = brigade?.tactical_in_parent?.posture || 'HASTY_DEFENSE';

                // Map each posture to the missions that are doctrinally valid for it.
                const POSTURE_MISSIONS = {
                  ATTACK:                  [{ value: 'ATTACK', label: 'Attack' }, { value: 'URBAN', label: 'Urban Operations' }, { value: 'RIVER_CROSSING', label: 'River Crossing' }],
                  HASTY_ATTACK:            [{ value: 'ATTACK', label: 'Attack' }, { value: 'URBAN', label: 'Urban Operations' }, { value: 'RIVER_CROSSING', label: 'River Crossing' }],
                  DELIBERATE_ATTACK:       [{ value: 'ATTACK', label: 'Attack' }, { value: 'URBAN', label: 'Urban Operations' }, { value: 'RIVER_CROSSING', label: 'River Crossing' }],
                  PURSUIT:                 [{ value: 'ATTACK', label: 'Attack' }, { value: 'EXPLOITATION', label: 'Exploitation' }],
                  EXPLOITATION:            [{ value: 'EXPLOITATION', label: 'Exploitation' }, { value: 'ATTACK', label: 'Attack' }],
                  DEFENSE:                 [{ value: 'DEFEND', label: 'Defend' }, { value: 'SUPPORTING', label: 'Supporting' }],
                  HASTY_DEFENSE:           [{ value: 'DEFEND', label: 'Defend' }, { value: 'SUPPORTING', label: 'Supporting' }],
                  DELIBERATE_DEFENSE:      [{ value: 'DEFEND', label: 'Defend' }, { value: 'SUPPORTING', label: 'Supporting' }],
                  DELAY:                   [{ value: 'DEFEND', label: 'Defend' }, { value: 'SUPPORTING', label: 'Supporting' }],
                  WITHDRAWAL:              [{ value: 'CONSOLIDATION', label: 'Consolidation' }, { value: 'MOVEMENT', label: 'Movement' }],
                  RETIRE:                  [{ value: 'CONSOLIDATION', label: 'Consolidation' }, { value: 'MOVEMENT', label: 'Movement' }],
                  RESERVE:                 [{ value: 'RESERVE', label: 'Reserve' }, { value: 'SUPPORTING', label: 'Supporting' }],
                  MARCH:                   [{ value: 'MOVEMENT', label: 'Movement' }],
                  MARCH_TACTICAL:          [{ value: 'MOVEMENT', label: 'Movement' }],
                  MARCH_ADMINISTRATIVE:    [{ value: 'MOVEMENT', label: 'Movement' }],
                  MARCHING:                [{ value: 'MOVEMENT', label: 'Movement' }],
                  TRANSIT:                 [{ value: 'MOVEMENT', label: 'Movement' }],
                  RECONSTITUTION:          [{ value: 'SUPPORTING', label: 'Supporting' }, { value: 'CONSOLIDATION', label: 'Consolidation' }],
                  SUPPORT_OPERATIONS:      [{ value: 'SUPPORTING', label: 'Supporting' }, { value: 'CONSOLIDATION', label: 'Consolidation' }],
                  CONSOLIDATION:           [{ value: 'CONSOLIDATION', label: 'Consolidation' }, { value: 'SUPPORTING', label: 'Supporting' }],
                };

                const allowedMissions = POSTURE_MISSIONS[posture] || [
                  { value: 'ATTACK', label: 'Attack' }, { value: 'DEFEND', label: 'Defend' },
                  { value: 'URBAN', label: 'Urban Operations' }, { value: 'RIVER_CROSSING', label: 'River Crossing' },
                  { value: 'EXPLOITATION', label: 'Exploitation' }, { value: 'CONSOLIDATION', label: 'Consolidation' },
                  { value: 'RESERVE', label: 'Reserve' }, { value: 'SUPPORTING', label: 'Supporting' },
                  { value: 'MOVEMENT', label: 'Movement' },
                ];

                // If current mission is no longer in the allowed list, auto-correct to first allowed.
                const currentMission = config.operation.mission_type;
                const isValid = allowedMissions.some(m => m.value === currentMission);
                if (!isValid) {
                  // Defer to avoid state update during render
                  setTimeout(() => updateBrigadeOperation(editingBrigade, 'mission_type', allowedMissions[0].value), 0);
                }

                return (
                  <Grid size={{ xs: 12, md: 6 }}>
                    <FormControl fullWidth>
                      <InputLabel>{t('tab2.operational_mission')}</InputLabel>
                      <Select
                        value={isValid ? currentMission : allowedMissions[0].value}
                        onChange={(e) => updateBrigadeOperation(editingBrigade, 'mission_type', e.target.value)}
                        label={t('tab2.operational_mission')}
                      >
                        {allowedMissions.map(m => (
                          <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                );
              })()}
              <Grid size={{ xs: 12 }}>
                <TextField
                  label={t('tab2.mission_statement')}
                  fullWidth
                  multiline
                  rows={2}
                  value={config.operation.mission_statement}
                  onChange={(e) => updateBrigadeOperation(editingBrigade, 'mission_statement', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 6, md: 3 }}>
                <TextField
                  label={t('tab2.start_day')}
                  type="number"
                  fullWidth
                  value={config.operation.start_day}
                  onChange={(e) => updateBrigadeOperation(editingBrigade, 'start_day', Number(e.target.value))}
                  inputProps={{ min: 1, max: totalDuration }}
                />
              </Grid>
              <Grid size={{ xs: 6, md: 3 }}>
                <TextField
                  label={t('tab2.start_hour')}
                  type="number"
                  fullWidth
                  value={config.operation.start_hour}
                  onChange={(e) => updateBrigadeOperation(editingBrigade, 'start_hour', Number(e.target.value))}
                  inputProps={{ min: 0, max: 23 }}
                />
              </Grid>
              <Grid size={{ xs: 6, md: 3 }}>
                <TextField
                  label={t('tab2.end_day')}
                  type="number"
                  fullWidth
                  value={config.operation.end_day}
                  onChange={(e) => updateBrigadeOperation(editingBrigade, 'end_day', Number(e.target.value))}
                  inputProps={{ min: 1, max: totalDuration }}
                />
              </Grid>
              <Grid size={{ xs: 6, md: 3 }}>
                <TextField
                  label={t('tab2.end_hour')}
                  type="number"
                  fullWidth
                  value={config.operation.end_hour}
                  onChange={(e) => updateBrigadeOperation(editingBrigade, 'end_hour', Number(e.target.value))}
                  inputProps={{ min: 0, max: 23 }}
                />
              </Grid>
            </Grid>
          </TabPanel>
          
          {/* TAB 2: PHASES - PHASE 2 UI */}
          <TabPanel value={brigadeDialogTab} index={2}>
            <BrigadePhaseTab
              brigade={config}
              divisionPhases={
                divisionConfigs.find(d => d.division_id === config.parent_division_id)?.phases || []
              }
              divisionNumber={
                divisionConfigs.find(d => d.division_id === config.parent_division_id)?.division_number || 1
              }
              operationDurationDays={totalDuration}
              onUpdateBrigade={(updatedBrigade) => {
                setBrigadeConfigs(prev => prev.map(b => 
                  b.brigade_id === updatedBrigade.brigade_id ? updatedBrigade : b
                ));
              }}
              onToggleInheritance={toggleBrigadeInheritance}
            />
          </TabPanel>
          
          {/* TAB 3: ENVIRONMENT */}
          <TabPanel value={brigadeDialogTab} index={3}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 4 }}>
                <FormControl fullWidth>
                  <InputLabel>{t('tab2.terrain_label')}</InputLabel>
                  <Select
                    value={config.environment?.terrain || 'MIXED'}
                    onChange={(e) => updateBrigadeEnvironment(editingBrigade, 'terrain', e.target.value)}
                    label={t('tab2.terrain_label')}
                  >
                    {Object.keys(TERRAIN_TYPES).map(key => (
                      <MenuItem key={key} value={key}>{TERRAIN_TYPES[key].label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {/* R2-10 / W4 (2026-10-20): the brigade weather selector read nothing and was removed. The weather is set for the operation
                  (Environmental Factors card) and per phase (phase table). */}
              <Grid size={{ xs: 12, md: 4 }}>
                <Typography variant="caption" color="text.secondary">{t('tab2.brigade_weather_moved')}</Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <FormControl fullWidth>
                  <InputLabel>{t('tab2.threat_level_label')}</InputLabel>
                  <Select
                    value={config.environment?.threat_level || 'MODERATE_THREAT'}
                    onChange={(e) => updateBrigadeEnvironment(editingBrigade, 'threat_level', e.target.value)}
                    label={t('tab2.threat_level_label')}
                  >
                    {Object.keys(THREAT_LEVELS).map(key => (
                      <MenuItem key={key} value={key}>{THREAT_LEVELS[key].label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid size={{ xs: 12 }}>
                <Divider sx={{ my: 2 }} />
                <TechnologyEnvironmentSection
                  brigadeConfig={config}
                  onUpdate={updateBrigadeConfig}
                  defaultTechEnv={defaultTechEnv}
                />
              </Grid>
            </Grid>
          </TabPanel>
          
          {/* TAB 4: MEDICAL */}
          <TabPanel value={brigadeDialogTab} index={4}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>{t('tab2.expected_casualty_rate')}</InputLabel>
                  <Select
                    value={config.medical?.expected_casualty_rate || 'MODERATE'}
                    onChange={(e) => updateBrigadeMedical(editingBrigade, 'expected_casualty_rate', e.target.value)}
                    label={t('tab2.expected_casualty_rate')}
                  >
                    <MenuItem value="LIGHT">Light (&lt;2%/day)</MenuItem>
                    <MenuItem value="MODERATE">Moderate (2-5%/day)</MenuItem>
                    <MenuItem value="HEAVY">Heavy (5-10%/day)</MenuItem>
                    <MenuItem value="VERY_HEAVY">Very Heavy (&gt;10%/day)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>{t('tab2.evacuation_priority')}</InputLabel>
                  <Select
                    value={config.medical?.evacuation_priority || 'ROUTINE'}
                    onChange={(e) => updateBrigadeMedical(editingBrigade, 'evacuation_priority', e.target.value)}
                    label={t('tab2.evacuation_priority')}
                  >
                    <MenuItem value="URGENT">Urgent</MenuItem>
                    <MenuItem value="PRIORITY">Priority</MenuItem>
                    <MenuItem value="ROUTINE">Routine</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>{t('tab2.ground_route_quality')}</InputLabel>
                  <Select
                    value={config.medical?.evacuation_factors?.ground_route_quality || 'FAIR'}
                    onChange={(e) => updateBrigadeMedical(editingBrigade, 'evacuation_factors', {
                      ...config.medical?.evacuation_factors,
                      ground_route_quality: e.target.value
                    })}
                    label={t('tab2.ground_route_quality')}
                  >
                    <MenuItem value="EXCELLENT">Excellent (paved, secure)</MenuItem>
                    <MenuItem value="GOOD">Good</MenuItem>
                    <MenuItem value="FAIR">Fair</MenuItem>
                    <MenuItem value="POOR">Poor (damaged, contested)</MenuItem>
                    <MenuItem value="DESTROYED">Destroyed</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label={t('tab2.dist_r2e_km')}
                  type="number"
                  fullWidth
                  value={config.medical?.evacuation_factors?.distance_to_role2e || 15}
                  onChange={(e) => updateBrigadeMedical(editingBrigade, 'evacuation_factors', {
                    ...config.medical?.evacuation_factors,
                    distance_to_role2e: Number(e.target.value)
                  })}
                />
              </Grid>
            </Grid>
          </TabPanel>
          
          {/* TAB 5: LOGISTICS */}
          <TabPanel value={brigadeDialogTab} index={5}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label={t('tab2.supply_route')}
                  fullWidth
                  value={config.logistics?.supply_route || ''}
                  onChange={(e) => updateBrigadeLogistics(editingBrigade, 'supply_route', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>{t('tab2.supply_priority')}</InputLabel>
                  <Select
                    value={config.logistics?.supply_priority || 'MODERATE'}
                    onChange={(e) => updateBrigadeLogistics(editingBrigade, 'supply_priority', e.target.value)}
                    label={t('tab2.supply_priority')}
                  >
                    <MenuItem value="HIGH">High</MenuItem>
                    <MenuItem value="MODERATE">Moderate</MenuItem>
                    <MenuItem value="LOW">Low</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </TabPanel>
          
          {/* TAB 6: SUMMARY */}
          <TabPanel value={brigadeDialogTab} index={6}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <Alert severity="info">
                  <AlertTitle>Brigade {config.brigade_number} Summary</AlertTitle>
                  <Typography variant="body2">
                    <strong>Unit Type:</strong> {config.unit_type}<br />
                    <strong>Operational State:</strong> {stateConfig.icon} {stateConfig.label} ({stateConfig.battle_casualty_multiplier}x battle casualties, {stateConfig.dnbi?.combined_rate}/1k DNBI)<br />
                    <strong>Position:</strong> {config.frontage_status?.holds_frontage ? 
                      `${config.position.frontage_start_km}-${config.position.frontage_end_km}km (${config.position.frontage_width_km}km width)` :
                      `${config.position?.position_feba_km || 0}km from FEBA (${config.frontage_status?.reason})`
                    }<br />
                    <strong>Mission:</strong> {config.operation.mission_type} (Days {config.operation.start_day}-{config.operation.end_day})<br />
                    <strong>Strength:</strong> {config.strength} troops<br />
                    <strong>National Standard:</strong> {config.national_standard}<br />
                    <strong>Force Quality:</strong> {config.force_quality_inherited_from} ({(config.modifiers?.force_quality_modifier || 1).toFixed(2)}x)<br />
                    <strong>Helicopters:</strong> {config.evacuation_assets?.helicopters || 0}
                  </Typography>
                </Alert>
              </Grid>
              
              <Grid size={{ xs: 12, md: 6 }}>
                <ForceQualityBadge
                  forceQuality={config.force_quality}
                  inheritedFrom={config.force_quality_inherited_from}
                  compact={false}
                />
              </Grid>
              
              <Grid size={{ xs: 12, md: 6 }}>
                <OperationalStateBadge state={config.operational_state} compact={false} />
              </Grid>
            </Grid>
          </TabPanel>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setEditingBrigade(null)} variant="contained">Save &amp; Close</Button>
        </DialogActions>
      </Dialog>
    );
  };
 
  // ============================================================================
  // RENDER: TIMELINE
  // ============================================================================
  
  const renderTimeline = () => {
    if (brigadeConfigs.length === 0) return null;
    
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>{t('tab2.operation_timeline')}</Typography>
          <Box sx={{ overflowX: 'auto' }}>
            <Box sx={{ minWidth: 600, p: 2 }}>
              <Box sx={{ display: 'flex', borderBottom: '1px solid #ccc', pb: 1, mb: 2 }}>
                {Array.from({ length: totalDuration }, (_, i) => (
                  <Box key={i} sx={{ flex: 1, textAlign: 'center', minWidth: 40 }}>
                    <Typography variant="caption">{formatDayLabel(i + 1, operationStartDate)}</Typography>
                  </Box>
                ))}
              </Box>
              
              {brigadeConfigs.map(config => {
                const stateConfig = OPERATIONAL_STATES[config.operational_state] || OPERATIONAL_STATES.COMBAT_ENGAGED;
                return (
                  <Box key={config.brigade_id} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Box sx={{ width: 100, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="caption">{stateConfig.icon}</Typography>
                      <Typography variant="caption">Bde {config.brigade_number}</Typography>
                    </Box>
                    <Box sx={{ flex: 1, display: 'flex', height: 24, bgcolor: 'grey.100', borderRadius: 1, position: 'relative' }}>
                      {config.phases && config.phases.length > 0 ? (
                        config.phases.map(phase => {
                          const startPct = ((phase.start_day - 1) / totalDuration) * 100;
                          const widthPct = ((phase.end_day - phase.start_day + 1) / totalDuration) * 100;
                          const colors = {
                            ATTACK: '#f44336',
                            DEFEND: '#2196f3',
                            CONSOLIDATION: '#4caf50',
                            EXPLOITATION: '#ff9800',
                            RESERVE: '#9e9e9e'
                          };
                          return (
                            <Tooltip key={phase.phase_id} title={`${phase.phase_name}: ${phase.mission_type} (Days ${phase.start_day}-${phase.end_day})`}>
                              <Box
                                sx={{
                                  position: 'absolute',
                                  left: `${startPct}%`,
                                  width: `${widthPct}%`,
                                  height: '100%',
                                  bgcolor: colors[phase.mission_type] || '#9e9e9e',
                                  borderRadius: 1,
                                  opacity: 0.8
                                }}
                              />
                            </Tooltip>
                          );
                        })
                      ) : (
                        <Box
                          sx={{
                            width: '100%',
                            height: '100%',
                            bgcolor: config.operation.mission_type === 'RESERVE' ? '#9e9e9e' : '#2196f3',
                            borderRadius: 1,
                            opacity: 0.6
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  };
  
  // ============================================================================
  // RENDER: FRONTAGE MAP
  // ============================================================================
  
  const renderFrontageMap = () => {
    if (brigadeConfigs.length === 0) return null;
    
    const frontageEligible = brigadeConfigs.filter(config => {
      if (!unitTypeHasFrontage(config.unit_type)) return false;
      if (NON_FRONTAGE_MISSIONS.includes(config.operation.mission_type)) return false;
      if (NON_FRONTAGE_POSTURES.includes(config.operation.operational_posture)) return false;
      if (config.position?.frontage_width_km === 0) return false;
      return true;
    });
    
    const rearUnits = brigadeConfigs.filter(config => 
      !unitTypeHasFrontage(config.unit_type) ||
      NON_FRONTAGE_MISSIONS.includes(config.operation.mission_type) ||
      NON_FRONTAGE_POSTURES.includes(config.operation.operational_posture) ||
      config.position?.frontage_width_km === 0
    );
    
    const mapWidth = 700;
    const mapHeight = 200;
    
    // BUG-163 FIX: Added GERMAN, BRITISH, POLISH, FRENCH — were missing from map,
    // causing those brigades to render grey with no legend chip.
    const nsColors = {
      'ROMANIAN':      '#d32f2f',  // Red
      'NATO':          '#1976d2',  // Blue
      'US':            '#388e3c',  // Green
      'MULTINATIONAL': '#7b1fa2',  // Purple
      'GERMAN':        '#f57c00',  // Orange — Bundeswehr
      'BRITISH':       '#0277bd',  // Light blue — British Army
      'POLISH':        '#ffa000',  // Amber — Wojska Lądowe (cosmetic: was #c62828, too close to ROMANIAN #d32f2f; GERMAN owns #f57c00)
      'FRENCH':        '#283593',  // Dark blue — Armée de Terre
    };
    
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>{t('tab2.frontage_allocation_map')}</Typography>
          
          <Box sx={{ overflowX: 'auto' }}>
            <svg width={mapWidth} height={mapHeight} style={{ border: '1px solid #ccc' }}>
              <line x1={0} y1={60} x2={mapWidth} y2={60} stroke="#f44336" strokeWidth={3} strokeDasharray="10,5" />
              	<text x={10} y={55} fontSize={12} fill="#f44336" fontWeight="bold">
  			FEBA (Forward Edge of the Battle Area)
  				<tspan x={230} fontWeight="normal" fontStyle="italic">
   				 - the line where fighting is actively happening
  				</tspan>
		</text>
              {/* v7.6.0: Division boundary lines & labels */}
              {(() => {
                // Collect unique divisions and their start positions from eligible configs
                const divSeen = new Map(); // divId -> { start_km, name }
                for (const cfg of frontageEligible) {
                  const divId = cfg.parent_division_id || 'UNKNOWN';
                  if (!divSeen.has(divId)) {
                    divSeen.set(divId, {
                      start_km: cfg.position?.frontage_start_km ?? 0,
                      name: cfg.parent_division_name || `Div ${cfg.parent_division_number || ''}`
                    });
                  }
                }
                return Array.from(divSeen.entries()).map(([divId, info], i) => {
                  const x = (info.start_km / totalFrontage) * mapWidth;
                  return (
                    <g key={`divboundary-${divId}`}>
                      {i > 0 && (
                        <line
                          x1={x} y1={65} x2={x} y2={155}
                          stroke="#ff6f00" strokeWidth={2} strokeDasharray="6,3"
                        />
                      )}
                      <text
                        x={x + 4} y={78}
                        fontSize={9} fill="#ff6f00" fontWeight="bold"
                      >
                        {info.name.substring(0, 16)}
                      </text>
                    </g>
                  );
                });
              })()}

              {(() => {
                // Count unique divisions to decide if div prefix is needed in brigade label
                const uniqueDivIds = new Set(frontageEligible.map(c => c.parent_division_id || 'UNKNOWN'));
                const multiDiv = uniqueDivIds.size > 1;
                return frontageEligible.map((config, idx) => {
                const xStart = (config.position.frontage_start_km / totalFrontage) * mapWidth;
                const width = (config.position.frontage_width_km / totalFrontage) * mapWidth;
                const color = nsColors[config.nationalStandard] || '#666';
                const yPosition = 85;
                const stateConfig = OPERATIONAL_STATES[config.operational_state];
                // In corps (multi-div) show "D1/B2" for uniqueness; in division show "Bde 2"
                const brigadeLabel = multiDiv
                  ? `D${config.parent_division_number}/B${config.brigade_number}`
                  : `Bde ${config.brigade_number}`;

                return (
                  <g key={config.brigade_id}>
                    <rect
                      x={xStart}
                      y={yPosition}
                      width={Math.max(width - 2, 10)}
                      height={60}
                      fill={color}
                      fillOpacity={0.7}
                      stroke={config.position.manual_lock ? '#ff9800' : '#333'}
                      strokeWidth={config.position.manual_lock ? 3 : 1}
                    />
                    <text
                      x={xStart + width / 2}
                      y={yPosition + 20}
                      fontSize={11}
                      fontWeight="bold"
                      textAnchor="middle"
                      fill="white"
                    >
                      {brigadeLabel}
                    </text>
                    <text
                      x={xStart + width / 2}
                      y={yPosition + 35}
                      fontSize={9}
                      textAnchor="middle"
                      fill="white"
                    >
                      {stateConfig?.icon} {stateConfig?.shortLabel}
                    </text>
                    <text
                      x={xStart + width / 2}
                      y={yPosition + 50}
                      fontSize={9}
                      textAnchor="middle"
                      fill="white"
                    >
                      FQ: {(config.modifiers?.force_quality_modifier || 1).toFixed(1)}x
                    </text>
                  </g>
                );
              });
              })()}

              {Array.from({ length: Math.floor(totalFrontage / 10) + 1 }, (_, i) => {
                const km = i * 10;
                const x = (km / totalFrontage) * mapWidth;
                return (
                  <g key={i}>
                    <line x1={x} y1={mapHeight - 30} x2={x} y2={mapHeight - 20} stroke="#666" />
                    <text x={x} y={mapHeight - 5} fontSize={10} textAnchor="middle" fill="#666">{km}km</text>
                  </g>
                );
              })}
            </svg>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>{t('tab2.legend')}</Typography>
            {Object.entries(nsColors)
              .filter(([ns]) => frontageEligible.some(c => c.nationalStandard === ns))
              .map(([ns, color]) => (
                <Chip key={ns} label={ns} size="small" sx={{ bgcolor: color, color: 'white' }} />
              ))}
            {/* v7.6.0: Division boundary legend item */}
            <Chip
              label={t('tab2.division_boundary')}
              size="small"
              variant="outlined"
              sx={{ borderColor: '#ff6f00', color: '#ff6f00', borderStyle: 'dashed' }}
            />
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => autoRedistributeFrontage(totalFrontage, 'RESET')}
            >
              {t('tab2.reset_frontage')}
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => autoRedistributeFrontage(totalFrontage, 'FILL_GAPS')}
            >
              {t('tab2.fill_gaps')}
            </Button>
          </Box>
          
          {rearUnits.length > 0 && (
            <Box sx={{ mt: 2, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                Not on frontage ({rearUnits.length}):
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                {(() => {
                  // BUG-S102-013 FIX: Show division prefix when rear units span multiple divisions
                  // so "Bde 3 (55km)" from Div1 and "Bde 3 (60km)" from Div2 are distinguishable.
                  const divIds = new Set(rearUnits.map(c => c.parent_division_id).filter(Boolean));
                  const multiDiv = divIds.size > 1;
                  return rearUnits.map(config => {
                    const stateConfig = OPERATIONAL_STATES[config.operational_state];
                    const reason = config.frontage_status?.reason || config.unit_type;
                    const divPrefix = multiDiv
                      ? `D${config.parent_division_number || ''}/`
                      : '';
                    return (
                      <Tooltip
                        key={config.brigade_id}
                        title={`${config.brigade_name} - ${reason} - ${stateConfig?.label || 'Unknown'} at ${config.position?.position_feba_km || 0}km`}
                      >
                        <Chip
                          icon={<span>{stateConfig?.icon || ''}</span>}
                          label={`${divPrefix}Bde ${config.brigade_number} (${config.position?.position_feba_km || 0}km)`}
                          size="small"
                          variant="outlined"
                          color={stateConfig?.color || 'default'}
                        />
                      </Tooltip>
                    );
                  });
                })()}
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };


  // ============================================================================
  // RENDER: CORPS CONFIG
  // ============================================================================

  const renderCorpsConfig = () => {
    if (configMode === 'STANDARD') return null;
    
    return (
      <>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                 {echelonLabel} Configuration
              </Typography>
              <Stack direction="row" gap={1}>
                <Button
                  variant="outlined"
                  startIcon={<AccountTreeIcon />}
                  onClick={() => setForceCascadeConfirm(true)}
                  color="warning"
                  size="small"
                  disabled={!corpsPhases || corpsPhases.length === 0}
                >
                  Force Cascade
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<SettingsIcon />}
                  onClick={handleEditCorpsConfig}
                  color="primary"
                >
                  Configure {echelonLabel} HQ & Phases
                </Button>
              </Stack>
            </Stack>
          
            <Alert severity="info" sx={{ mb: 2 }}>
              <AlertTitle> {echelonLabel}-Level Planning</AlertTitle>
              Configure {echelonLabel}: total operation duration (in days), total front line - Frontage(in km), operational state, define operational phases (that cascade to all subordinate units).
            </Alert>
            
            {/* Force Structure Summary */}
            <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <Chip label={`Echelon: ${echelon}`} color="primary" />
              {getEchelonCounts.corps > 0 && (
                <Chip label={`${getEchelonCounts.corps} Corps`} variant="outlined" />
              )}
              {getEchelonCounts.divisions > 0 && (
                <Chip label={`${getEchelonCounts.divisions} Division${getEchelonCounts.divisions !== 1 ? 's' : ''}`} variant="outlined" />
              )}
              {getEchelonCounts.brigades > 0 && (
                <Chip label={`${getEchelonCounts.brigades} Brigade${getEchelonCounts.brigades !== 1 ? 's' : ''}`} variant="outlined" />
              )}
              {getEchelonCounts.battalions > 0 && (
                <Chip label={`${getEchelonCounts.battalions} Battalion${getEchelonCounts.battalions !== 1 ? 's' : ''}`} variant="outlined" />
              )}
            </Box>
            
            {/* BUG-077 FIX (March 13, 2026): Corps phase validation warning — corps phases only.
                Division mismatches belong in DivisionConfigDialog (where the user can act).
                Brigade mismatches appear in the brigade dialog Mission tab with Auto-Fix button.
                Removed division + brigade mismatch collection that was surfacing warnings here
                where the user had no direct way to correct them. */}
            {(() => {
              const mismatches = [];

              // Corps phases only — division/brigade handled in their own dialogs
              // URBAN and RIVER_CROSSING are specialised offensive missions — compatible with ATTACK posture.
              const isCompatible = (expected, current) =>
                expected === 'ATTACK' ? ['ATTACK', 'URBAN', 'RIVER_CROSSING'].includes(current) : expected === current;
              corpsPhases.forEach((phase) => {
                const expected = getMissionFromPosture(phase.operational_posture);
                if (!isCompatible(expected, phase.mission_type)) {
                  mismatches.push({
                    phase: phase.phase_number,
                    posture: phase.operational_posture,
                    currentMission: phase.mission_type,
                    expectedMission: expected
                  });
                }
              });

              if (mismatches.length > 0) {
                return (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <AlertTitle>[!] {echelonLabel} Phase Configuration Warnings ({mismatches.length})</AlertTitle>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Some {echelonLabel.toLowerCase()} phases have posture/mission mismatches. Click "Configure {echelonLabel} HQ &amp; Phases" above to fix:
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, mb: 0 }}>
                      {mismatches.map((m, idx) => (
                        <Typography component="li" variant="body2" key={idx}>
                          Phase {m.phase}: {m.posture} (posture) expects{' '}
                          <strong>{m.expectedMission}</strong> mission, but has{' '}
                          <strong>{m.currentMission}</strong>
                        </Typography>
                      ))}
                    </Box>
                  </Alert>
                );
              }
              return null;
            })()}
            
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  label={t('tab2.total_duration_days')}
                  type="number"
                  fullWidth
                  value={totalDuration}
                  onChange={(e) => setTotalDuration(Math.max(1, Number(e.target.value)))}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  label={t('tab2.total_frontage_km')}
                  type="number"
                  fullWidth
                  value={totalFrontage}
                  onChange={(e) => {
                    const newFrontage = Math.max(10, Number(e.target.value));
                    setTotalFrontage(newFrontage);
                    if (autoCalculate) {
                      autoRedistributeFrontage(newFrontage, 'RESET');
                    }
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <FormControl fullWidth>
                  <InputLabel>{t('tab2.default_tech_env')}</InputLabel>
                  <Select
                    value={defaultTechEnv}
                    onChange={(e) => { setDefaultTechEnv(e.target.value); setTechDefaultUserSet(true); }}
                    label={t('tab2.default_tech_env')}
                  >
                    {Object.entries(TECHNOLOGY_ENVIRONMENT).map(([key, val]) => (
                      <MenuItem key={key} value={key}>{val.label} ({val.casualty_multiplier}x)</MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {t('tab2.drone_ew_helper')}
                  </FormHelperText>
                </FormControl>
              </Grid>
              {/* GROUP-1-D: Medical Structure Protection Level (Gielerak bo factor) */}
              <Grid size={{ xs: 12 }}>
                <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="subtitle2">
                      {t('tab2.med_protection_label')}
                    </Typography>
                    <Chip
                      label={`${medicalProtectionLevel}%`}
                      size="small"
                      color={
                        medicalProtectionLevel >= MEDICAL_PROTECTION_THRESHOLDS.HIGH ? 'success' :
                        medicalProtectionLevel >= MEDICAL_PROTECTION_THRESHOLDS.LOW  ? 'warning' : 'error'
                      }
                      sx={{ fontWeight: 700, minWidth: 52 }}
                    />
                  </Box>
                  <Slider
                    value={medicalProtectionLevel}
                    onChange={(e, val) => setMedicalProtectionLevel(val)}
                    min={0}
                    max={100}
                    step={5}
                    marks={[
                      { value: 0,   label: '0%'   },
                      { value: 30,  label: '30%'  },
                      { value: 60,  label: '60%'  },
                      { value: 100, label: '100%' }
                    ]}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(v) => `${v}%`}
                    color={
                      medicalProtectionLevel >= MEDICAL_PROTECTION_THRESHOLDS.HIGH ? 'success' :
                      medicalProtectionLevel >= MEDICAL_PROTECTION_THRESHOLDS.LOW  ? 'warning' : 'error'
                    }
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {medicalProtectionLevel >= MEDICAL_PROTECTION_THRESHOLDS.HIGH
                      ? t('tab2.med_protection_high')
                      : medicalProtectionLevel >= MEDICAL_PROTECTION_THRESHOLDS.LOW
                        ? t('tab2.med_protection_medium')
                        : t('tab2.med_protection_low')}
                  </Typography>
                  <Alert severity="info" sx={{ mt: 1.5, py: 0.5 }}>
                    <Typography variant="caption">
                      <strong>Doctrinal basis:</strong> Gielerak &amp; Murawski (2024), <em>Lekarz Wojskowy</em> Vol. 102.
                      The operational safety coefficient <strong>bo</strong> (0&#8211;1) quantifies medical structure
                      survivability in the Gielerak dynamic planning model. In PSP/CRE the surgical capacity lost to enemy fire is
                      <em>1 &minus; hit probability per day &times; 3 days out &times; (1 &minus; protection)</em>, where the hit probability
                      comes from the artillery threat of the threat assessment (moderate 5%, high 10%, extreme 20% a day; low none).
                      At the default 70% a high artillery threat leaves 91% of the capacity. Power and water of the infrastructure card
                      multiply it. No artillery threat, no loss.
                    </Typography>
                  </Alert>
                </Box>
              </Grid>
            </Grid>
            
            {/* Display current Corps HQ configuration */}
            <Typography variant="subtitle2" gutterBottom sx={{ mt: 2, mb: 1 }}>
              {t('tab2.current_echelon_config', { echelon: echelonLabel })}
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 3 }}>
                <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {echelonLabel} Personnel (HQ + Support units)
                  </Typography>
                  <Typography variant="h6">
                    {corpsPersonnel.toLocaleString()} troops
                  </Typography>
                </Box>
              </Grid>
              
              <Grid size={{ xs: 12, md: 3 }}>
                <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {t('tab2.operational_state_label')}
                  </Typography>
                  <Typography variant="h6">
                    {corpsOperationalState.replace(/_/g, ' ')}
                  </Typography>
                </Box>
              </Grid>
              
              <Grid size={{ xs: 12, md: 3 }}>
                <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {t('tab2.position_feba')}
                  </Typography>
                  <Typography variant="h6">
                    {corpsPositionFeba} km
                  </Typography>
                </Box>
              </Grid>
              
              <Grid size={{ xs: 12, md: 3 }}>
                <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {t('tab2.operational_phases')}
                  </Typography>
                  <Chip 
                    label={corpsPhases.length === 0 ? 'No phases configured' : `${corpsPhases.length} phase${corpsPhases.length !== 1 ? 's' : ''}`}
                    color={corpsPhases.length === 0 ? 'default' : 'primary'}
                    size="small"
                  />
                </Box>
              </Grid>
            </Grid>
            
            {/* Show phase summary if phases exist */}
            {corpsPhases.length > 0 && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'primary.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                   Configured Phases:
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {corpsPhases.map((phase, index) => (
                    <Chip
                      key={index}
                      label={`Phase ${phase.phase_number}: ${phase.operational_posture} (Days ${phase.start_day}-${phase.end_day})`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Stack>

                {/* IMP-003: Phase Gantt bar — horizontal D+N timeline */}
                {(() => {
                  const POSTURE_COLORS = {
                    DELIBERATE_ATTACK: '#d32f2f', ATTACK: '#d32f2f',
                    DELIBERATE_DEFENSE: '#1565c0', DEFEND: '#1565c0',
                    EXPLOITATION: '#e65100',
                    CONSOLIDATION: '#2e7d32',
                    MOVEMENT_TO_CONTACT: '#00695c', MOVEMENT: '#00695c',
                    RETROGRADE: '#6a1b9a',
                    HASTY_DEFENSE: '#1976d2',
                    DELAY: '#4a148c',
                  };
                  const dur = corpsPhases[corpsPhases.length - 1]?.end_day || totalDuration || 1;
                  return (
                    <Box sx={{ mt: 1.5 }}>
                      {/* Gantt bar */}
                      <Box sx={{ position: 'relative', height: 28, borderRadius: 1, overflow: 'hidden', bgcolor: 'grey.300' }}>
                        {corpsPhases.map((phase, idx) => {
                          const startPct = ((phase.start_day - 1) / dur) * 100;
                          const widthPct = ((phase.end_day - phase.start_day + 1) / dur) * 100;
                          const color = POSTURE_COLORS[phase.operational_posture] || '#607d8b';
                          return (
                            <Tooltip
                              key={idx}
                              title={`Ph.${phase.phase_number}: ${phase.operational_posture} | D+${phase.start_day}–D+${phase.end_day} (${phase.end_day - phase.start_day + 1}d)`}
                            >
                              <Box sx={{
                                position: 'absolute',
                                left: `${startPct}%`,
                                width: `${widthPct}%`,
                                height: '100%',
                                bgcolor: color,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                overflow: 'hidden',
                                borderRight: idx < corpsPhases.length - 1 ? '2px solid rgba(255,255,255,0.6)' : 'none',
                              }}>
                                <Typography sx={{ color: 'white', fontSize: '0.6rem', fontWeight: 700, whiteSpace: 'nowrap', px: 0.5 }}>
                                  Ph.{phase.phase_number}
                                </Typography>
                              </Box>
                            </Tooltip>
                          );
                        })}
                      </Box>
                      {/* D+N axis labels below bar */}
                      <Box sx={{ position: 'relative', height: 14, mt: 0.25 }}>
                        {corpsPhases.map((phase, idx) => (
                          <Box key={idx} sx={{ position: 'absolute', left: `${((phase.start_day - 1) / dur) * 100}%`, transform: 'translateX(-50%)' }}>
                            <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>D+{phase.start_day}</Typography>
                          </Box>
                        ))}
                        <Box sx={{ position: 'absolute', right: 0 }}>
                          <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>D+{dur}</Typography>
                        </Box>
                      </Box>
                    </Box>
                  );
                })()}
              </Box>
            )}
            
            {/* Cascade status */}
            {divisionConfigs.length > 0 && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                   Cascade Status:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {divisionConfigs.filter(d => d.uses_corps_phases).length} of {divisionConfigs.length} divisions 
                  will inherit these phases
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </>
    );
  };

  // ============================================================================
  // RENDER: OPERATIONAL MODIFIERS (ENH-017 — extracted from renderCorpsConfig)
  // Threat / Enemy / Air Situation / LOC / Advanced
  // Rendered AFTER Brigade list — configure who before what they face.
  // ============================================================================

  const renderOperationalModifiers = () => {
    if (configMode === 'STANDARD') return null;

    return (
      <>
        <ThreatAssessmentPanel
          threatConfig={threatConfig}
          onThreatConfigChange={setThreatConfig}
          onApplyPreset={setThreatConfig}
        />

        <EnemyAssessmentPanel
          enemyQuality={enemyQuality}
          forceSuperority={forceSuperority}
          enemyStrengthRatio={enemyStrengthRatio}
          onEnemyQualityChange={setEnemyQuality}
          onForceSuperiorityChange={setForceSuperority}
          onEnemyStrengthRatioChange={setEnemyStrengthRatio}
        />

        <EnemyForcesPanel
          forces={enemyForces}
          onChange={setEnemyForces}
          sectors={enemySectors}
          brigades={enemyBrigades}
          duration={Number(totalDuration)}
          cardRatio={typeof enemyStrengthRatio === 'number' ? enemyStrengthRatio : undefined}
        />

        <AirSituationPanel
          airSituationConfig={airSituationConfig}
          onAirSituationConfigChange={setAirSituationConfig}
          enemyAdThreat={threatConfig.enemy_ad}
        />

        <LOCConfigurationPanel
          locConfig={locConfig}
          onLocConfigChange={setLocConfig}
          onApplyPreset={setLocConfig}
          threatConfig={threatConfig}
          operationWeather={weather}
        />

        <AdvancedFeaturesPanel
          infrastructureConfig={infrastructureConfig}
          onInfrastructureConfigChange={setInfrastructureConfig}
          hnsConfig={hnsConfig}
          onHnsConfigChange={setHnsConfig}
          nightOpsConfig={nightOpsConfig}
          onNightOpsConfigChange={setNightOpsConfig}
          climateConfig={climateConfig}
          onClimateConfigChange={setClimateConfig}
          mroeConfig={mroeConfig}
          onMroeConfigChange={setMroeConfig}
          monteCarloConfig={monteCarloConfig}
          onMonteCarloConfigChange={setMonteCarloConfig}
          buildupConfig={buildupConfig}
          onBuildupConfigChange={setBuildupConfig}
          operationDurationDays={totalDuration}
          scenario={scenario}
          forceBuilderResult={forceBuilderResult}
        />
      </>
    );
  };

  // ============================================================================
  // RENDER: CORPS CONFIGURATION (DEPRECATED - combined into renderCorpsConfig above)
  // ============================================================================
  
  // ============================================================================
  // RENDER: DIVISION CONTROL
  // ============================================================================
  
  const renderDivisionControl = () => {
    // BUG-84-001: When echelon IS division, renderCorpsConfig() already renders
    // the top-level unit block via echelonLabel. renderDivisionControl() is only
    // needed at CORPS echelon to show subordinate divisions under the corps.
    // BUG-84-001b FIX: Original guard read scenario.operation_parameters.echelon
    // which is often unset. Use the echelon variable (from scenario.force.echelon)
    // which is the authoritative source used everywhere else in Tab2.
    if (configMode === 'STANDARD' || divisions.length === 0) return null;
    if (echelon === 'DIVISION' || echelon === 'BRIGADE') return null;
    
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>{t('tab2.division_overview')}</Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            View division composition and national standards. Configure operations per brigade below.
          </Alert>
          
          {divisions.map(division => {
            const divisionBrigades = brigadeConfigs.filter(bc => bc.parent_division_id === division.id);
            const divConfig = divisionConfigs.find(d => d.division_id === division.id);
            const divPhaseCount = divConfig?.phases?.length || 0;
            const usesCorpsPhases = divConfig?.uses_corps_phases ?? true;
            
            return (
              <Accordion key={division.id} sx={{ mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontWeight: 'bold' }}>{division.name}</Typography>
                    <Chip label={`${divisionBrigades.length} brigades`} size="small" />
                    <Chip label={division.nationalStandard || 'NATO'} size="small" color="primary" />
                    <Tooltip title={usesCorpsPhases ? 'Inheriting phases from Corps' : divPhaseCount > 0 ? `${divPhaseCount} custom phase${divPhaseCount !== 1 ? 's' : ''} configured` : 'No phases configured'}>
                      <Chip
                        label={usesCorpsPhases ? 'Corps phases' : divPhaseCount > 0 ? `${divPhaseCount} phases` : 'No phases'}
                        size="small" variant="outlined"
                        color={usesCorpsPhases ? 'default' : divPhaseCount > 0 ? 'primary' : 'warning'}
                      />
                    </Tooltip>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Brigades in this division:
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                    {divisionBrigades.map(bc => {
                      const stateConfig = OPERATIONAL_STATES[bc.operational_state];
                      return (
                        <Chip 
                          key={bc.brigade_id} 
                          icon={<span>{stateConfig?.icon || ''}</span>}
                          label={`Bde ${bc.brigade_number}`} 
                          size="small" 
                          variant="outlined"
                          color={stateConfig?.color || 'default'}
                        />
                      );
                    })}
                  </Box>
                  
                  {/* Division Configuration Button */}
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<SettingsIcon />}
                    onClick={() => {
                      const divConfig = divisionConfigs.find(d => d.division_id === division.id);
                      if (!divConfig) {
                        // BUG-S117-001 fix: division_configs was persisted as [] (empty array).
                        // Rebuild a default config for this division so the dialog has something to show.
                        // Both state updates are batched into one render — dialog finds config immediately.
                        const divisionIndex = divisions.findIndex(d => d.id === division.id);
                        const newConfig = {
                          division_id: division.id,
                          division_number: division.division_number || (divisionIndex + 1),
                          division_name: division.name || `Division ${divisionIndex + 1}`,
                          personnel: division.strength || 1000,
                          operational_state: 'SUPPORT_ACTIVE',
                          position_feba_km: division.tactical_in_parent?.hq_position_feba_km || 30,
                          uses_corps_phases: true,
                          phases: [],
                          phase_overrides: {},
                          brigade_ids: brigades
                            .filter(b => b.parent_division_id === division.id)
                            .map(b => b.id),
                          brigade_overrides: []
                        };
                        setDivisionConfigs(prev => [...prev, newConfig]);
                      }
                      handleEditDivisionConfig(division.id);
                    }}
                    sx={{ mb: 2 }}
                  >
                    {t('tab2.configure_division_hq')}
                  </Button>
                  
                  {/* BUG-84-002: corpsPhases state can lag on async scenario load;
                      fall back to reading scenario prop directly so the message is always current. */}
                  {(() => {
                    const displayPhases = corpsPhases.length > 0
                      ? corpsPhases
                      : (scenario?.operation_parameters?.corps_config?.phases || []);
                    return (
                      <Alert severity={displayPhases.length === 0 ? 'warning' : 'info'} size="small">
                        {displayPhases.length === 0
                          ? <>No corps phases defined yet. You can configure custom division phases here,
                              or define <strong>Corps HQ &amp; Phases</strong> first to use the cascade chain
                              (Corps → Division → Brigade).</>
                          : <>Corps has <strong>{displayPhases.length} phase{displayPhases.length !== 1 ? 's' : ''}</strong>.
                              Division can inherit these or define custom phases. Brigade phases cascade from division.</>
                        }
                      </Alert>
                    );
                  })()}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </CardContent>
      </Card>
    );
  };
  
  // ============================================================================
  // RENDER: VALIDATION
  // ============================================================================
  
  const renderValidation = () => {
    if (validationErrors.length === 0 && validationWarnings.length === 0) {
      return (
        <Alert severity="success" sx={{ mb: 3 }}>
          <AlertTitle>{t('tab2.validation_ok')}</AlertTitle>
          {t('tab2.validation_ok_body')}
        </Alert>
      );
    }
    
    return (
      <>
        {validationErrors.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <AlertTitle>{t('tab2.validation_errors')}</AlertTitle>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {validationErrors.map((error, idx) => (
                <li key={idx}>{error}</li>
              ))}
            </ul>
          </Alert>
        )}
        
        {validationWarnings.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}
            action={
              validationWarnings.some(w => typeof w === 'string' && w.includes('without phase role')) && corpsPhases.length > 0 ? (
                <Button color="inherit" size="small" variant="outlined"
                  onClick={handleApplyRoleDefaults}
                  title="Auto-assign MAIN_EFFORT to first combat brigade, SUPPORTING to others, ECONOMY_OF_FORCE to CSS — only for phases with no roles set">
                  Apply Role Defaults
                </Button>
              ) : undefined
            }
          >
            <AlertTitle>{t('tab2.warnings_title')}</AlertTitle>
            {validationWarnings.map((warning, idx) => (
              <Box key={idx} sx={{ mb: 1 }}>
                {typeof warning === 'string' ? (
                  <Typography variant="body2">{warning}</Typography>
                ) : (
                  <>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      Brigade {warning.brigade}: {warning.message}
                    </Typography>
                    {warning.recommendations && (
                      <ul style={{ marginTop: 4, marginBottom: 0 }}>
                        {warning.recommendations.map((rec, recIdx) => (
                          <li key={recIdx}><Typography variant="caption">{rec}</Typography></li>
                        ))}
                      </ul>
                    )}
                  </>
                )}
              </Box>
            ))}
          </Alert>
        )}
      </>
    );
  };
  
  // ============================================================================
  // MAIN RENDER
  // ============================================================================
  
  return (
<EnemyIndexContext.Provider value={enemyIndexValue}>
<OperationWeatherContext.Provider value={weather}>
<Box>
    
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 800, color: 'success.main', mb: 3 }}>
        {t('tab2.tab2_heading')}
      </Typography>

      {/* 2026-10-13: helper button (educational): the Ukraine 2024 scenario */}
      <Box sx={{ mb: 2 }}>
        <Button variant="outlined" color="error" onClick={() => setUkraineBundleOpen(true)}>{t('tab2.ukraine_bundle_button')}</Button>
        <HelpText id="ukraine_bundle" />
      </Box>
      <Dialog open={ukraineBundleOpen} onClose={() => setUkraineBundleOpen(false)}>
        <DialogTitle>{t('tab2.ukraine_bundle_title')}</DialogTitle>
        <DialogContent>
          <HelpText id="preset_threat_ukraine" />
          <HelpText id="preset_loc_high" />
          <HelpText id="preset_climate_ee" />
          <HelpText id="ukraine_bundle" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUkraineBundleOpen(false)}>{t('tab2.ukraine_bundle_cancel')}</Button>
          <Button variant="contained" color="error" onClick={applyUkraineBundle}>{t('tab2.ukraine_bundle_apply')}</Button>
        </DialogActions>
      </Dialog>
      
      <Alert severity="info" sx={{ mb: 3 }}>
        {t('tab2.subtitle')}
      </Alert>

      {/* Operation Name */}
      <TextField
        label={t('tab2.operation_name')}
        value={operationName}
        onChange={e => setOperationName(e.target.value)}
        placeholder="e.g. Op IRON SWORD"
        fullWidth
        sx={{ mb: 2 }}
        inputProps={{ maxLength: 80 }}
        helperText={t('tab2.operation_name_hint')}
      />
      {/* FEA-S97-002: configurable "Prepared by" for Annex Q PDF cover */}
      <TextField
        label={t('tab2.prepared_by')}
        value={preparedBy}
        onChange={e => setPreparedBy(e.target.value)}
        placeholder={t('tab2.prepared_by_ph')}
        fullWidth
        sx={{ mb: 3 }}
        inputProps={{ maxLength: 100 }}
        helperText={t('tab2.prepared_by_hint')}
      />

      {renderModeSelector()}
      {renderStandardModeConfig()}
      {renderEnvironmentalFactors()}
      {renderCorpsConfig()}
      {renderDivisionControl()}
      {renderBrigadesList()}
      {renderEvacuationEnvironment()}
      {/* ENH-017 (March 10, 2026): Threat/Enemy/Air/LOC/Advanced moved after Brigade list.
          Configure WHO (force hierarchy) before WHAT THEY FACE (modifiers). */}
      {renderOperationalModifiers()}
      <Tab2CasualtyPreview getScenario={getPreviewScenario} />
      {renderTimeline()}
      {renderFrontageMap()}
      {renderValidation()}
      {renderBrigadeDialog()}
      
      {/* Brigade Sync Dialog */}
      <Dialog open={showSyncDialog} onClose={() => setShowSyncDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('tab2.sync_dialog_title')}</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t('tab2.sync_dialog_body')}
          </Alert>

          {syncChanges.added.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="success.main">
                [OK] {syncChanges.added.length} {t('tab2.sync_added')}
              </Typography>
            </Box>
          )}

          {syncChanges.deleted.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="error.main">
                [X] {syncChanges.deleted.length} {t('tab2.sync_deleted')}
              </Typography>
            </Box>
          )}

          {syncChanges.modified.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="info.main">
                 {syncChanges.modified.length} {t('tab2.sync_modified')}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setShowSyncDialog(false); onEdit(); }} color="inherit">
            {t('tab2.back_to_tab1')}
          </Button>
          <Button onClick={handleResetTab2} color="error" variant="outlined">
            {t('tab2.sync_reset_tab2')}
          </Button>
          <Button onClick={handleAutoSync} variant="contained" color="primary">
            {t('tab2.auto_sync')}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* PHASE 2: Corps Configuration Dialog */}
      <CorpsConfigDialog
        open={editingCorps}
        onClose={handleCloseCorpsDialog}
        onSave={handleSaveCorpsConfig}
        initialConfig={{
          personnel: corpsPersonnel,
          operational_state: corpsOperationalState,
          position_feba_km: corpsPositionFeba,
          phases: corpsPhases
        }}
        operationDurationDays={totalDuration}
        divisions={divisionConfigs}
        echelonLabel={echelonLabel}
      />
      
      {/* PHASE 2: Division Configuration Dialog */}
      <DivisionConfigDialog
        open={editingDivision !== null}
        onClose={handleCloseDivisionDialog}
        onSave={handleSaveDivisionConfig}
        initialDivision={divisionConfigs.find(d => d.division_id === editingDivision)}
        corpsPhases={corpsPhases}
        operationDurationDays={totalDuration}
        brigades={brigadeConfigs}
        onToggleInheritance={toggleDivisionInheritance}
      />
      
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between', mt: 3 }}>
        <Button variant="outlined" onClick={onEdit}>
          {t('tab2.back_to_tab1')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleMarkComplete}
          disabled={!isValid}
        >
          {t('tab2.save_continue')}
        </Button>
      </Box>

      {/* V4-014: CoA B Detection Dialog */}
      <Dialog
        open={coaDetectOpen}
        onClose={handleCoaNormalFlow}
        maxWidth="sm"
        fullWidth
      >
        {coaDetectMode === 'new_b' ? (
          <>
            <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 700 }}>
              {t('tab2.coa_b_dialog_title')}
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
              <Typography variant="body1" gutterBottom>
                <strong>CoA A is saved.</strong> You have changed the operational parameters — this is typically done to configure a second Course of Action (CoA B).
              </Typography>
              <Typography variant="body1" gutterBottom sx={{ mt: 2 }}>
                You can skip Tab 3 and Tab 4 (medical and logistics are the same for both CoAs) and go directly to <strong>Tab 5</strong> to recalculate.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontStyle: 'italic' }}>
                Tab 5 will show a reminder banner: &ldquo;CoA B — recalculate and save as CoA B&rdquo;. Select &ldquo;No&rdquo; to go through Tab 3 → Tab 4 → Tab 5 normally.
              </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
              <Button onClick={handleCoaNormalFlow} variant="outlined">
                {t('tab2.coa_b_no')}
              </Button>
              <Button onClick={handleCoaSkipToTab5} variant="contained" color="primary">
                {t('tab2.coa_b_yes')}
              </Button>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogTitle sx={{ bgcolor: 'warning.main', color: 'white', fontWeight: 700 }}>
              {t('tab2.coa_both_title')}
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
              <Typography variant="body1" gutterBottom>
                <strong>CoA A and CoA B are both saved.</strong> You have changed the operational parameters. Which CoA are you updating?
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontStyle: 'italic' }}>
                After recalculating in Tab 5, overwrite the CoA slot you intend to update. The other slot will remain unchanged.
              </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
              <Button onClick={() => setCoaDetectOpen(false)} variant="outlined" color="inherit">
                Cancel
              </Button>
              <Button onClick={handleCoaNormalFlow} variant="outlined" color="primary">
                {t('tab2.coa_update_a')}
              </Button>
              <Button onClick={handleCoaSkipToTab5} variant="contained" color="primary">
                {t('tab2.coa_update_b')}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* BUG-S97-011: Cascade-skip warning — shown when divisions/brigades have local overrides.
          OBS-S109-002: offers Force Cascade action to overwrite all overrides immediately. */}
      <Snackbar
        open={Boolean(cascadeSkipMsg)}
        autoHideDuration={12000}
        onClose={() => setCascadeSkipMsg(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity="warning"
          onClose={() => setCascadeSkipMsg(null)}
          sx={{ width: '100%', maxWidth: 620 }}
          action={
            <Button
              size="small"
              color="inherit"
              startIcon={<AccountTreeIcon />}
              onClick={() => { setCascadeSkipMsg(null); setForceCascadeConfirm(true); }}
            >
              Force Cascade
            </Button>
          }
        >
          {cascadeSkipMsg}
        </Alert>
      </Snackbar>
      {/* OBS-S109-002: Force Cascade confirmation dialog */}
      <Dialog open={forceCascadeConfirm} onClose={() => setForceCascadeConfirm(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: 'warning.main', color: 'white', fontWeight: 700 }}>
          Force Cascade — Overwrite All Phase Overrides
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" gutterBottom>
            This resets ALL division and brigade phase configurations to match the current {echelonLabel.toLowerCase()} phases.
          </Typography>
          <Typography variant="body2" color="warning.dark" sx={{ mb: 1 }}>
            <strong>All custom phase overrides on all {divisionConfigs.length} division{divisionConfigs.length !== 1 ? 's' : ''} and {brigadeConfigs.length} brigade{brigadeConfigs.length !== 1 ? 's' : ''} will be cleared.</strong>{' '}
            Units currently using custom (non-inherited) phases will revert to inherit from {echelonLabel.toLowerCase()}.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Use this when building CoA B — after changing {echelonLabel.toLowerCase()}-level phases, Force Cascade ensures all subordinate units reflect the new posture before recalculating in Tab 5.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setForceCascadeConfirm(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            startIcon={<AccountTreeIcon />}
            onClick={handleForceCascade}
          >
            Force Cascade to All Units
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
</OperationWeatherContext.Provider>
</EnemyIndexContext.Provider>
  );
});

export { DroneScheduleEditor };
export default Tab2_Operations;
